import {
  createSessionEnvelope,
  type ArtifactManifestPayload,
  type ArtifactUrlPayload,
  type SessionEnvelope,
} from "@bianhua/protocol";
import { installArtifactSync, type ArtifactSync } from "./artifact-sync";
import { loadRuntimeConfig, type RuntimeConfig } from "./config";
import { GatewayClient, type GatewayState } from "./gateway-client";
import { bindWindowControls, getClientPlatform, isTauri } from "./platform";
import { ArtifactReceiver } from "./artifact-receiver";
import { ChatControlStore, type ChatControlSnapshot } from "./chat-control";
import { buildElicitationResponse, commandApprovalDecisions, type UiDecision } from "./elicitation";
import { bindVideoPreviewViewer, VideoRuntime } from "./video-runtime";
import { ThreadHistoryPersistence } from "./thread-history-cache";
import {
  WorkflowHandoffError,
  WorkflowHandoffStore,
  type NewWorkflowHandoff,
  type WorkflowArtifactRef,
  type WorkflowDelivery,
  type WorkflowHandoff,
  type WorkflowHandoffPurpose,
} from "./workflow-handoff";
import {
  buildWorkInterrupt,
  buildWorkSubmission,
  normalizeWorkExecutionPolicy,
  threadExecutionPolicyParams,
  transitionOptimisticDelivery,
  turnExecutionPolicyParams,
  WorkControlStore,
  type OptimisticDelivery,
  type WorkControlSnapshot,
  type WorkExecutionPolicy,
  type WorkInput,
} from "./work-control";
import {
  DEFAULT_WORK_FOLLOW_UP_BEHAVIOR,
  WorkFollowUpQueue,
  workFollowUpQueueStorageKey,
  type WorkFollowUpBehavior,
} from "./work-followup-queue";

type Surface = "chat" | "work";
const THREAD_HISTORY_FRESH_MS = 2 * 60_000;
const ATTACHMENT_UPLOAD_OPERATIONS = [
  "artifact/upload/begin",
  "artifact/upload/chunk",
  "artifact/upload/complete",
  "artifact/upload/cancel",
] as const;
const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;
const MAX_ATTACHMENT_TOTAL_BYTES = 100 * 1024 * 1024;
const MAX_ATTACHMENTS_PER_MESSAGE = 10;
type CodexFileInput = Extract<WorkInput, { type: "localImage" | "mention" }>;
type Attachment = {
  uploadId: string;
  name: string;
  size: number;
  mimeType: string;
  sha256: string;
  codexInput: CodexFileInput;
  localPreviewUrl?: string;
};
type AttachmentDraft = {
  localId: string;
  file: File;
  name: string;
  size: number;
  mimeType: string;
  status: "uploading" | "failed";
  error?: string;
  localPreviewUrl?: string;
  remoteUploadId?: string;
};
type MessageAttachment = Pick<Attachment, "name" | "size" | "mimeType" | "localPreviewUrl">;

function relativeServerActivity(at: number | null, now = Date.now()): string {
  if (!at || !Number.isFinite(at)) return "";
  const elapsedSeconds = Math.max(0, Math.floor((now - at) / 1_000));
  if (elapsedSeconds < 10) return "刚刚";
  if (elapsedSeconds < 60) return `${elapsedSeconds} 秒前`;
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) return `${elapsedMinutes} 分钟前`;
  return `${Math.floor(elapsedMinutes / 60)} 小时前`;
}

function persistedAttachment(attachment: Attachment): Attachment {
  return {
    uploadId: attachment.uploadId,
    name: attachment.name,
    size: attachment.size,
    mimeType: attachment.mimeType,
    sha256: attachment.sha256,
    codexInput: { ...attachment.codexInput },
  };
}
type WorkspaceArtifactTransfer = {
  serverPath: string;
  artifactManifest: ArtifactManifestPayload;
  artifactUrl: ArtifactUrlPayload;
};
type OptimisticMessageOptions = {
  clientMessageId: string;
  intent: "start" | "steer" | "queue";
  createdAt?: string;
};
type WorkSubmissionJournal = {
  version: 1 | 2;
  principalId: string;
  threadId?: string;
  clientMessageId: string;
  text: string;
  input: WorkInput[];
  intent: "start" | "steer";
  createdAt: string;
  rpcRequestId?: string;
  threadStartRequestId?: string;
  expectedTurnId?: string;
  ownerWindowId?: string;
  ownerLeaseUntil?: string;
  authoritativeOutcomeUnknown?: boolean;
  model?: string;
  effort?: string;
  executionPolicy: WorkExecutionPolicy;
  handoffId?: string;
};

type WorkSubmissionSettlement = {
  clientMessageId: string;
  outcome: "confirmed" | "failed" | "abandoned";
  settledAt: string;
};

type ChatSubmissionOperation = "chat/message/create" | "chat/message/edit" | "chat/message/regenerate";
type ChatSubmissionJournal = {
  principalId: string;
  clientMessageId: string;
  operation: ChatSubmissionOperation;
  text: string;
  attachments: Attachment[];
  tools: string[];
  createdAt: string;
  threadId?: string;
  messageId?: string;
};

type HandoffCreateDetail = {
  requestId?: unknown;
  correlationId?: unknown;
  purpose?: unknown;
  kind?: unknown;
  intent?: unknown;
  sourceSurface?: unknown;
  targetSurface?: unknown;
  sourceThreadId?: unknown;
  sourceTurnId?: unknown;
  sourceItemId?: unknown;
  artifactId?: unknown;
  text?: unknown;
  objective?: unknown;
  assetIds?: unknown;
  artifactRefs?: unknown;
};

type MaterializedArtifact = {
  artifactId: string;
  relativePath: string;
  serverPath: string;
  sha256: string;
  size: number;
  mediaType: string;
};

type QueuedWorkFollowUpPayload = {
  text: string;
  input: WorkInput[];
  attachments: Attachment[];
  createdAt: string;
  executionPolicy?: WorkExecutionPolicy;
  handoffId?: string;
};
export type WorkModelOption = {
  id: string;
  model: string;
  displayName: string;
  description: string;
  isDefault: boolean;
  inputModalities: string[];
  efforts: Array<{ effort: string; description: string }>;
  defaultEffort: string | null;
};

type WorkModelSelection = {
  model: string;
  displayName: string;
  effort: string | null;
};
const WORK_EXECUTION_POLICY_LABELS: Record<WorkExecutionPolicy, string> = {
  "read-only": "只读",
  workspace: "工作区",
  "full-access": "完全访问",
};
export type ComposerSubmitShortcut = "enter" | "modifier-enter";

export type ComposerKeyIntent = {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  isComposing?: boolean;
};

export function shouldSubmitComposerKey(
  shortcut: ComposerSubmitShortcut,
  event: ComposerKeyIntent,
): boolean {
  if (event.key !== "Enter" || event.isComposing) return false;
  if (shortcut === "modifier-enter") return Boolean(event.ctrlKey || event.metaKey);
  return !event.shiftKey;
}

export function resizeComposerTextarea(textarea: HTMLTextAreaElement, maxHeight = 184): void {
  textarea.style.height = "auto";
  const styles = window.getComputedStyle(textarea);
  const minimum = Number.parseFloat(styles.minHeight) || 34;
  // Chromium reports a few extra internal pixels even for an empty single
  // line textarea. Preserve the approved compact resting height and grow only
  // when the content actually needs another visual line.
  const contentHeight = textarea.scrollHeight;
  const measured = contentHeight <= minimum + 6 ? minimum : Math.max(minimum, contentHeight);
  const nextHeight = Math.min(measured, maxHeight);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = measured > maxHeight ? "auto" : "hidden";
}

const WORK_REMOTE_ACTION_OPERATIONS: Readonly<Record<string, string>> = {
  file_tree: "fs/readDirectory",
  terminal_output: "thread/read",
  skills: "skills/list",
  mcp: "mcpServerStatus/list",
  permissions: "permissionProfile/list",
  model: "model/list",
  other: "capabilities/read",
};

export function collectSupportedOperations(payload: Record<string, unknown>): Set<string> {
  const result = new Set<string>();
  const candidates = [
    ...(Array.isArray(payload.capabilities) ? payload.capabilities : []),
    ...(Array.isArray(payload.features) ? payload.features : []),
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const record = candidate as Record<string, unknown>;
    const status = String(record.status || "unavailable");
    if (!["available", "degraded", "experimental"].includes(status) || !Array.isArray(record.operations)) continue;
    for (const operation of record.operations) {
      if (typeof operation === "string" && operation) result.add(operation);
    }
  }
  return result;
}

export function remoteActionAvailability(
  status: string,
  underDevelopment: boolean,
  missingOperation: boolean,
  activationPending: boolean,
): { disabled: boolean; clearSelection: boolean } {
  const hardUnavailable = status === "unavailable"
    || status === "unsupported"
    || status === "auth_required"
    || underDevelopment
    || missingOperation;
  return {
    disabled: hardUnavailable || activationPending,
    clearSelection: hardUnavailable,
  };
}

export function threadListItems(response: Record<string, unknown>): Record<string, unknown>[] {
  const candidates = response.items || response.threads || response.data;
  if (!Array.isArray(candidates)) return [];
  return candidates.filter((candidate): candidate is Record<string, unknown> => Boolean(candidate && typeof candidate === "object"));
}

export function threadListNextCursor(response: Record<string, unknown>): string | null {
  const cursor = response.nextCursor ?? response.next_cursor;
  return typeof cursor === "string" && cursor ? cursor : null;
}

export function parseWorkModelCatalog(response: Record<string, unknown>): WorkModelOption[] {
  const candidates = response.data || response.items || response.models;
  if (!Array.isArray(candidates)) return [];
  const result: WorkModelOption[] = [];
  const seen = new Set<string>();
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const record = candidate as Record<string, unknown>;
    if (record.hidden === true) continue;
    const model = String(record.model || record.id || "").trim();
    if (!model || seen.has(model)) continue;
    seen.add(model);
    const effortCandidates = Array.isArray(record.supportedReasoningEfforts) ? record.supportedReasoningEfforts : [];
    const efforts = effortCandidates.flatMap((entry) => {
      if (!entry || typeof entry !== "object") return [];
      const effortRecord = entry as Record<string, unknown>;
      const effort = String(effortRecord.reasoningEffort || effortRecord.effort || "").trim();
      return effort ? [{ effort, description: String(effortRecord.description || "") }] : [];
    });
    result.push({
      id: String(record.id || model),
      model,
      displayName: String(record.displayName || record.name || model),
      description: String(record.description || ""),
      isDefault: record.isDefault === true,
      inputModalities: Array.isArray(record.inputModalities) ? record.inputModalities.map(String) : ["text", "image"],
      efforts,
      defaultEffort: typeof record.defaultReasoningEffort === "string" ? record.defaultReasoningEffort : efforts[0]?.effort || null,
    });
  }
  return result;
}

export function reasoningEffortLabel(effort: string): string {
  return ({
    none: "不启用推理",
    minimal: "最低",
    low: "低",
    medium: "中",
    high: "高",
    xhigh: "很高",
    max: "最高",
    ultra: "极高",
  } as Record<string, string>)[effort] || effort;
}

function dispatchSyncState(state: GatewayState): void {
  const syncState = state === "ready" ? "synced" : state === "error" ? "error" : state;
  window.dispatchEvent(new CustomEvent("bianhua:transport-state", { detail: { source: "session", state, syncState } }));
}

function canvasFor(surface: Surface): HTMLElement {
  const selector = surface === "work" ? ".work-panel .conversation-canvas" : ".chat-panel .conversation-canvas";
  const canvas = document.querySelector<HTMLElement>(selector);
  if (!canvas) throw new Error(`找不到 ${surface} 消息区域`);
  return canvas;
}

function formatTime(value = new Date()): string {
  return new Intl.DateTimeFormat(navigator.language, { hour: "2-digit", minute: "2-digit", hour12: false }).format(value);
}

const BRAND_FLOWER_URL = new URL("../../assets/bianhua-flower-transparent.png", import.meta.url).href;

function workflowId(prefix: string): string {
  const suffix = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${suffix}`;
}

function workflowIdentifier(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(normalized) ? normalized : undefined;
}

function handoffPurpose(value: unknown): WorkflowHandoffPurpose | undefined {
  if (value === "idea_to_video_prompt" || value === "chat_to_work"
    || value === "develop-video-prompt" || value === "chat.idea-to-work") return "idea_to_video_prompt";
  if (value === "video_prompt_to_generation" || value === "work_to_video"
    || value === "generate-video" || value === "work.prompt-to-video") return "video_prompt_to_generation";
  if (value === "review_video" || value === "video_review" || value === "review-video") return "review_video";
  if (value === "edit_video" || value === "video_edit" || value === "edit-video") return "edit_video";
  return undefined;
}

function appendMessageHandoffAction(
  article: HTMLElement,
  surface: Surface,
  text: string,
  context: { itemId?: string } = {},
): void {
  if (!text.trim() || article.querySelector("[data-workflow-handoff-action]")) return;
  const purpose: WorkflowHandoffPurpose = surface === "chat"
    ? "idea_to_video_prompt"
    : "video_prompt_to_generation";
  const targetSurface = surface === "chat" ? "work" : "video";
  const actions = document.createElement("div");
  actions.className = "handoff-action-row";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "handoff-action";
  button.dataset.workflowHandoffAction = purpose;
  button.textContent = surface === "chat" ? "交给工作生成视频提示词" : "送往视频";
  button.setAttribute("aria-label", surface === "chat"
    ? "把这条聊天内容交给工作模式生成视频提示词"
    : "把这条工作结果送往视频模式");
  button.addEventListener("click", () => {
    document.dispatchEvent(new CustomEvent("bianhua:handoff-create", {
      detail: {
        purpose,
        sourceSurface: surface,
        targetSurface,
        sourceItemId: context.itemId,
        text,
      },
    }));
  });
  actions.append(button);
  article.append(actions);
}

function scrollConversation(surface: Surface): void {
  const scroll = canvasFor(surface).closest<HTMLElement>(".conversation-scroll");
  requestAnimationFrame(() => scroll?.scrollTo({ top: scroll.scrollHeight, behavior: "smooth" }));
}

function attachmentSizeLabel(size: number): string {
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / 1024 / 1024).toFixed(size >= 10 * 1024 * 1024 ? 1 : 2)} MB`;
}

function fileNameFromPath(path: string, fallback = "附件"): string {
  const value = path.replace(/\\/g, "/").split("/").filter(Boolean).at(-1) || fallback;
  try { return decodeURIComponent(value); } catch { return value; }
}

function messageAttachmentsFromUserItem(item: Record<string, unknown>): MessageAttachment[] {
  const candidates = [item.content, item.input].find(Array.isArray) as Array<Record<string, unknown>> | undefined;
  if (!candidates) return [];
  const attachments: MessageAttachment[] = [];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const type = String(candidate.type || "");
    if (type !== "localImage" && type !== "mention") continue;
    const path = typeof candidate.path === "string" ? candidate.path : "";
    const name = type === "mention" && typeof candidate.name === "string" && candidate.name.trim()
      ? candidate.name.trim()
      : fileNameFromPath(path, type === "localImage" ? "图片" : "附件");
    attachments.push({
      name,
      size: 0,
      mimeType: type === "localImage" ? "image/*" : "application/octet-stream",
    });
  }
  return attachments;
}

function messageAttachmentsFromInputs(inputs: WorkInput[]): MessageAttachment[] {
  return inputs.flatMap((input) => {
    if (input.type === "localImage") {
      return [{ name: fileNameFromPath(input.path, "图片"), size: 0, mimeType: "image/*" }];
    }
    if (input.type === "mention") {
      return [{ name: input.name || fileNameFromPath(input.path), size: 0, mimeType: "application/octet-stream" }];
    }
    return [];
  });
}

function renderMessageAttachments(article: HTMLElement, attachments: MessageAttachment[]): void {
  article.querySelector("[data-message-attachments]")?.remove();
  if (!attachments.length) return;
  const region = document.createElement("div");
  region.className = "runtime-message-attachments";
  region.dataset.messageAttachments = "";
  region.setAttribute("aria-label", `附件 ${attachments.length} 个`);
  for (const attachment of attachments) {
    const card = document.createElement("span");
    card.className = "runtime-message-attachment";
    if (attachment.localPreviewUrl && attachment.mimeType.startsWith("image/")) {
      const preview = document.createElement("img");
      preview.src = attachment.localPreviewUrl;
      preview.alt = "";
      card.append(preview);
    } else {
      const icon = document.createElement("span");
      icon.className = "runtime-attachment-file-icon";
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = attachment.mimeType.startsWith("image/") ? "图" : "文";
      card.append(icon);
    }
    const copy = document.createElement("span");
    const name = document.createElement("strong");
    name.textContent = attachment.name;
    copy.append(name);
    const size = attachmentSizeLabel(attachment.size);
    if (size) {
      const detail = document.createElement("small");
      detail.textContent = size;
      copy.append(detail);
    }
    card.append(copy);
    region.append(card);
  }
  const bubble = article.querySelector(".user-bubble");
  if (bubble) bubble.after(region);
  else article.append(region);
}

function renderUserMessage(surface: Surface, text: string, optimistic?: OptimisticMessageOptions): HTMLElement {
  const article = document.createElement("article");
  article.className = `message user-message runtime-message${surface === "work" ? " compact" : ""}`;
  article.setAttribute("aria-label", "你的消息");
  article.dataset.messageSurface = surface;
  article.dataset.messageRole = "user";
  article.dataset.messageText = text;
  if (optimistic) {
    article.dataset.optimistic = "pending";
    article.dataset.clientMessageId = optimistic.clientMessageId;
    article.dataset.submissionIntent = optimistic.intent;
    article.setAttribute("aria-busy", "true");
  }
  const meta = document.createElement("div");
  meta.className = "message-meta";
  const time = document.createElement("time");
  const restoredTime = optimistic?.createdAt ? new Date(optimistic.createdAt) : new Date();
  const messageTime = Number.isNaN(restoredTime.getTime()) ? new Date() : restoredTime;
  time.dateTime = messageTime.toISOString();
  time.textContent = formatTime(messageTime);
  const you = document.createElement("span");
  you.textContent = "你";
  const avatar = document.createElement("span");
  avatar.className = "mini-avatar";
  avatar.textContent = "Y";
  meta.append(time, you, avatar);
  if (optimistic) {
    const delivery = document.createElement("span");
    delivery.className = "runtime-delivery-state";
    delivery.textContent = optimistic.intent === "queue" ? "正在加入下一轮" : optimistic.intent === "steer" ? "正在发送补充引导" : "正在发送";
    meta.insertBefore(delivery, avatar);
  }
  const bubble = document.createElement("div");
  bubble.className = "user-bubble";
  bubble.textContent = text;
  bubble.hidden = !text;
  article.append(meta, bubble);
  if (surface === "chat") appendMessageHandoffAction(article, surface, text);
  canvasFor(surface).append(article);
  scrollConversation(surface);
  return article;
}

function updateOptimisticMessage(article: HTMLElement, event: "rpcAccepted" | "serverConfirmed" | "rpcFailed"): void {
  const current = (article.dataset.optimistic || "pending") as OptimisticDelivery;
  const next = transitionOptimisticDelivery(current, event);
  article.dataset.optimistic = next;
  article.classList.toggle("runtime-message-failed", next === "failed");
  if (next === "pending") article.setAttribute("aria-busy", "true");
  else article.removeAttribute("aria-busy");
  const status = article.querySelector<HTMLElement>(".runtime-delivery-state");
  if (!status) return;
  const isSteer = article.dataset.submissionIntent === "steer";
  const isQueued = article.dataset.submissionIntent === "queue";
  if (next === "accepted") status.textContent = isQueued ? "已排队 · 当前任务完成后发送" : isSteer ? "补充引导已送达" : "已送达";
  if (next === "confirmed") status.remove();
  if (next === "failed") status.textContent = "未送达 · 内容已恢复，可重试";
}

function retryableOptimisticMessage(surface: Surface, text: string, intent: "start" | "steer" | "queue"): HTMLElement | null {
  return Array.from(canvasFor(surface).querySelectorAll<HTMLElement>(".user-message[data-optimistic='failed']"))
    .find((message) => message.dataset.submissionIntent === intent
      && message.querySelector(".user-bubble")?.textContent === text) || null;
}

function markOptimisticRetry(article: HTMLElement, intent: "start" | "steer" | "queue"): void {
  article.dataset.optimistic = "pending";
  article.dataset.submissionIntent = intent;
  article.classList.remove("runtime-message-failed");
  article.setAttribute("aria-busy", "true");
  const status = article.querySelector<HTMLElement>(".runtime-delivery-state");
  if (status) status.textContent = intent === "queue" ? "正在重新发送下一轮" : intent === "steer" ? "正在重新发送补充引导" : "正在重新发送";
}

function restoreComposerText(prompt: HTMLTextAreaElement, text: string): void {
  const current = prompt.value.trim();
  if (!current) prompt.value = text;
  else if (current !== text) prompt.value = `${text}\n\n${prompt.value}`;
  prompt.dispatchEvent(new Event("input", { bubbles: true }));
}

function requestOutcomeMayExist(error: unknown): boolean {
  const candidate = error && typeof error === "object" ? error as Record<string, unknown> : null;
  if (String(candidate?.code || "").toUpperCase() === "REQUEST_OUTCOME_UNKNOWN") return true;
  if (candidate?.retryable === true) return true;
  if (candidate?.retryable === false) return false;
  const message = error instanceof Error ? error.message : String(error || "");
  return /超时|连接|会话|socket|timeout|network/i.test(message);
}

function authoritativeMutationOutcomeUnknown(error: unknown): boolean {
  const candidate = error && typeof error === "object" ? error as Record<string, unknown> : null;
  return String(candidate?.code || "").toUpperCase() === "REQUEST_OUTCOME_UNKNOWN";
}

export function threadReadFailureIsMissing(error: unknown): boolean {
  const candidate = error && typeof error === "object" ? error as Record<string, unknown> : null;
  const code = String(candidate?.code ?? "").trim().toUpperCase().replace(/[-.\s]/g, "_");
  return ["NOT_FOUND", "NOTFOUND", "THREAD_NOT_FOUND", "THREAD_DELETED"].includes(code)
    || candidate?.status === 404
    || candidate?.statusCode === 404;
}

function runtimeErrorDetails(error: unknown): { code: string; message: string } {
  if (error instanceof Error) {
    const candidate = error as Error & { code?: unknown };
    return {
      code: typeof candidate.code === "string" ? candidate.code : "",
      message: error.message,
    };
  }
  if (error && typeof error === "object") {
    const candidate = error as Record<string, unknown>;
    return {
      code: typeof candidate.code === "string" ? candidate.code : "",
      message: typeof candidate.message === "string" ? candidate.message : "",
    };
  }
  return { code: "", message: String(error || "") };
}

export function artifactFailureCanRetry(error: unknown): boolean {
  const { code, message } = runtimeErrorDetails(error);
  if (code) {
    return new Set(["dns_timeout", "dns_failed", "dns_empty", "download_failed"]).has(code);
  }
  if (/项目|SHA-256|校验|路径|权限|磁盘|大小|上限|域名|清单|保留副本|不安全/i.test(message)) return false;
  return /地址已经过期|下载失败（(?:408|409|425|429|5\d\d)）|Failed to fetch|NetworkError|网络|timeout|timed out|连接/i.test(message);
}

export function artifactSaveToastVisible(mediaType: unknown, activeMode: unknown): boolean {
  const normalizedMediaType = String(mediaType || "").trim().toLowerCase();
  if (normalizedMediaType.startsWith("video/")) return activeMode === "video";
  return true;
}

type AssistantRender = { article: HTMLElement; body: HTMLParagraphElement; text: string; status: HTMLElement };

function createAssistantMessage(surface: Surface, label = "正在思考"): AssistantRender {
  const article = document.createElement("article");
  article.className = `message assistant-message runtime-message${surface === "work" ? " work-message" : ""}`;
  article.setAttribute("aria-label", "彼岸花·智体的回复");
  article.dataset.messageSurface = surface;
  article.dataset.messageRole = "assistant";
  const thread = document.createElement("div");
  thread.className = `red-thread${surface === "work" ? " work-thread" : ""}`;
  thread.setAttribute("aria-hidden", "true");
  thread.append(document.createElement("span"));
  const heading = document.createElement("div");
  heading.className = "assistant-heading";
  const brand = document.createElement("span");
  brand.className = "petal-avatar";
  const image = document.createElement("img");
  image.src = BRAND_FLOWER_URL;
  image.width = 38;
  image.height = 38;
  image.alt = "";
  brand.append(image);
  const identity = document.createElement("span");
  const name = document.createElement("strong");
  name.textContent = "彼岸花·智体";
  const status = document.createElement("small");
  status.textContent = label;
  identity.append(name, status);
  const time = document.createElement("time");
  time.dateTime = new Date().toISOString();
  time.textContent = formatTime();
  heading.append(brand, identity, time);
  const wrapper = document.createElement("div");
  wrapper.className = "assistant-body";
  const body = document.createElement("p");
  body.className = "runtime-stream";
  body.setAttribute("aria-live", "polite");
  wrapper.append(body);
  article.append(thread, heading, wrapper);
  canvasFor(surface).append(article);
  scrollConversation(surface);
  return { article, body, text: "", status };
}

function textFrom(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(textFrom).filter(Boolean).join("\n");
  if (!value || typeof value !== "object") return value == null ? "" : String(value);
  const record = value as Record<string, unknown>;
  for (const key of ["text", "content", "message", "summary", "output", "result"]) {
    if (record[key] !== undefined) {
      const text = textFrom(record[key]);
      if (text) return text;
    }
  }
  return "";
}

export type RichTextSegment =
  | { type: "text"; text: string }
  | { type: "link"; text: string; url: string }
  | { type: "workspace-artifact"; text: string; serverPath: string };

export type RichTextBlock =
  | { type: "text"; text: string }
  | { type: "code"; text: string; language?: string };

/**
 * Splits the fenced-code subset emitted by Codex without exposing Markdown
 * control lines such as ```http or ```text in the conversation UI.
 */
export function parseRichTextBlocks(value: string): RichTextBlock[] {
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  const blocks: RichTextBlock[] = [];
  let textLines: string[] = [];
  let codeLines: string[] | null = null;
  let language = "";
  const pushText = () => {
    if (!textLines.length) return;
    blocks.push({ type: "text", text: textLines.join("\n") });
    textLines = [];
  };
  const pushCode = () => {
    if (!codeLines) return;
    const text = codeLines.join("\n").replace(/^\n+|\n+$/g, "");
    blocks.push({ type: "code", text, ...(language ? { language } : {}) });
    codeLines = null;
    language = "";
  };

  for (const line of lines) {
    if (codeLines) {
      if (/^\s*```\s*$/.test(line)) pushCode();
      else codeLines.push(line);
      continue;
    }
    const opening = line.match(/^\s*```\s*([^`]*)$/);
    if (opening) {
      pushText();
      language = opening[1].trim().split(/\s+/, 1)[0].toLowerCase();
      codeLines = [];
      continue;
    }
    textLines.push(line);
  }
  pushCode();
  pushText();
  return blocks.length ? blocks : [{ type: "text", text: value }];
}

function safeExternalUrl(value: string): string | null {
  try {
    const parsed = new URL(value);
    if (!["http:", "https:"].includes(parsed.protocol) || parsed.username || parsed.password) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

function trimBareUrl(value: string): { url: string; suffix: string } {
  const trimmed = value.replace(/[),.;!?，。；！？）》】」』]+$/u, "");
  return { url: trimmed, suffix: value.slice(trimmed.length) };
}

/** Converts the small, safe link subset emitted by Codex into DOM-ready parts. */
export function parseRichTextLinks(value: string): RichTextSegment[] {
  const segments: RichTextSegment[] = [];
  const pattern = /\[([^\]\r\n]{1,240})\][ \t\r\n]*\((?:(https?:\/\/[^\s)]+)|<((?:\/srv\/bianhua\/workspaces\/)[^>\r\n]{1,2048})>|((?:\/srv\/bianhua\/workspaces\/)[^)\r\n]{1,2048}))\)|(https?:\/\/[^\s<>"']+)/giu;
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) segments.push({ type: "text", text: value.slice(cursor, index) });
    const markdownUrl = match[2];
    const serverPath = (match[3] || match[4] || "").trim();
    if (serverPath) {
      segments.push({
        type: "workspace-artifact",
        text: match[1]?.trim() || serverPath.split("/").at(-1) || "工作产物",
        serverPath,
      });
      cursor = index + match[0].length;
      continue;
    }
    const bare = markdownUrl ? { url: markdownUrl, suffix: "" } : trimBareUrl(match[5] || "");
    const url = safeExternalUrl(bare.url);
    if (url) {
      segments.push({ type: "link", text: match[1]?.trim() || bare.url, url });
      if (bare.suffix) segments.push({ type: "text", text: bare.suffix });
    } else {
      segments.push({ type: "text", text: match[0] });
    }
    cursor = index + match[0].length;
  }
  if (cursor < value.length) segments.push({ type: "text", text: value.slice(cursor) });
  return segments.length ? segments : [{ type: "text", text: value }];
}

async function copyPlainText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const field = document.createElement("textarea");
  field.value = text;
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.append(field);
  field.select();
  const copied = document.execCommand("copy");
  field.remove();
  if (!copied) throw new Error("复制失败");
}

async function openExternalUrl(url: string): Promise<void> {
  const safeUrl = safeExternalUrl(url);
  if (!safeUrl) throw new Error("链接地址无效");
  if (isTauri()) {
    const { openUrl } = await import("@tauri-apps/plugin-opener");
    await openUrl(safeUrl);
    return;
  }
  const opened = window.open(safeUrl, "_blank", "noopener,noreferrer");
  if (!opened) throw new Error("浏览器阻止了新窗口，请允许打开外部链接");
}

let externalLinkMenu: HTMLElement | null = null;
let externalLinkMenuCleanup: (() => void) | null = null;

function closeExternalLinkMenu(restoreFocus = false): void {
  const trigger = externalLinkMenu?.dataset.triggerId
    ? document.getElementById(externalLinkMenu.dataset.triggerId)
    : null;
  externalLinkMenuCleanup?.();
  externalLinkMenuCleanup = null;
  externalLinkMenu?.remove();
  externalLinkMenu = null;
  if (restoreFocus && trigger instanceof HTMLElement) trigger.focus();
}

function showExternalLinkMenu(trigger: HTMLAnchorElement, url: string, clientX: number, clientY: number): void {
  closeExternalLinkMenu();
  if (!trigger.id) trigger.id = `runtime-link-${crypto.randomUUID()}`;
  const menu = document.createElement("div");
  menu.className = "runtime-context-menu runtime-link-context-menu";
  menu.setAttribute("role", "menu");
  menu.setAttribute("aria-label", "链接操作");
  menu.dataset.triggerId = trigger.id;
  const addItem = (label: string, action: () => Promise<void>) => {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("role", "menuitem");
    button.textContent = label;
    button.addEventListener("click", () => {
      closeExternalLinkMenu();
      void action().catch((error) => toast(error instanceof Error ? error.message : "链接操作失败", "error"));
    });
    menu.append(button);
  };
  addItem("在浏览器中打开", () => openExternalUrl(url));
  addItem("复制链接", async () => {
    await copyPlainText(url);
    toast("链接已复制", "success");
  });
  document.body.append(menu);
  const bounds = menu.getBoundingClientRect();
  menu.style.left = `${Math.max(8, Math.min(clientX, window.innerWidth - bounds.width - 8))}px`;
  menu.style.top = `${Math.max(8, Math.min(clientY, window.innerHeight - bounds.height - 8))}px`;
  externalLinkMenu = menu;
  const onPointerDown = (event: PointerEvent) => {
    if (!menu.contains(event.target as Node)) closeExternalLinkMenu();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeExternalLinkMenu(true);
    }
  };
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("keydown", onKeyDown, true);
  externalLinkMenuCleanup = () => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("keydown", onKeyDown, true);
  };
  menu.querySelector<HTMLButtonElement>("button")?.focus();
}

function showLocalArtifactMenu(
  trigger: HTMLElement,
  actions: { open(): Promise<void>; reveal(): Promise<void>; copy(): Promise<void> },
  clientX: number,
  clientY: number,
): void {
  closeExternalLinkMenu();
  if (!trigger.id) trigger.id = `runtime-artifact-${crypto.randomUUID()}`;
  const menu = document.createElement("div");
  menu.className = "runtime-context-menu runtime-link-context-menu";
  menu.setAttribute("role", "menu");
  menu.setAttribute("aria-label", "产物操作");
  menu.dataset.triggerId = trigger.id;
  const addItem = (label: string, action: () => Promise<void>) => {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("role", "menuitem");
    button.textContent = label;
    button.addEventListener("click", () => {
      closeExternalLinkMenu();
      void action().catch((error) => toast(error instanceof Error ? error.message : "产物操作失败", "error"));
    });
    menu.append(button);
  };
  addItem("打开文件", actions.open);
  addItem("打开所在目录", actions.reveal);
  addItem("复制本地路径", actions.copy);
  document.body.append(menu);
  const bounds = menu.getBoundingClientRect();
  menu.style.left = `${Math.max(8, Math.min(clientX, window.innerWidth - bounds.width - 8))}px`;
  menu.style.top = `${Math.max(8, Math.min(clientY, window.innerHeight - bounds.height - 8))}px`;
  externalLinkMenu = menu;
  const onPointerDown = (event: PointerEvent) => {
    if (!menu.contains(event.target as Node)) closeExternalLinkMenu();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeExternalLinkMenu(true);
    }
  };
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("keydown", onKeyDown, true);
  externalLinkMenuCleanup = () => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("keydown", onKeyDown, true);
  };
  menu.querySelector<HTMLButtonElement>("button")?.focus();
}

function externalLink(text: string, url: string): HTMLAnchorElement {
  const anchor = document.createElement("a");
  anchor.className = "runtime-external-link";
  anchor.href = url;
  anchor.textContent = text;
  anchor.title = url;
  anchor.rel = "noopener noreferrer";
  anchor.addEventListener("click", (event) => {
    event.preventDefault();
    void openExternalUrl(url).catch((error) => toast(error instanceof Error ? error.message : "无法打开链接", "error"));
  });
  anchor.addEventListener("contextmenu", (event) => {
    event.preventDefault();
    showExternalLinkMenu(anchor, url, event.clientX, event.clientY);
  });
  return anchor;
}

function workspaceArtifactLink(text: string, serverPath: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "runtime-work-artifact";
  button.dataset.workArtifactServerPath = serverPath;
  button.title = "将服务器产物保存到本地后打开";
  const icon = document.createElement("span");
  icon.className = "runtime-work-artifact-icon";
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = "↗";
  const copy = document.createElement("span");
  copy.className = "runtime-work-artifact-copy";
  const name = document.createElement("strong");
  name.textContent = text;
  const status = document.createElement("small");
  status.dataset.workArtifactStatus = "";
  status.textContent = "准备保存到本地产物目录";
  copy.append(name, status);
  button.append(icon, copy);
  return button;
}

function appendLinkedText(container: HTMLElement, value: string): void {
  for (const segment of parseRichTextLinks(value)) {
    if (segment.type === "text") container.append(document.createTextNode(segment.text));
    else if (segment.type === "link") container.append(externalLink(segment.text, segment.url));
    else container.append(workspaceArtifactLink(segment.text, segment.serverPath));
  }
}

function appendInlineRichText(container: HTMLElement, value: string): void {
  const inlineCode = /`([^`\r\n]+)`/g;
  let cursor = 0;
  for (const match of value.matchAll(inlineCode)) {
    const index = match.index ?? 0;
    if (index > cursor) appendLinkedText(container, value.slice(cursor, index));
    const code = document.createElement("code");
    code.className = "runtime-inline-code";
    code.textContent = match[1];
    container.append(code);
    cursor = index + match[0].length;
  }
  if (cursor < value.length) appendLinkedText(container, value.slice(cursor));
}

function renderRichText(container: HTMLElement, value: string): void {
  container.className = "runtime-stream runtime-rich-text";
  const fragment = document.createDocumentFragment();
  for (const block of parseRichTextBlocks(value)) {
    if (block.type === "text") {
      const span = document.createElement("span");
      span.className = "runtime-markdown-text";
      appendInlineRichText(span, block.text);
      fragment.append(span);
      continue;
    }
    const code = document.createElement("code");
    code.className = "runtime-markdown-code";
    code.setAttribute("aria-label", "Code block");
    appendLinkedText(code, block.text);
    fragment.append(code);
  }
  container.replaceChildren(fragment);
}

export type WorkJournalHistoryResolution = "confirmed" | "notAccepted" | "pending";

/**
 * Reconciles an outcome-unknown client journal against an authoritative
 * thread/resume response. A steer cannot be accepted after its target turn is
 * terminal, and no journal is allowed to hold the composer forever.
 */
export function resolveWorkJournalAgainstHistory(
  journal: {
    version?: number;
    clientMessageId: string;
    text: string;
    intent: "start" | "steer";
    createdAt: string;
    expectedTurnId?: string;
    authoritativeOutcomeUnknown?: boolean;
  },
  turns: Record<string, unknown>[],
  attempt: number,
  now = Date.now(),
): WorkJournalHistoryResolution {
  const terminal = new Set(["completed", "interrupted", "failed"]);
  const active = turns.some((turn) => ["inProgress", "in_progress", "running"].includes(String(turn.status || "")));
  const itemMatchesClient = (turn: Record<string, unknown>) => Array.isArray(turn.items) && turn.items.some((candidate) => {
    if (!candidate || typeof candidate !== "object") return false;
    const item = candidate as Record<string, unknown>;
    return item.type === "userMessage"
      && String(item.clientId || item.clientMessageId || item.clientUserMessageId || "") === journal.clientMessageId;
  });
  if (turns.some(itemMatchesClient)) return "confirmed";

  const expected = journal.expectedTurnId
    ? turns.find((turn) => String(turn.id || "") === journal.expectedTurnId)
    : undefined;
  if (journal.version === 2
    && journal.intent === "steer"
    && expected
    && terminal.has(String(expected.status || ""))) {
    return "notAccepted";
  }

  // A v2 start has a durable gateway request identity. An older thread
  // snapshot that does not contain the new client id is never proof that the
  // mutation failed; only the cached RPC outcome can settle it. A steer whose
  // expected turn is already terminal is the exception handled above.
  if (journal.version === 2 && !journal.authoritativeOutcomeUnknown) return "pending";

  if (journal.intent === "steer") {
    if (journal.version !== 2 && expected && Array.isArray(expected.items) && expected.items.some((candidate) => {
      if (!candidate || typeof candidate !== "object") return false;
      const item = candidate as Record<string, unknown>;
      return item.type === "userMessage" && textFrom(item).trim() === journal.text.trim();
    })) return "confirmed";
    if ((expected && terminal.has(String(expected.status || ""))) || !active) return "notAccepted";
  }

  const ageMs = Math.max(0, now - Date.parse(journal.createdAt));
  if (!active && ageMs >= 30_000 && attempt >= 2) return "notAccepted";
  if (ageMs >= 5 * 60_000) return "notAccepted";
  return "pending";
}

export function imageGenerationDataUrl(item: Record<string, unknown>): string | null {
  const result = typeof item.result === "string" ? item.result.replace(/\s+/g, "") : "";
  if (!result || result.length > 32 * 1024 * 1024 || result.length % 4 !== 0) return null;
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(result)) return null;
  const mimeType = result.startsWith("iVBORw0KGgo")
    ? "image/png"
    : result.startsWith("/9j/")
      ? "image/jpeg"
      : result.startsWith("UklGR")
        ? "image/webp"
        : result.startsWith("R0lGOD")
          ? "image/gif"
          : null;
  return mimeType ? `data:${mimeType};base64,${result}` : null;
}

export type WebSearchPresentation = {
  state: "running" | "completed";
  prefix: string;
  title?: string;
  url?: string;
  suffix?: string;
};

export type ImageViewPresentation = {
  state: "running" | "completed";
  summary: string;
};

export type FileChangePresentation = {
  state: "running" | "completed" | "failed" | "declined";
  summary: string;
  details: string[];
};

function structuredItemState(item: Record<string, unknown>): FileChangePresentation["state"] {
  const status = String(item.status || "").replace(/[_-]/g, "").toLowerCase();
  if (["inprogress", "running", "pending"].includes(status)) return "running";
  if (["failed", "error"].includes(status)) return "failed";
  if (["declined", "cancelled", "canceled"].includes(status)) return "declined";
  return "completed";
}

function clientFilePath(value: unknown): string {
  const raw = String(value || "").replace(/\\/g, "/").replace(/[\u0000-\u001f\u007f]/g, "").trim();
  if (!raw) return "未命名文件";
  const workspaceMarker = "/bianhua-intelligence/";
  const workspaceIndex = raw.toLowerCase().indexOf(workspaceMarker);
  const relative = workspaceIndex >= 0
    ? raw.slice(workspaceIndex + workspaceMarker.length)
    : /^\/?(?:srv|var|home|users)\//i.test(raw) || /^[A-Za-z]:\//.test(raw)
      ? raw.split("/").filter(Boolean).at(-1) || raw
      : raw.replace(/^\.\//, "");
  return relative.length > 180 ? `…${relative.slice(-179)}` : relative;
}

function fileChangeKindLabel(value: unknown): string {
  const raw = value && typeof value === "object"
    ? (value as Record<string, unknown>).type ?? (value as Record<string, unknown>).kind
    : value;
  const kind = String(raw || "").replace(/[_-]/g, "").toLowerCase();
  if (["add", "added", "create", "created"].includes(kind)) return "新建";
  if (["delete", "deleted", "remove", "removed"].includes(kind)) return "删除";
  if (["rename", "renamed", "move", "moved"].includes(kind)) return "移动";
  return "修改";
}

/**
 * Converts an app-server fileChange item into client-safe UI copy. Diffs and
 * absolute server paths are intentionally excluded from the presentation.
 */
export function fileChangePresentation(item: Record<string, unknown>): FileChangePresentation {
  const state = structuredItemState(item);
  const changes = Array.isArray(item.changes)
    ? item.changes.filter((candidate): candidate is Record<string, unknown> => Boolean(candidate && typeof candidate === "object"))
    : [];
  const details = [...new Set(changes.map((change) => `${fileChangeKindLabel(change.kind)} · ${clientFilePath(change.path)}`))];
  const target = changes.length === 1 ? clientFilePath(changes[0].path) : "";
  const action = changes.length === 1 ? fileChangeKindLabel(changes[0].kind) : "更新";
  const summary = state === "running"
    ? changes.length > 1 ? `正在更新 ${changes.length} 个文件` : target ? `正在${action} ${target}` : "正在准备文件变更"
    : state === "failed"
      ? `${changes.length > 1 ? `${changes.length} 个文件` : target || "文件"}更新失败`
      : state === "declined"
        ? `已取消${changes.length > 1 ? `${changes.length} 个文件的` : target ? ` ${target} 的` : ""}更新`
        : changes.length > 1 ? `已更新 ${changes.length} 个文件` : target ? `已${action} ${target}` : "文件更新已完成";
  return { state, summary, details };
}

export function imageViewPresentation(item: Record<string, unknown>): ImageViewPresentation {
  const rawPath = String(item.path || item.filePath || item.imagePath || "").trim();
  const normalized = rawPath.replace(/\\/g, "/").replace(/\/+$/, "");
  const encodedName = normalized.split("/").at(-1) || "";
  let fileName = encodedName;
  try { fileName = decodeURIComponent(encodedName); } catch { /* keep the safe original */ }
  fileName = fileName.replace(/[\u0000-\u001f\u007f]/g, "").slice(0, 160);
  const status = String(item.status || "").toLowerCase();
  const running = ["inprogress", "in_progress", "running"].includes(status);
  return {
    state: running ? "running" : "completed",
    summary: `${running ? "正在查看图片" : "已查看图片"}${fileName ? ` · ${fileName}` : ""}`,
  };
}

export function webSearchPresentation(item: Record<string, unknown>): WebSearchPresentation {
  const results = Array.isArray(item.results)
    ? item.results.filter((candidate): candidate is Record<string, unknown> => Boolean(candidate && typeof candidate === "object"))
    : [];
  const firstLink = results.find((candidate) => safeExternalUrl(String(candidate.url || "")));
  const query = String(item.query || "").trim();
  const action = item.action && typeof item.action === "object" ? item.action as Record<string, unknown> : null;
  if (!action && !results.length) {
    return { state: "running", prefix: query ? `正在搜索“${query}”` : "正在搜索网页" };
  }
  if (firstLink) {
    const url = safeExternalUrl(String(firstLink.url))!;
    const title = String(firstLink.title || firstLink.domain || new URL(url).hostname).trim();
    return {
      state: "completed",
      prefix: "已查看",
      title,
      url,
      ...(results.length > 1 ? { suffix: `等 ${results.length} 个结果` } : {}),
    };
  }
  if (query) return { state: "completed", prefix: `已搜索“${query}”` };
  const actionType = String(action?.type || "");
  const labels: Record<string, string> = {
    search: "已搜索网页",
    openPage: "已打开网页",
    find: "已在网页中查找",
    click: "已浏览网页",
    other: "已查看网页",
  };
  return { state: "completed", prefix: labels[actionType] || "已完成网页操作" };
}

function lastReadableLine(value: string, fallback: string): string {
  const lines = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const text = lines.at(-1) || fallback;
  return text.length > 140 ? `${text.slice(0, 139)}…` : text;
}

function renderExpandableItem(
  renderer: AssistantRender,
  summary: string,
  detail: string,
  options: { kind: string; running?: boolean } = { kind: "event" },
): void {
  const wrapper = renderer.body.parentElement;
  if (!wrapper) return;
  renderer.body.className = "runtime-stream runtime-item-summary";
  renderer.body.replaceChildren();
  for (const existing of wrapper.querySelectorAll(":scope > .runtime-item-detail")) existing.remove();

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "runtime-item-toggle";
  toggle.setAttribute("aria-expanded", "false");
  toggle.dataset.kind = options.kind;
  const marker = document.createElement("span");
  marker.className = "runtime-item-marker";
  marker.setAttribute("aria-hidden", "true");
  if (options.running) marker.dataset.running = "true";
  const copy = document.createElement("span");
  copy.className = "runtime-item-summary-copy";
  copy.textContent = summary;
  const chevron = document.createElement("span");
  chevron.className = "runtime-item-chevron";
  chevron.setAttribute("aria-hidden", "true");
  toggle.append(marker, copy, chevron);
  renderer.body.append(toggle);

  const expanded = document.createElement("pre");
  expanded.className = "runtime-item-detail";
  expanded.dataset.kind = options.kind;
  expanded.hidden = true;
  expanded.textContent = detail;
  toggle.addEventListener("click", () => {
    const next = expanded.hidden;
    expanded.hidden = !next;
    toggle.setAttribute("aria-expanded", String(next));
  });
  wrapper.append(expanded);
}

function renderCompactItem(
  renderer: AssistantRender,
  summary: string,
  options: { kind: string; running?: boolean },
): void {
  const wrapper = renderer.body.parentElement;
  if (!wrapper) return;
  renderer.body.className = "runtime-stream runtime-item-summary";
  renderer.body.replaceChildren();
  for (const existing of wrapper.querySelectorAll(":scope > .runtime-item-detail")) existing.remove();
  const row = document.createElement("div");
  row.className = "runtime-item-toggle runtime-item-static";
  row.dataset.kind = options.kind;
  const marker = document.createElement("span");
  marker.className = "runtime-item-marker";
  marker.setAttribute("aria-hidden", "true");
  if (options.running) marker.dataset.running = "true";
  const copy = document.createElement("span");
  copy.className = "runtime-item-summary-copy";
  copy.textContent = summary;
  row.append(marker, copy);
  renderer.body.append(row);
}

function renderWebSearch(renderer: AssistantRender, item: Record<string, unknown>): void {
  const presentation = webSearchPresentation(item);
  renderer.status.textContent = presentation.state === "running" ? "网页搜索中" : "网页搜索";
  renderer.body.className = "runtime-stream runtime-web-search";
  const marker = document.createElement("span");
  marker.className = "runtime-web-search-icon";
  marker.setAttribute("aria-hidden", "true");
  const content = document.createElement("span");
  content.append(document.createTextNode(presentation.prefix));
  if (presentation.title && presentation.url) {
    content.append(document.createTextNode(" "), externalLink(presentation.title, presentation.url));
  }
  if (presentation.suffix) content.append(document.createTextNode(` ${presentation.suffix}`));
  renderer.body.replaceChildren(marker, content);
}

function generatedImageFileInfo(dataUrl: string, itemId: unknown): { extension: string; mimeType: string; fileName: string } {
  const mimeType = dataUrl.slice(5, dataUrl.indexOf(";"));
  const extension = mimeType === "image/png"
    ? "png"
    : mimeType === "image/jpeg"
      ? "jpg"
      : mimeType === "image/webp"
        ? "webp"
        : "gif";
  const safeId = String(itemId || Date.now()).replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 80);
  return { extension, mimeType, fileName: `generated-image-${safeId}.${extension}` };
}

function imageDataBytes(dataUrl: string): Uint8Array {
  const separator = dataUrl.indexOf(",");
  if (separator < 0 || !dataUrl.slice(0, separator).endsWith(";base64")) throw new Error("图片数据格式无效");
  const binary = atob(dataUrl.slice(separator + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function imageDataBlob(bytes: Uint8Array, mimeType: string): Blob {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return new Blob([buffer], { type: mimeType });
}

function showGeneratedImageViewer(dataUrl: string, fileName: string): void {
  document.querySelector<HTMLDialogElement>(".runtime-image-viewer")?.close();
  const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const dialog = document.createElement("dialog");
  dialog.className = "runtime-image-viewer";
  dialog.setAttribute("aria-label", "查看生成的原图");
  const shell = document.createElement("div");
  shell.className = "runtime-image-viewer-shell";
  const header = document.createElement("header");
  const title = document.createElement("strong");
  title.textContent = fileName;
  const close = document.createElement("button");
  close.type = "button";
  close.className = "runtime-image-viewer-close";
  close.setAttribute("aria-label", "关闭原图查看器");
  close.textContent = "×";
  header.append(title, close);
  const viewport = document.createElement("div");
  viewport.className = "runtime-image-viewer-viewport";
  const image = document.createElement("img");
  image.src = dataUrl;
  image.alt = "生成的原图";
  image.title = "点击切换适应窗口与原始尺寸";
  image.addEventListener("click", () => image.classList.toggle("is-actual-size"));
  viewport.append(image);
  const hint = document.createElement("p");
  hint.textContent = "点击图片切换原始尺寸 · Esc 关闭";
  shell.append(header, viewport, hint);
  dialog.append(shell);
  const dismiss = () => dialog.close();
  close.addEventListener("click", dismiss);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dismiss();
  });
  dialog.addEventListener("close", () => {
    dialog.remove();
    if (restoreTarget?.isConnected) queueMicrotask(() => restoreTarget.focus());
  }, { once: true });
  document.body.append(dialog);
  dialog.showModal();
  close.focus();
}

async function saveGeneratedImage(dataUrl: string, fileName: string, mimeType: string, extension: string): Promise<boolean> {
  const bytes = imageDataBytes(dataUrl);
  if (isTauri()) {
    const [{ save }, { writeFile }] = await Promise.all([
      import("@tauri-apps/plugin-dialog"),
      import("@tauri-apps/plugin-fs"),
    ]);
    const destination = await save({
      defaultPath: fileName,
      filters: [{ name: "图片", extensions: [extension] }],
    });
    if (!destination) return false;
    await writeFile(destination, bytes);
    return true;
  }

  const picker = (window as unknown as {
    showSaveFilePicker?: (options: unknown) => Promise<{
      createWritable: () => Promise<{ write: (blob: Blob) => Promise<void>; close: () => Promise<void> }>;
    }>;
  }).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker.call(window, {
        suggestedName: fileName,
        types: [{ description: "图片", accept: { [mimeType]: [`.${extension}`] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(imageDataBlob(bytes, mimeType));
      await writable.close();
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return false;
      throw error;
    }
  }
  const blobUrl = URL.createObjectURL(imageDataBlob(bytes, mimeType));
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
  return true;
}

function renderGeneratedImage(renderer: AssistantRender, item: Record<string, unknown>): void {
  const dataUrl = imageGenerationDataUrl(item);
  if (!dataUrl) {
    const status = String(item.status || "").toLowerCase();
    renderer.body.textContent = status === "inprogress" || status === "in_progress"
      ? "正在服务器生成图片…"
      : status === "failed"
        ? `图片生成失败${textFrom(item.error) ? `：${textFrom(item.error)}` : "。"}`
        : "正在从服务器恢复图片数据…";
    return;
  }
  const wrapper = renderer.body.parentElement;
  if (!wrapper) return;
  for (const existing of wrapper.querySelectorAll(":scope > .runtime-generated-image")) existing.remove();
  renderer.status.textContent = "图像已生成";
  renderer.body.className = "runtime-stream runtime-image-summary";
  renderer.body.textContent = "图片已生成，可直接预览或下载原图。";

  const figure = document.createElement("figure");
  figure.className = "runtime-generated-image";
  const image = document.createElement("img");
  image.src = dataUrl;
  image.alt = "生成的图片";
  image.loading = "eager";
  const { extension, mimeType, fileName } = generatedImageFileInfo(dataUrl, item.id);
  image.tabIndex = 0;
  image.setAttribute("role", "button");
  image.setAttribute("aria-label", "放大查看生成的原图");
  image.addEventListener("click", () => showGeneratedImageViewer(dataUrl, fileName));
  image.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      showGeneratedImageViewer(dataUrl, fileName);
    }
  });
  image.addEventListener("load", () => {
    renderer.body.textContent = `图片已生成 · ${image.naturalWidth} × ${image.naturalHeight}`;
  }, { once: true });
  const actions = document.createElement("figcaption");
  const open = document.createElement("button");
  open.type = "button";
  open.textContent = "查看原图";
  open.addEventListener("click", () => showGeneratedImageViewer(dataUrl, fileName));
  const download = document.createElement("button");
  download.type = "button";
  download.textContent = "下载";
  download.addEventListener("click", () => {
    download.disabled = true;
    download.textContent = "正在保存…";
    void saveGeneratedImage(dataUrl, fileName, mimeType, extension)
      .then((saved) => {
        if (saved) toast("图片已保存", "success");
      })
      .catch((error) => toast(error instanceof Error ? error.message : "图片保存失败", "error"))
      .finally(() => {
        download.disabled = false;
        download.textContent = "下载";
      });
  });
  actions.append(open, download);
  figure.append(image, actions);
  wrapper.append(figure);
}

function renderStructuredItem(renderer: AssistantRender, item: Record<string, unknown>): void {
  const type = String(item.type || item.kind || "服务器事件");
  const labels: Record<string, string> = {
    plan: "执行计划",
    reasoning: "推理摘要",
    commandExecution: "命令执行",
    fileChange: "文件变更",
    mcpToolCall: "MCP 工具调用",
    dynamicToolCall: "工具调用",
    collabToolCall: "协作任务",
    webSearch: "网页搜索",
    imageView: "图像查看",
    imageGeneration: "图像生成",
    enteredReviewMode: "进入审查模式",
    exitedReviewMode: "完成审查",
    contextCompaction: "上下文整理",
  };
  renderer.status.textContent = labels[type] || type;
  if (type === "imageGeneration") {
    renderGeneratedImage(renderer, item);
    return;
  }
  if (type === "webSearch") {
    renderWebSearch(renderer, item);
    return;
  }
  if (type === "imageView") {
    const presentation = imageViewPresentation(item);
    renderCompactItem(renderer, presentation.summary, {
      kind: "image-view",
      running: presentation.state === "running",
    });
    return;
  }
  if (type === "reasoning") {
    const readable = textFrom(item.summary) || textFrom(item.content) || renderer.text;
    if (!readable.trim()) {
      renderer.article.remove();
      return;
    }
    renderExpandableItem(renderer, lastReadableLine(readable, "已完成分析"), readable, { kind: "reasoning" });
    return;
  }
  if (type === "plan") {
    const readable = textFrom(item.text ?? item.plan ?? item.steps ?? item.content);
    if (readable.trim()) {
      renderExpandableItem(renderer, `执行计划 · ${lastReadableLine(readable, "已更新")}`, readable, { kind: "plan" });
    } else {
      renderCompactItem(renderer, "执行计划已更新", { kind: "plan", running: false });
    }
    return;
  }
  if (type === "commandExecution") {
    const command = String(item.command || "").trim();
    const output = String(item.aggregatedOutput || item.output || "").trimEnd();
    const exitCode = Number.isInteger(item.exitCode) ? Number(item.exitCode) : null;
    const detail = [
      command ? `$ ${command}` : "命令执行",
      output,
      exitCode === null ? "" : `退出代码 ${exitCode}`,
    ].filter(Boolean).join("\n");
    const running = String(item.status || "") === "inProgress";
    const summarySource = running ? output || command : command || output;
    renderExpandableItem(
      renderer,
      `${running ? "正在执行" : exitCode && exitCode !== 0 ? "执行失败" : "执行完成"} · ${lastReadableLine(summarySource, "后台命令")}`,
      detail,
      { kind: "command", running },
    );
    return;
  }
  if (type === "fileChange") {
    const presentation = fileChangePresentation(item);
    const options = { kind: "file", running: presentation.state === "running" };
    if (presentation.details.length) {
      renderExpandableItem(renderer, presentation.summary, presentation.details.join("\n"), options);
    } else {
      renderCompactItem(renderer, presentation.summary, options);
    }
    return;
  }
  if (["mcpToolCall", "dynamicToolCall"].includes(type)) {
    const appContext = item.appContext && typeof item.appContext === "object"
      ? item.appContext as Record<string, unknown>
      : {};
    const name = String(appContext.appName || item.server || item.tool || "工具").trim();
    const state = structuredItemState(item);
    const summary = state === "running"
      ? `正在使用 ${name}`
      : state === "failed" ? `${name} 调用失败` : state === "declined" ? `已取消使用 ${name}` : `已使用 ${name}`;
    renderCompactItem(renderer, summary, { kind: "tool", running: state === "running" });
    return;
  }
  if (["collabToolCall", "collabAgentToolCall"].includes(type)) {
    const state = structuredItemState(item);
    renderCompactItem(renderer, state === "running" ? "正在协作处理任务" : state === "failed" ? "协作任务未完成" : "协作任务已完成", {
      kind: "collaboration",
      running: state === "running",
    });
    return;
  }
  const compactLabels: Record<string, string> = {
    enteredReviewMode: "已进入审查模式",
    exitedReviewMode: "审查已完成",
    contextCompaction: "较长的任务上下文已整理",
  };
  renderCompactItem(renderer, compactLabels[type] || "服务器操作已完成", { kind: "event", running: false });
}

function toast(message: string, tone: "neutral" | "success" | "error" = "neutral"): void {
  let region = document.querySelector<HTMLElement>("#runtime-toasts");
  if (!region) {
    region = document.createElement("div");
    region.id = "runtime-toasts";
    region.className = "runtime-toasts";
    region.setAttribute("role", "status");
    region.setAttribute("aria-live", "polite");
    document.body.append(region);
  }
  const duplicate = [...region.querySelectorAll<HTMLElement>(".runtime-toast")]
    .find((candidate) => candidate.textContent === message && candidate.dataset.tone === tone);
  if (duplicate) return;
  const item = document.createElement("div");
  item.className = "runtime-toast";
  item.dataset.tone = tone;
  item.textContent = message;
  region.append(item);
  requestAnimationFrame(() => item.classList.add("visible"));
  window.setTimeout(() => {
    item.classList.remove("visible");
    window.setTimeout(() => item.remove(), 180);
  }, 4_000);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const slice = 16_384;
  for (let offset = 0; offset < bytes.length; offset += slice) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + slice));
  }
  return btoa(binary);
}

function base64ToText(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  try {
    const binary = atob(value);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return "[服务器发送了无法解码的终端输出]";
  }
}

function resultId(result: Record<string, unknown> | undefined, kind: "thread" | "turn"): string | undefined {
  if (!result) return undefined;
  const direct = result[`${kind}Id`];
  if (typeof direct === "string") return direct;
  const nested = result[kind];
  if (nested && typeof nested === "object" && typeof (nested as Record<string, unknown>).id === "string") {
    return String((nested as Record<string, unknown>).id);
  }
  return kind === "thread" && typeof result.id === "string" ? result.id : undefined;
}

function domainEventThreadId(data: Record<string, unknown>, item?: Record<string, unknown>): string | null {
  const thread = data.thread && typeof data.thread === "object"
    ? data.thread as Record<string, unknown>
    : null;
  const turn = data.turn && typeof data.turn === "object"
    ? data.turn as Record<string, unknown>
    : null;
  return workflowIdentifier(data.threadId)
    || workflowIdentifier(thread?.id)
    || workflowIdentifier(turn?.threadId)
    || workflowIdentifier(item?.threadId)
    || null;
}

function codexInputForUpload(
  result: Record<string, unknown>,
  file: File,
): CodexFileInput {
  const path = String(result.serverPath || (result.input && typeof result.input === "object" ? (result.input as Record<string, unknown>).path : "") || "");
  const relativePath = String(result.relativePath || "").replace(/\\/g, "/");
  const normalizedPath = path.replace(/\\/g, "/");
  const expectedPrefix = ".bianhua/attachments/upload-";
  if (!normalizedPath.startsWith("/")
    || normalizedPath.includes("/../")
    || !relativePath.startsWith(expectedPrefix)
    || relativePath.includes("../")
    || !normalizedPath.endsWith(`/${relativePath}`)) {
    throw new Error("服务器没有返回工作区内的安全附件路径");
  }
  return file.type.startsWith("image/")
    ? { type: "localImage", path, detail: null }
    : { type: "mention", name: file.name, path };
}

async function sha256(bytes: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

class SessionController {
  readonly config: RuntimeConfig;
  readonly client: GatewayClient;
  readonly artifacts: ArtifactSync;
  readonly artifactReceiver: ArtifactReceiver;
  private selectedTools = new Map<Surface, Set<string>>([["chat", new Set()], ["work", new Set()]]);
  private readonly remoteActionPending = new Set<string>();
  private attachments = new Map<Surface, Attachment[]>([["chat", []], ["work", []]]);
  private attachmentDrafts = new Map<Surface, Map<string, AttachmentDraft>>([["chat", new Map()], ["work", new Map()]]);
  private modeStatuses = new Map<Surface, string>([["chat", "unavailable"], ["work", "unavailable"]]);
  private modeReasons = new Map<Surface, string>();
  private capabilitiesReady = false;
  private readonly supportedOperations = new Set<string>();
  private readonly chatControl = new ChatControlStore();
  private renderers = new Map<string, AssistantRender>();
  private activeThread = new Map<Surface, string>();
  private readonly threadHistoryCache = new Map<string, Record<string, unknown>>();
  private readonly threadHistoryCacheUpdatedAt = new Map<string, number>();
  private readonly dirtyThreadHistories = new Set<string>();
  private readonly threadHistoryPersistence: ThreadHistoryPersistence;
  private readonly recentlyStartedWorkThreads = new Map<string, { item: Record<string, unknown>; expiresAt: number }>();
  private threadOpenEpoch = new Map<Surface, number>([["chat", 0], ["work", 0]]);
  private threadListEpoch = new Map<Surface, number>([["chat", 0], ["work", 0]]);
  private threadListRefreshTimers = new Map<Surface, number>();
  private threadContextMenu: HTMLElement | null = null;
  private threadContextMenuCleanup: (() => void) | null = null;
  private readonly workTerminalReconcileTimers = new Map<string, number>();
  private readonly workTerminalReconcileAttempts = new Map<string, number>();
  private threadLoading = new Set<Surface>();
  private serverPrincipalId: string | null = null;
  private readonly workThreadStoragePrefix: string;
  private readonly chatThreadStoragePrefix: string;
  private readonly chatSubmissionStoragePrefix: string;
  private readonly workSubmissionStoragePrefix: string;
  private readonly workModelStoragePrefix: string;
  private readonly workExecutionPolicyStoragePrefix: string;
  private readonly workPinnedStoragePrefix: string;
  private readonly workArtifactStoragePrefix: string;
  private readonly workArtifactLocalPaths = new Map<string, string>();
  private readonly workArtifactInFlight = new Map<string, Promise<string>>();
  private selectedWorkModel: WorkModelSelection | null = null;
  private selectedWorkExecutionPolicy: WorkExecutionPolicy = "workspace";
  private uncertainChatSubmission: ChatSubmissionJournal | null = null;
  private uncertainChatTimer = 0;
  private uncertainChatAttempt = 0;
  private chatRecoveryInFlight = false;
  private uncertainWorkSubmission: WorkSubmissionJournal | null = null;
  private uncertainWorkTimer = 0;
  private uncertainWorkAttempt = 0;
  private workRecoveryInFlight = false;
  private readonly windowInstanceId = crypto.randomUUID();
  private readonly workControl = new WorkControlStore();
  private workFollowUpBehavior: WorkFollowUpBehavior = DEFAULT_WORK_FOLLOW_UP_BEHAVIOR;
  private composerSubmitShortcut: ComposerSubmitShortcut = "enter";
  private readonly workFollowUpQueues = new Map<string, WorkFollowUpQueue<QueuedWorkFollowUpPayload>>();
  private readonly workFollowUpRetryTimers = new Map<string, number>();
  private readonly workFollowUpRetryAttempts = new Map<string, number>();
  private workRequestInFlight = false;
  private workflowHandoffs: WorkflowHandoffStore | null = null;
  private unsubscribeWorkflowHandoffs: (() => void) | null = null;
  private readonly pendingRecoveredHandoffIds = new Set<string>();
  private readonly recoveringWorkflowHandoffIds = new Set<string>();
  private recoveredHandoffRetryTimer = 0;
  private recoveredHandoffRetryAttempt = 0;
  private readonly submittedVideoHandoffIds = new Set<string>();
  private readonly submittedVideoHandoffPrincipals = new Map<string, string>();
  private readonly acceptedVideoHandoffs = new Map<string, { principalId: string; taskId: string }>();
  private readonly workflowDraftTimers = new Map<"work" | "video", number>();
  private handoffMaterializationAvailable = false;
  private elicitationQueue: Array<{ requestId: string; payload: Record<string, unknown> }> = [];
  private elicitationIds = new Set<string>();
  private elicitationActive = false;
  private activeElicitationRequestId: string | null = null;
  private activeElicitationPayload: Record<string, unknown> | null = null;
  private readonly elicitationThreads = new Map<string, { threadId: string; turnId: string | null }>();
  private elicitationRestoreFocus: HTMLElement | null = null;
  private workActivityTicker = 0;
  private readonly visualDemo = (() => {
    const params = new URLSearchParams(location.search);
    return window.BianhuaVisualDemoAllowed === true
      && (params.get("capture") === "1" || params.get("media") === "demo");
  })();

  constructor(config: RuntimeConfig, client: GatewayClient, artifacts: ArtifactSync) {
    this.config = config;
    this.client = client;
    this.artifacts = artifacts;
    this.artifactReceiver = new ArtifactReceiver(artifacts, config);
    const endpoint = config.gatewayUrl ? new URL(config.gatewayUrl).host : "unconfigured";
    this.threadHistoryPersistence = new ThreadHistoryPersistence(`${config.projectId}:${endpoint}`);
    this.workThreadStoragePrefix = `bianhua-work-thread:${config.projectId}:${endpoint}`;
    this.chatThreadStoragePrefix = `bianhua-chat-thread:${config.projectId}:${endpoint}`;
    this.chatSubmissionStoragePrefix = `bianhua-chat-submission:v1:${config.projectId}:${endpoint}`;
    this.workSubmissionStoragePrefix = `bianhua-work-submission:${config.projectId}:${endpoint}`;
    this.workModelStoragePrefix = `bianhua-work-model:v1:${config.projectId}:${endpoint}`;
    this.workExecutionPolicyStoragePrefix = `bianhua-work-execution-policy:v1:${config.projectId}:${endpoint}`;
    this.workPinnedStoragePrefix = `bianhua-work-pinned:v1:${config.projectId}:${endpoint}`;
    this.workArtifactStoragePrefix = `bianhua-work-artifacts:v1:${config.projectId}:${endpoint}`;
  }

  bind(): void {
    this.prepareRuntimeShell();
    window.addEventListener("storage", (event) => this.onWorkSubmissionStorage(event));
    this.workControl.subscribe((snapshot) => this.renderWorkControl(snapshot));
    this.workActivityTicker = window.setInterval(() => {
      const snapshot = this.workControl.snapshot();
      if (snapshot.isActive) this.renderWorkControl(snapshot);
    }, 15_000);
    this.chatControl.subscribe((snapshot) => this.renderChatControl(snapshot));
    this.resetCapabilities("等待服务器能力清单");
    this.client.addEventListener("state", (event) => {
      const detail = (event as CustomEvent<{ state: GatewayState; previousState?: GatewayState }>).detail;
      const state = detail.state;
      const previousState = detail.previousState;
      dispatchSyncState(state);
      this.renderWorkControl(this.workControl.snapshot());
      if (["offline", "connecting", "reconnecting", "error"].includes(state)) {
        this.resetCapabilities("等待服务器重新确认能力");
        this.setHandoffMaterializationCapability(false);
        this.clearRecoveredHandoffRetry();
      }
      // Returning from a cursor-only replay does not represent a new
      // transport. Running the full ready workflow here would reopen the
      // active thread, generate more Codex metadata events and create a
      // self-sustaining resume loop.
      if (state === "ready" && previousState !== "syncing") this.onGatewayReady().catch(() => {});
    });
    this.client.addEventListener("envelope", (event) => this.onEnvelope((event as CustomEvent<SessionEnvelope>).detail));
    this.client.addEventListener("auth-error", () => toast("服务器身份验证失败，请检查登录状态", "error"));
    // Cursor replay is reflected by the non-intrusive connection status. A
    // toast on every gap obscures real errors and used to incorrectly claim
    // that the network had disconnected.
    this.client.addEventListener("reset-required", (event) => {
      const reason = String((event as CustomEvent).detail?.reason || "服务器会话已重新同步");
      this.resetElicitations();
      toast(reason);
      const threadId = this.activeThread.get("work");
      if (threadId) {
        this.workControl.beginResync(threadId);
        queueMicrotask(() => void this.openThread("work", threadId, undefined, false));
      }
    });
    this.artifacts.addEventListener("saved", (event) => {
      const detail = (event as CustomEvent).detail || {};
      const result = detail.result;
      // Video history already owns its save status and path. Keep that
      // notification inside the Video surface instead of allowing a
      // background reconciliation to interrupt an unrelated Work task.
      if (!artifactSaveToastVisible(detail.mediaType, document.body.dataset.mode)) return;
      toast(`产物已保存：${result.path || "项目目录"}`, "success");
    });
    this.artifacts.addEventListener("permission-required", () => {
      if (!isTauri()) toast("网页端需先在连接详情中授权保存文件夹");
    });
    this.artifacts.addEventListener("error", () => toast("产物保存失败，请检查磁盘与权限", "error"));
    this.artifactReceiver.addEventListener("error", (event) => {
      const artifactId = String((event as CustomEvent).detail?.artifactId || "");
      const error = (event as CustomEvent).detail?.error;
      const { message } = runtimeErrorDetails(error);
      toast(message || "服务器产物校验失败", "error");
      if (artifactId && artifactFailureCanRetry(error)) {
        window.dispatchEvent(new CustomEvent("bianhua:artifact-retry-request", {
          detail: { artifactId },
        }));
      }
    });
    this.artifactReceiver.addEventListener("complete", (event) => {
      const artifactId = String((event as CustomEvent).detail?.artifactId || "");
      const result = (event as CustomEvent).detail?.result;
      if (artifactId) {
        window.dispatchEvent(new CustomEvent("bianhua:artifact-save-complete", {
          detail: { artifactId, result },
        }));
      }
    });
    window.addEventListener("bianhua:artifact-sync-recovered", (event) => {
      const detail = (event as CustomEvent).detail as {
        manifest?: ArtifactManifestPayload;
        url?: ArtifactUrlPayload;
      } | undefined;
      if (!detail?.manifest || !detail.url) return;
      try {
        this.artifactReceiver.handle(createSessionEnvelope({
          id: workflowId("artifact-recovered-manifest"),
          kind: "artifact.manifest",
          payload: detail.manifest,
        }));
        this.artifactReceiver.handle(createSessionEnvelope({
          id: workflowId("artifact-recovered-url"),
          kind: "artifact.url",
          payload: detail.url,
        }));
      } catch (error) {
        toast(error instanceof Error ? error.message : "恢复的视频产物合同无效", "error");
      }
    });

    for (const send of document.querySelectorAll<HTMLButtonElement>("[data-send-for]")) {
      send.addEventListener("click", () => this.sendFromComposer(send));
      const prompt = document.querySelector<HTMLTextAreaElement>(`#${CSS.escape(send.dataset.sendFor || "")}`);
      prompt?.addEventListener("keydown", (event) => {
        if (shouldSubmitComposerKey(this.composerSubmitShortcut, event)) {
          event.preventDefault();
          if (!send.disabled) send.click();
        }
      });
      if (prompt?.id === "work-prompt") {
        prompt.addEventListener("input", () => {
          this.renderWorkControl(this.workControl.snapshot());
          this.scheduleWorkflowDraftPersist("work", prompt);
        });
      } else if (prompt?.id === "chat-prompt") {
        prompt.addEventListener("input", () => this.renderChatControl(this.chatControl.snapshot()));
      }
    }
    const videoPrompt = document.querySelector<HTMLTextAreaElement>("#video-prompt");
    videoPrompt?.addEventListener("input", () => this.scheduleWorkflowDraftPersist("video", videoPrompt));

    window.addEventListener("bianhua:remote-action", (event) => this.onRemoteAction((event as CustomEvent).detail));
    document.addEventListener("bianhua:handoff-create", (event) => {
      void this.createWorkflowHandoff((event as CustomEvent<HandoffCreateDetail>).detail);
    });
    document.addEventListener("bianhua:handoff-remove", (event) => {
      const handoffId = workflowIdentifier((event as CustomEvent).detail?.handoffId);
      if (!handoffId || !this.workflowHandoffs) return;
      if (this.submittedVideoHandoffIds.has(handoffId)) {
        window.dispatchEvent(new CustomEvent("bianhua:handoff-error", {
          detail: { targetSurface: "video", message: "视频付费创建正在确认，暂不能移除这条交接" },
        }));
        return;
      }
      try {
        const dismissed = this.workflowHandoffs.dismiss(handoffId);
        this.pendingRecoveredHandoffIds.delete(handoffId);
        if (!this.pendingRecoveredHandoffIds.size) this.clearRecoveredHandoffRetry();
        window.dispatchEvent(new CustomEvent("bianhua:handoff-dismissed", {
          detail: { handoff: dismissed },
        }));
      } catch (error) {
        window.dispatchEvent(new CustomEvent("bianhua:handoff-error", {
          detail: {
            targetSurface: (event as CustomEvent).detail?.targetSurface,
            message: error instanceof Error ? error.message : "交接移除未能持久保存",
          },
        }));
      }
    });
    window.addEventListener("bianhua:video-handoff-submitted", (event) => {
      const handoffId = workflowIdentifier((event as CustomEvent).detail?.handoffId);
      const principalId = workflowIdentifier((event as CustomEvent).detail?.principalId);
      if (!handoffId) return;
      if (principalId) {
        this.submittedVideoHandoffPrincipals.set(handoffId, principalId);
        if (principalId === this.serverPrincipalId) this.submittedVideoHandoffIds.add(handoffId);
      } else {
        this.submittedVideoHandoffIds.add(handoffId);
        if (this.serverPrincipalId) this.submittedVideoHandoffPrincipals.set(handoffId, this.serverPrincipalId);
      }
    });
    window.addEventListener("bianhua:video-handoff-released", (event) => {
      const handoffId = workflowIdentifier((event as CustomEvent).detail?.handoffId);
      if (handoffId) {
        this.submittedVideoHandoffIds.delete(handoffId);
        this.submittedVideoHandoffPrincipals.delete(handoffId);
      }
    });
    window.addEventListener("bianhua:video-handoff-accepted", (event) => {
      const handoffId = workflowIdentifier((event as CustomEvent).detail?.handoffId);
      const taskId = workflowIdentifier((event as CustomEvent).detail?.taskId);
      const principalId = workflowIdentifier((event as CustomEvent).detail?.principalId) || this.serverPrincipalId;
      if (!handoffId || !taskId || !principalId) return;
      this.acceptedVideoHandoffs.set(handoffId, { principalId, taskId });
      this.reconcileAcceptedVideoHandoffs(principalId);
    });
    this.bindAttachments();
    this.bindComposerAutoResize();
    this.bindStaticActions();
    this.bindKeyboardShortcuts();
  }

  private bindComposerAutoResize(): void {
    for (const textarea of document.querySelectorAll<HTMLTextAreaElement>("#chat-prompt, #work-prompt, #video-prompt")) {
      const resize = () => resizeComposerTextarea(textarea);
      textarea.addEventListener("input", resize);
      textarea.addEventListener("change", resize);
      requestAnimationFrame(resize);
    }
    window.addEventListener("bianhua:composer-value-changed", (event) => {
      const target = event instanceof CustomEvent && typeof event.detail?.target === "string"
        ? document.querySelector<HTMLTextAreaElement>(event.detail.target)
        : null;
      if (target) resizeComposerTextarea(target);
      else for (const textarea of document.querySelectorAll<HTMLTextAreaElement>("#chat-prompt, #work-prompt, #video-prompt")) resizeComposerTextarea(textarea);
    });
  }

  private prepareRuntimeShell(): void {
    if (this.visualDemo) return;
    const remoteMediaConfigured = Boolean(window.BianhuaRemoteConfig?.transport || window.BianhuaRemoteConfig?.url);
    for (const canvas of document.querySelectorAll<HTMLElement>(".conversation-canvas")) {
      const isChatCanvas = Boolean(canvas.closest(".chat-panel"));
      const mediaMessage = isChatCanvas && remoteMediaConfigured
        ? canvas.querySelector<HTMLElement>("[data-media-transfer]")?.closest<HTMLElement>(".assistant-message")
        : null;
      for (const message of canvas.querySelectorAll<HTMLElement>(".message")) {
        if (message !== mediaMessage) message.remove();
      }
      for (const empty of canvas.querySelectorAll<HTMLElement>(".runtime-empty-state")) empty.remove();
      if (mediaMessage) {
        mediaMessage.hidden = true;
        const intro = mediaMessage.querySelector<HTMLElement>(".assistant-body > p:first-child");
        if (intro) intro.textContent = "正在通过服务器订阅会话生成图像。";
        const preview = mediaMessage.querySelector<HTMLElement>("[data-media-preview]");
        if (preview) preview.hidden = true;
        mediaMessage.querySelector<HTMLImageElement>("[data-media-image]")?.removeAttribute("src");
      }
      const empty = document.createElement("p");
      empty.className = "runtime-empty-state";
      empty.textContent = canvas.closest(".work-panel")
        ? "连接工作服务器后，可在这里继续远程任务。"
        : "连接聊天服务后，可在这里开始新对话。";
      canvas.append(empty);
    }
    for (const history of document.querySelectorAll<HTMLElement>(".sidebar-mode .history")) {
      const mode = history.closest("[data-sidebar-mode]")?.getAttribute("data-sidebar-mode");
      if (mode === "video") {
        history.replaceChildren();
        const heading = document.createElement("div");
        heading.className = "history-heading";
        const label = document.createElement("span");
        label.textContent = "视频任务";
        heading.append(label);
        const copy = document.createElement("p");
        copy.className = "runtime-history-empty";
        copy.textContent = "连接视频服务后同步任务";
        history.append(heading, copy);
        continue;
      }
      const surface = mode === "work" ? "work" : "chat";
      history.replaceChildren();
      const heading = document.createElement("div");
      heading.className = "history-heading";
      const label = document.createElement("span");
      label.textContent = surface === "work" ? "服务器任务" : "服务器聊天";
      heading.append(label);
      const copy = document.createElement("p");
      copy.className = "runtime-history-empty";
      copy.textContent = "连接后同步历史记录";
      history.append(heading, copy);
    }
    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-remote-action]")) {
      button.disabled = true;
      button.dataset.capabilityState = "unavailable";
      button.title = "等待服务器能力清单";
    }
    if (remoteMediaConfigured) {
      for (const button of document.querySelectorAll<HTMLButtonElement>("[data-surface='chat'][data-remote-action='image_generate']")) {
        button.disabled = false;
        button.dataset.capabilityState = "available";
        button.title = "订阅图像测试桥已配置";
      }
    }
    const changeCopy = document.querySelector<HTMLElement>(".change-button span");
    if (changeCopy) changeCopy.textContent = "暂无变更";
    const stop = document.querySelector<HTMLButtonElement>("[data-work-stop]");
    if (stop) stop.disabled = true;
    const modelCopy = document.querySelector<HTMLElement>(".model-button > span:nth-child(2)");
    if (modelCopy) modelCopy.textContent = "聊天能力 · 未连接";
    this.installWorkGuide();
  }

  private installWorkGuide(): void {
    const composer = document.querySelector<HTMLElement>(".work-composer");
    if (!composer || document.querySelector("#runtime-work-guide")) return;
    const guide = document.createElement("section");
    guide.id = "runtime-work-guide";
    guide.className = "runtime-work-guide";
    guide.setAttribute("aria-live", "polite");
    guide.hidden = true;

    const marker = document.createElement("span");
    marker.className = "runtime-work-marker";
    marker.setAttribute("aria-hidden", "true");
    const copy = document.createElement("span");
    copy.className = "runtime-work-copy";
    const title = document.createElement("strong");
    title.dataset.workStageLabel = "";
    const description = document.createElement("small");
    description.id = "runtime-work-guide-description";
    description.dataset.workGuidance = "";
    const detail = document.createElement("small");
    detail.className = "runtime-work-detail";
    detail.dataset.workDetail = "";
    copy.append(title, description, detail);
    const intent = document.createElement("span");
    intent.className = "runtime-work-intent";
    intent.dataset.workIntent = "";
    const plan = document.createElement("ol");
    plan.className = "runtime-work-plan";
    plan.dataset.workPlan = "";
    plan.hidden = true;
    const recoveryActions = document.createElement("span");
    recoveryActions.className = "runtime-work-recovery-actions";
    recoveryActions.dataset.workRecoveryActions = "";
    recoveryActions.hidden = true;
    const check = document.createElement("button");
    check.type = "button";
    check.className = "runtime-work-recovery-check";
    check.textContent = "再次核对";
    check.addEventListener("click", () => void this.retryUncertainWorkRecovery());
    const unlock = document.createElement("button");
    unlock.type = "button";
    unlock.className = "runtime-work-recovery-unlock";
    unlock.textContent = "解除锁定";
    unlock.addEventListener("click", () => this.confirmAbandonUncertainWorkRecovery());
    recoveryActions.append(check, unlock);
    const approvalActions = document.createElement("span");
    approvalActions.className = "runtime-work-approval-actions";
    approvalActions.dataset.workApprovalActions = "";
    approvalActions.hidden = true;
    const reviewApproval = document.createElement("button");
    reviewApproval.type = "button";
    reviewApproval.className = "runtime-work-approval-review";
    reviewApproval.textContent = "查看并确认";
    reviewApproval.addEventListener("click", () => this.focusActiveElicitation());
    approvalActions.append(reviewApproval);
    guide.append(marker, copy, intent, recoveryActions, approvalActions, plan);
    composer.before(guide);

    const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
    prompt?.setAttribute("aria-describedby", description.id);
  }

  private renderWorkControl(snapshot: WorkControlSnapshot): void {
    if (this.visualDemo) return;
    this.installWorkGuide();
    const guide = document.querySelector<HTMLElement>("#runtime-work-guide");
    const composer = document.querySelector<HTMLElement>(".work-composer");
    const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
    const send = document.querySelector<HTMLButtonElement>("[data-send-for='work-prompt']");
    const stop = document.querySelector<HTMLButtonElement>("[data-work-stop]");
    if (!composer || !prompt || !send || !stop) return;

    if (guide) {
      guide.hidden = !snapshot.threadId && snapshot.stage === "idle";
      guide.dataset.stage = snapshot.stage;
      guide.dataset.active = String(snapshot.isActive);
      const stageLabel = guide.querySelector<HTMLElement>("[data-work-stage-label]");
      const guidance = guide.querySelector<HTMLElement>("[data-work-guidance]");
      const detail = guide.querySelector<HTMLElement>("[data-work-detail]");
      const intent = guide.querySelector<HTMLElement>("[data-work-intent]");
      const recoveryActions = guide.querySelector<HTMLElement>("[data-work-recovery-actions]");
      const approvalActions = guide.querySelector<HTMLElement>("[data-work-approval-actions]");
      const plan = guide.querySelector<HTMLOListElement>("[data-work-plan]");
      if (stageLabel) stageLabel.textContent = snapshot.stageLabel;
      if (guidance) guidance.textContent = snapshot.guidance;
      if (detail) {
        const activity = relativeServerActivity(snapshot.lastActivityAt);
        const activityCopy = activity ? `服务器状态最近确认：${activity}` : "";
        detail.textContent = [snapshot.detail, activityCopy].filter(Boolean).join(" · ");
        detail.hidden = !detail.textContent;
      }
      if (intent) intent.textContent = snapshot.composerIntent === "steer"
        ? this.workFollowUpBehavior === "wait-for-next" ? "排队下一轮" : "补充引导"
        : "新任务";
      if (recoveryActions) {
        const recovering = Boolean(
          this.uncertainWorkSubmission
          && snapshot.threadId
          && this.uncertainWorkSubmission.threadId === snapshot.threadId,
        );
        recoveryActions.hidden = !recovering;
        if (intent) intent.hidden = recovering;
        const check = recoveryActions.querySelector<HTMLButtonElement>(".runtime-work-recovery-check");
        if (check) check.disabled = !recovering || this.threadLoading.has("work") || this.client.state !== "ready";
      }
      if (approvalActions) {
        const requestId = this.activeElicitationRequestId;
        const requestThread = requestId ? this.elicitationThreads.get(requestId)?.threadId : "";
        const available = Boolean(snapshot.awaitingApproval && requestId && requestThread === snapshot.threadId);
        const waiting = snapshot.stage === "waiting";
        approvalActions.hidden = !waiting;
        const review = approvalActions.querySelector<HTMLButtonElement>(".runtime-work-approval-review");
        if (review) {
          review.disabled = !available;
          review.textContent = available ? "查看并确认" : "正在同步确认…";
          review.setAttribute("aria-label", "查看服务器等待确认的操作");
        }
        if (intent && waiting) intent.hidden = true;
      }
      if (plan) {
        plan.replaceChildren();
        for (const step of snapshot.plan) {
          const item = document.createElement("li");
          item.dataset.status = step.status;
          const state = document.createElement("span");
          state.className = "runtime-work-plan-state";
          state.setAttribute("aria-hidden", "true");
          const text = document.createElement("span");
          text.textContent = step.step;
          const status = document.createElement("small");
          status.textContent = step.status === "completed" ? "已完成" : step.status === "inProgress" ? "进行中" : "待处理";
          item.append(state, text, status);
          plan.append(item);
        }
        plan.hidden = snapshot.plan.length === 0;
      }
    }

    composer.dataset.workIntent = snapshot.composerIntent;
    composer.classList.toggle("runtime-work-active", snapshot.isActive);
    const queueNext = snapshot.isActive && this.workFollowUpBehavior === "wait-for-next";
    const submitHint = this.composerSubmitShortcut === "modifier-enter"
      ? "Enter 换行，Ctrl/⌘ + Enter 发送"
      : "Shift + Enter 换行";
    prompt.placeholder = queueNext ? "输入将在当前任务完成后开始的下一轮…" : snapshot.composerPlaceholder;
    prompt.title = queueNext
      ? `当前任务完成后按顺序发送；${submitHint}`
      : snapshot.isActive ? `${snapshot.composerHint} ${submitHint}` : submitHint;
    prompt.setAttribute("aria-label", queueNext ? "输入下一轮工作指令" : snapshot.isActive ? "补充引导当前任务" : "输入工作指令");
    const stagedHandoffId = workflowIdentifier(document.querySelector<HTMLElement>("[data-handoff-stage='work']")?.dataset.handoffId);
    const handoffRecoveryPending = Boolean(stagedHandoffId && this.pendingRecoveredHandoffIds.has(stagedHandoffId));
    const submissionPending = snapshot.submissionPending || this.workRequestInFlight;
    const requiredOperation = snapshot.composerIntent === "steer"
      ? queueNext ? "gateway/idempotentTurnStart" : "turn/steer"
      : "turn/start";
    const workAttachments = this.attachments.get("work") || [];
    const workAttachmentDrafts = [...(this.attachmentDrafts.get("work")?.values() || [])];
    const attachmentPending = workAttachmentDrafts.length > 0;
    const sendDisabled = (!prompt.value.trim() && workAttachments.length === 0)
      || attachmentPending
      || submissionPending
      || snapshot.interruptPending
      || handoffRecoveryPending
      || this.threadLoading.has("work")
      || this.client.state !== "ready"
      || !this.modeIsUsable("work")
      || !this.operationIsAvailable(requiredOperation)
      || (queueNext && !this.operationIsAvailable("turn/start"));
    send.disabled = sendDisabled;
    const starting = submissionPending && !snapshot.isActive;
    send.hidden = snapshot.isActive || starting;
    send.dataset.pending = String(submissionPending || handoffRecoveryPending);
    send.setAttribute("aria-label", this.threadLoading.has("work")
      ? "正在恢复所选任务"
      : handoffRecoveryPending
        ? "正在恢复交接视频文件"
        : attachmentPending
          ? "正在准备附件"
          : submissionPending
            ? "正在发送"
            : queueNext ? "加入下一轮" : snapshot.sendLabel);
    send.title = queueNext ? "当前任务完成后按顺序发送" : snapshot.composerHint;
    if (submissionPending) send.setAttribute("aria-busy", "true");
    else send.removeAttribute("aria-busy");

    stop.hidden = !(snapshot.isActive || starting);
    stop.disabled = starting || !snapshot.canStop || !this.operationIsAvailable("turn/interrupt");
    stop.dataset.pending = String(starting || snapshot.interruptPending);
    stop.setAttribute("aria-label", starting ? "正在启动任务" : snapshot.interruptPending ? "正在停止当前任务" : "停止当前任务");
    const stopCopy = stop.querySelector<HTMLElement>("span");
    if (stopCopy) stopCopy.textContent = snapshot.stopLabel;
    if (starting || snapshot.interruptPending) stop.setAttribute("aria-busy", "true");
    else stop.removeAttribute("aria-busy");
  }

  private renderChatControl(snapshot: ChatControlSnapshot): void {
    if (this.visualDemo) return;
    const prompt = document.querySelector<HTMLTextAreaElement>("#chat-prompt");
    const send = document.querySelector<HTMLButtonElement>("[data-send-for='chat-prompt']");
    const stop = document.querySelector<HTMLButtonElement>("[data-chat-stop]");
    if (!prompt || !send || !stop) return;

    const active = Boolean(snapshot.turnId);
    const subscriptionImageBridge = Boolean(
      this.selectedTools.get("chat")?.has("image_generate")
      && window.BianhuaMediaBridge
      && (window.BianhuaRemoteConfig?.transport || window.BianhuaRemoteConfig?.url),
    );
    const canCreate = subscriptionImageBridge || (this.client.state === "ready"
      && this.modeIsUsable("chat")
      && this.operationIsAvailable("chat/message/create")
      && this.operationIsAvailable("gateway/idempotentChatCreate"));
    send.hidden = active;
    const chatAttachments = this.attachments.get("chat") || [];
    const chatAttachmentDrafts = [...(this.attachmentDrafts.get("chat")?.values() || [])];
    send.disabled = (!prompt.value.trim() && chatAttachments.length === 0)
      || chatAttachmentDrafts.length > 0
      || snapshot.submitting
      || !canCreate;
    send.setAttribute("aria-label", snapshot.submitting ? "正在发送聊天消息" : "发送聊天消息");
    if (snapshot.submitting) send.setAttribute("aria-busy", "true");
    else send.removeAttribute("aria-busy");

    stop.hidden = !active;
    stop.disabled = !active
      || snapshot.interruptPending
      || this.client.state !== "ready"
      || !this.operationIsAvailable("chat/turn/interrupt");
    stop.setAttribute("aria-label", snapshot.interruptPending ? "正在停止生成" : "停止生成");
    if (snapshot.interruptPending) stop.setAttribute("aria-busy", "true");
    else stop.removeAttribute("aria-busy");

    prompt.setAttribute("aria-label", active ? "输入后续内容；当前生成结束后可发送" : "输入聊天消息");
    prompt.title = this.composerSubmitShortcut === "modifier-enter"
      ? "Enter 换行，Ctrl/⌘ + Enter 发送"
      : "Shift + Enter 换行";
  }

  private async sendFromComposer(
    button: HTMLButtonElement,
    options: { handoffId?: string } = {},
  ): Promise<boolean> {
    const prompt = document.querySelector<HTMLTextAreaElement>(`#${CSS.escape(button.dataset.sendFor || "")}`);
    if (!prompt) return false;
    const text = prompt.value.trim();
    const surface: Surface = prompt.id.startsWith("work") ? "work" : "chat";
    const currentAttachments = [...(this.attachments.get(surface) || [])];
    const drafts = [...(this.attachmentDrafts.get(surface)?.values() || [])];
    if (drafts.some((draft) => draft.status === "uploading")) {
      toast("附件仍在上传，请等待上传完成后再发送");
      return false;
    }
    if (drafts.some((draft) => draft.status === "failed")) {
      toast("有附件上传失败，请重试或移除后再发送", "error");
      return false;
    }
    if (!text && !currentAttachments.length) return false;
    const editedChatMessageId = surface === "chat" ? workflowIdentifier(prompt.dataset.editMessageId) : undefined;
    const chatSubmitOperation = editedChatMessageId ? "chat/message/edit" : "chat/message/create";
    const stagedHandoffId = surface === "work"
      ? workflowIdentifier(document.querySelector<HTMLElement>("[data-handoff-stage='work']")?.dataset.handoffId)
      : undefined;
    const workflowHandoffId = workflowIdentifier(options.handoffId) || stagedHandoffId;
    if (surface === "work" && workflowHandoffId && this.pendingRecoveredHandoffIds.has(workflowHandoffId)) {
      toast("正在从服务器恢复交接视频，请等待文件校验完成", "error");
      return false;
    }
    if (text && surface === "chat" && this.selectedTools.get("chat")?.has("image_generate") && window.BianhuaMediaBridge) {
      const article = renderUserMessage("chat", text);
      this.decorateMessage(article, "chat", "user", undefined, text);
      return true;
    }
    if (this.client.state !== "ready") {
      toast("服务器尚未连接，内容已保留在输入框中", "error");
      return false;
    }
    if (!this.capabilitiesReady) {
      toast("正在核对服务器能力，内容已保留在输入框中", "error");
      return false;
    }
    const modeStatus = this.modeStatuses.get(surface);
    if (modeStatus === "unavailable" || modeStatus === "unsupported" || modeStatus === "auth_required") {
      toast(this.modeReasons.get(surface) || (modeStatus === "auth_required" ? "请先登录服务器账户" : "当前服务器未提供此模式"), "error");
      return false;
    }
    if (surface === "chat" && !this.operationIsAvailable(chatSubmitOperation)) {
      toast(editedChatMessageId ? "当前服务器没有声明消息编辑能力，内容已保留" : "当前服务器没有声明聊天发送能力，内容已保留", "error");
      return false;
    }
    if (surface === "chat" && !this.operationIsAvailable("gateway/idempotentChatCreate")) {
      toast("聊天服务未声明断线幂等恢复能力；为避免重复发送，内容已保留", "error");
      return false;
    }
    if (surface === "work" && this.workRequestInFlight) {
      toast("上一条工作指令仍在发送，请稍后重试");
      return false;
    }
    if (this.threadLoading.has(surface)) {
      if (surface !== "work") toast("正在恢复所选会话，请稍后再发送");
      return false;
    }
    if (surface === "work" && !this.serverPrincipalId) {
      toast("服务器未提供匿名账户恢复标识；为避免重复执行，工作指令没有发送", "error");
      return false;
    }
    if (surface === "chat" && !this.serverPrincipalId) {
      toast("服务器未提供账户恢复标识；为避免结果未知时重复发送，聊天消息没有发送", "error");
      return false;
    }
    if (surface === "work" && this.serverPrincipalId) {
      const persisted = this.readWorkSubmission(this.serverPrincipalId);
      if (!persisted && this.uncertainWorkSubmission) this.dropWorkSubmissionMemory();
      else if (persisted && persisted.clientMessageId !== this.uncertainWorkSubmission?.clientMessageId) {
        this.adoptWorkSubmission(persisted);
      }
    }
    if (surface === "work" && this.uncertainWorkSubmission) {
      toast("上一条工作指令的结果尚未确认，正在恢复；请勿重复发送", "error");
      return false;
    }
    const submissionPrincipalId = this.serverPrincipalId!;
    if (surface === "work") {
      const threadId = this.activeThread.get("work");
      const activeTurnId = threadId ? this.workControl.activeTurnId(threadId) : null;
      const queueNext = Boolean(activeTurnId && this.workFollowUpBehavior === "wait-for-next");
      const required = activeTurnId && !queueNext ? "turn/steer" : "turn/start";
      if (!this.operationIsAvailable(required) || (!threadId && !this.operationIsAvailable("thread/start"))) {
        toast("当前服务器没有声明所需的工作任务操作，内容已保留", "error");
        return false;
      }
      if (queueNext) {
        if (!this.operationIsAvailable("gateway/idempotentTurnStart")) {
          toast("服务器未声明可恢复的下一轮队列能力；为避免重复执行，指令没有排队", "error");
          return false;
        }
        return this.enqueueWorkFollowUp(
          threadId!,
          activeTurnId!,
          text,
          prompt,
          currentAttachments,
          workflowHandoffId,
        );
      }
    }
    if (surface === "chat" && !this.chatControl.beginSubmission()) {
      toast("当前聊天请求仍在进行；可以停止后再发送", "error");
      return false;
    }
    if (surface === "work") {
      this.workRequestInFlight = true;
      this.renderWorkControl(this.workControl.snapshot());
    }

    const predictedIntent = surface === "work" && this.activeThread.get("work")
      && this.workControl.activeTurnId(this.activeThread.get("work")!)
      ? "steer"
      : "start";
    const retryMessage = editedChatMessageId ? null : retryableOptimisticMessage(surface, text, predictedIntent);
    const editClientMessageId = editedChatMessageId
      ? workflowIdentifier(prompt.dataset.editClientMessageId)
      : undefined;
    // A retried Work command is a new durable mutation and therefore needs a
    // new client message identity as well as a new RPC identity. Reusing the
    // failed optimistic row's id lets a late response from the previous
    // attempt settle or erase the new journal in another window.
    const retryClientMessageId = surface === "chat" ? retryMessage?.dataset.clientMessageId : undefined;
    const clientMessageId = editClientMessageId || retryClientMessageId || crypto.randomUUID();
    const optimisticMessage = retryMessage || renderUserMessage(surface, text, { clientMessageId, intent: predictedIntent });
    if (surface === "work" && retryMessage) optimisticMessage.dataset.clientMessageId = clientMessageId;
    if (editedChatMessageId) {
      optimisticMessage.dataset.editedMessageId = editedChatMessageId;
      prompt.dataset.editClientMessageId = clientMessageId;
    }
    this.decorateMessage(optimisticMessage, surface, "user", undefined, text);
    renderMessageAttachments(optimisticMessage, currentAttachments);
    if (retryMessage) markOptimisticRetry(retryMessage, predictedIntent);
    prompt.value = "";
    prompt.dispatchEvent(new Event("input", { bubbles: true }));
    const attachments = currentAttachments;
    const tools = Array.from(this.selectedTools.get(surface) || []);
    const activeChatThreadId = this.activeThread.get("chat");
    const chatJournal: ChatSubmissionJournal | null = surface === "chat"
      ? {
        principalId: this.serverPrincipalId!,
        clientMessageId,
        operation: chatSubmitOperation,
        text,
        attachments: attachments.map(persistedAttachment),
        tools,
        createdAt: new Date().toISOString(),
        ...(activeChatThreadId ? { threadId: activeChatThreadId } : {}),
        ...(editedChatMessageId ? { messageId: editedChatMessageId } : {}),
      }
      : null;
    let workThreadId: string | undefined;
    let workSubmissionBegan = false;
    let accepted = false;
    try {
      if (surface === "chat") {
        if (!chatJournal || !this.persistChatSubmission(chatJournal)) {
          throw new Error("无法写入聊天请求恢复日志；为避免重复发送，消息没有提交");
        }
        const result = await this.client.rpc<Record<string, unknown>>(chatSubmitOperation, this.chatSubmissionRequest(chatJournal));
        if (this.serverPrincipalId !== submissionPrincipalId) return false;
        this.applyChatSubmissionResult(chatJournal, result);
        accepted = true;
      } else {
        workThreadId = this.activeThread.get("work");
        const expectedTurnId = workThreadId ? this.workControl.activeTurnId(workThreadId) || undefined : undefined;
        const input: WorkInput[] = [
          ...(text ? [{ type: "text", text } as const] : []),
          ...attachments.map((attachment) => attachment.codexInput),
        ];
        let journal: WorkSubmissionJournal = {
          version: 2,
          principalId: this.serverPrincipalId!,
          ...(workThreadId ? { threadId: workThreadId } : {}),
          clientMessageId,
          text,
          input,
          intent: expectedTurnId ? "steer" : "start",
          createdAt: new Date().toISOString(),
          rpcRequestId: `work-rpc-${crypto.randomUUID()}`,
          ...(!workThreadId ? { threadStartRequestId: `work-thread-${crypto.randomUUID()}` } : {}),
          ...(expectedTurnId ? { expectedTurnId } : {}),
          ownerWindowId: this.windowInstanceId,
          ownerLeaseUntil: this.workSubmissionLeaseUntil(),
          ...(this.selectedWorkModel ? {
            model: this.selectedWorkModel.model,
            ...(this.selectedWorkModel.effort ? { effort: this.selectedWorkModel.effort } : {}),
          } : {}),
          executionPolicy: this.selectedWorkExecutionPolicy,
          ...(workflowHandoffId ? { handoffId: workflowHandoffId } : {}),
        };
        if (!await this.claimWorkSubmission(journal)) {
          throw new Error("另一个窗口正在发送或恢复上一条工作指令；本次内容没有提交");
        }
        if (!workThreadId) {
          const started = await this.client.rpc<Record<string, unknown>>("thread/start", {
            cwd: this.config.workspacePath,
            ...(journal.model ? { model: journal.model } : {}),
            ...threadExecutionPolicyParams(journal.executionPolicy),
          }, { requestId: journal.threadStartRequestId! });
          if (this.serverPrincipalId !== submissionPrincipalId) return false;
          workThreadId = resultId(started, "thread");
          if (!workThreadId) throw new Error("服务器没有返回工作线程标识");
          const startedThreadId = workThreadId;
          const updatedJournal = await this.updateOwnedWorkSubmission(journal, (latest) => ({
            ...latest,
            threadId: startedThreadId,
            ownerLeaseUntil: this.workSubmissionLeaseUntil(),
          }));
          if (!updatedJournal) return false;
          journal = updatedJournal;
          const startedThread = started.thread && typeof started.thread === "object"
            ? started.thread as Record<string, unknown>
            : { id: startedThreadId, name: text, preview: "服务器正在同步" };
          this.recentlyStartedWorkThreads.set(startedThreadId, {
            item: { ...startedThread, id: startedThreadId },
            expiresAt: Date.now() + 10_000,
          });
          this.setActiveThread("work", startedThreadId);
          this.scheduleThreadListRefresh("work", 0);
        }
        if (!this.workControl.beginSubmission(workThreadId)) {
          throw new Error("当前请求仍在发送或任务正在停止，请稍后重试");
        }
        workSubmissionBegan = true;
        const request = this.workSubmissionRequest(journal);
        if (!request) throw new Error("工作指令恢复日志缺少任务标识");
        optimisticMessage.dataset.submissionIntent = request.intent;
        const delivery = optimisticMessage.querySelector<HTMLElement>(".runtime-delivery-state");
        if (delivery) delivery.textContent = request.intent === "steer" ? "正在发送补充引导" : "正在发送";
        const result = await this.client.rpc<Record<string, unknown>>(
          request.method,
          request.params,
          { requestId: journal.rpcRequestId! },
        );
        if (this.serverPrincipalId !== submissionPrincipalId) return false;
        const returnedTurnId = resultId(result, "turn");
        if (!returnedTurnId) throw new Error("服务器没有返回任务标识");
        if (request.intent === "steer" && returnedTurnId !== request.params.expectedTurnId) {
          throw new Error("服务器返回的任务标识与当前任务不一致，请重新同步会话");
        }
        updateOptimisticMessage(optimisticMessage, "rpcAccepted");
        this.workControl.submissionAccepted(workThreadId, returnedTurnId, request.intent);
        this.scheduleWorkTerminalReconcile(workThreadId, 1_200);
        const handoffCompleted = !workflowHandoffId || this.completeWorkflowHandoff(workflowHandoffId);
        if (handoffCompleted) this.clearWorkSubmission(clientMessageId, "confirmed");
        accepted = true;
      }
      this.attachments.set(surface, []);
      this.updateAttachmentChips(surface);
    } catch (error) {
      if (this.serverPrincipalId !== submissionPrincipalId) return false;
      const message = error instanceof Error ? error.message : "服务器请求失败";
      const serverConfirmed = optimisticMessage.dataset.optimistic === "confirmed";
      const outcomeUnknown = requestOutcomeMayExist(error);
      if (!serverConfirmed && outcomeUnknown) {
        const delivery = optimisticMessage.querySelector<HTMLElement>(".runtime-delivery-state");
        if (delivery) delivery.textContent = surface === "work" ? "结果待确认 · 正在恢复同一请求" : "结果待确认 · 正在用同一请求恢复";
        optimisticMessage.setAttribute("aria-busy", "true");
      } else if (editedChatMessageId && !serverConfirmed) {
        optimisticMessage.remove();
      } else if (!serverConfirmed) {
        updateOptimisticMessage(optimisticMessage, "rpcFailed");
      }
      if (serverConfirmed) {
        if (surface === "chat") {
          if (chatJournal) {
            this.clearRestoredChatComposer(chatJournal);
            this.clearChatSubmission(chatJournal.clientMessageId);
          }
        }
        const journal = surface === "work" && this.uncertainWorkSubmission?.clientMessageId === clientMessageId
          ? this.uncertainWorkSubmission
          : null;
        if (journal) {
          this.confirmUncertainWorkSubmission(journal);
        } else {
          if (surface === "work") this.clearWorkSubmission(clientMessageId, "confirmed");
          this.attachments.set(surface, []);
          this.updateAttachmentChips(surface);
          if (surface === "work" && workThreadId && workSubmissionBegan) {
            this.workControl.submissionConfirmed(workThreadId);
          }
        }
      } else if (surface === "chat" && outcomeUnknown) {
        restoreComposerText(prompt, text);
        this.chatControl.submissionUncertain(message);
        toast("聊天请求结果尚未确认，正在用同一请求标识恢复；请勿重复发送", "error");
        this.scheduleUncertainChatResume(1_200);
      } else if (surface === "work" && outcomeUnknown && this.uncertainWorkSubmission?.clientMessageId === clientMessageId) {
        let journal = this.uncertainWorkSubmission;
        if (authoritativeMutationOutcomeUnknown(error)) {
          const updated = await this.updateOwnedWorkSubmission(journal, (latest) => ({
            ...latest,
            authoritativeOutcomeUnknown: true,
          }));
          if (!updated) return false;
          journal = updated;
        }
        restoreComposerText(prompt, text);
        const delivery = optimisticMessage.querySelector<HTMLElement>(".runtime-delivery-state");
        if (delivery) delivery.textContent = "结果待确认 · 正在恢复";
        if (journal.authoritativeOutcomeUnknown && !journal.threadId) {
          this.clearWorkSubmission(journal.clientMessageId, "failed");
          toast("服务器重启时新任务标识尚未确认，已自动解除锁定；原内容已保留", "error");
          this.scheduleThreadListRefresh("work", 0);
        } else {
          if (journal.threadId) this.workControl.beginResync(journal.threadId);
          toast("请求结果尚未确认，正在从服务器恢复同一任务；请勿重复发送", "error");
          this.scheduleUncertainWorkResume(1_200);
        }
      } else {
        if (surface === "chat") this.clearChatSubmission(clientMessageId);
        if (surface === "work") this.clearWorkSubmission(clientMessageId, "failed");
        if (surface === "chat" && editedChatMessageId) {
          delete prompt.dataset.editClientMessageId;
        }
        restoreComposerText(prompt, text);
        if (surface === "work" && workThreadId && workSubmissionBegan) this.workControl.submissionFailed(workThreadId, message);
        toast(message, "error");
      }
      if (surface === "chat" && !serverConfirmed && !outcomeUnknown) this.chatControl.submissionFailed(message);
    } finally {
      if (surface === "work" && this.serverPrincipalId === submissionPrincipalId) {
        this.workRequestInFlight = false;
        this.renderWorkControl(this.workControl.snapshot());
      }
    }
    return accepted;
  }

  private workFollowUpPreferenceKey(principalId: string): string {
    return `bianhua-work-follow-up-behavior:v1:${encodeURIComponent(principalId)}:${encodeURIComponent(this.config.projectId)}`;
  }

  private composerShortcutPreferenceKey(principalId: string): string {
    return `bianhua-composer-submit-shortcut:v1:${encodeURIComponent(principalId)}:${encodeURIComponent(this.config.projectId)}`;
  }

  private readComposerSubmitShortcut(principalId: string | null): ComposerSubmitShortcut {
    if (!principalId) return "enter";
    try {
      return localStorage.getItem(this.composerShortcutPreferenceKey(principalId)) === "modifier-enter"
        ? "modifier-enter"
        : "enter";
    } catch {
      return "enter";
    }
  }

  private setComposerSubmitShortcut(value: ComposerSubmitShortcut): boolean {
    if (!this.serverPrincipalId) return false;
    try {
      const key = this.composerShortcutPreferenceKey(this.serverPrincipalId);
      localStorage.setItem(key, value);
      if (localStorage.getItem(key) !== value) return false;
    } catch {
      return false;
    }
    this.composerSubmitShortcut = value;
    this.renderChatControl(this.chatControl.snapshot());
    this.renderWorkControl(this.workControl.snapshot());
    return true;
  }

  private readWorkFollowUpBehavior(principalId: string | null): WorkFollowUpBehavior {
    if (!principalId) return DEFAULT_WORK_FOLLOW_UP_BEHAVIOR;
    try {
      return localStorage.getItem(this.workFollowUpPreferenceKey(principalId)) === "wait-for-next"
        ? "wait-for-next"
        : DEFAULT_WORK_FOLLOW_UP_BEHAVIOR;
    } catch {
      return DEFAULT_WORK_FOLLOW_UP_BEHAVIOR;
    }
  }

  private setWorkFollowUpBehavior(behavior: WorkFollowUpBehavior): boolean {
    if (!this.serverPrincipalId) return false;
    if (behavior === "wait-for-next"
      && (!this.operationIsAvailable("gateway/idempotentTurnStart")
        || !this.operationIsAvailable("turn/start"))) return false;
    try {
      const key = this.workFollowUpPreferenceKey(this.serverPrincipalId);
      localStorage.setItem(key, behavior);
      if (localStorage.getItem(key) !== behavior) return false;
    } catch {
      return false;
    }
    this.workFollowUpBehavior = behavior;
    this.renderWorkControl(this.workControl.snapshot());
    return true;
  }

  private workFollowUpQueue(threadId: string): WorkFollowUpQueue<QueuedWorkFollowUpPayload> {
    if (!this.serverPrincipalId) throw new Error("服务器账户标识缺失，无法恢复下一轮队列");
    let queue = this.workFollowUpQueues.get(threadId);
    if (!queue) {
      queue = new WorkFollowUpQueue<QueuedWorkFollowUpPayload>({
        principalId: this.serverPrincipalId,
        projectId: this.config.projectId,
        threadId,
      });
      this.workFollowUpQueues.set(threadId, queue);
    }
    return queue;
  }

  private enqueueWorkFollowUp(
    threadId: string,
    activeTurnId: string,
    text: string,
    prompt: HTMLTextAreaElement,
    attachments: Attachment[],
    handoffId?: string,
  ): boolean {
    try {
      const queue = this.workFollowUpQueue(threadId);
      const clientMessageId = crypto.randomUUID();
      const payload: QueuedWorkFollowUpPayload = {
        text,
        input: [...(text ? [{ type: "text", text } as const] : []), ...attachments.map((attachment) => attachment.codexInput)],
        attachments: attachments.map(persistedAttachment),
        createdAt: new Date().toISOString(),
        executionPolicy: this.selectedWorkExecutionPolicy,
        ...(handoffId ? { handoffId } : {}),
      };
      const decision = queue.route({ clientMessageId, activeTurnId, payload }, "wait-for-next");
      if (decision.action !== "queued") throw new Error("下一轮队列没有接受这条指令");
      const article = renderUserMessage("work", text, { clientMessageId, intent: "queue" });
      updateOptimisticMessage(article, "rpcAccepted");
      this.decorateMessage(article, "work", "user", undefined, text);
      renderMessageAttachments(article, attachments);
      prompt.value = "";
      prompt.dispatchEvent(new Event("input", { bubbles: true }));
      this.attachments.set("work", []);
      this.updateAttachmentChips("work");
      toast(`已加入下一轮${queue.snapshot().size > 1 ? ` · 队列中共 ${queue.snapshot().size} 条` : ""}`, "success");
      this.renderWorkControl(this.workControl.snapshot());
      return true;
    } catch (error) {
      toast(error instanceof Error ? error.message : "下一轮队列写入失败；内容没有发送", "error");
      return false;
    }
  }

  private renderQueuedWorkMessages(queue: WorkFollowUpQueue<QueuedWorkFollowUpPayload>): void {
    if (this.activeThread.get("work") !== queue.threadId) return;
    const canvas = canvasFor("work");
    for (const entry of queue.snapshot().entries) {
      let article = canvas.querySelector<HTMLElement>(`.user-message[data-client-message-id='${CSS.escape(entry.clientMessageId)}']`);
      if (!article) article = renderUserMessage("work", entry.payload.text, {
        clientMessageId: entry.clientMessageId,
        intent: "queue",
        createdAt: entry.payload.createdAt,
      });
      article.dataset.submissionIntent = "queue";
      article.dataset.optimistic = "accepted";
      article.removeAttribute("aria-busy");
      const delivery = article.querySelector<HTMLElement>(".runtime-delivery-state");
      if (delivery) delivery.textContent = entry.state === "dispatching"
        ? "下一轮结果待确认 · 正在恢复"
        : entry.state === "ready"
          ? "当前任务已结束 · 等待发送下一轮"
          : "已排队 · 当前任务完成后发送";
      this.decorateMessage(article, "work", "user", undefined, entry.payload.text);
      renderMessageAttachments(article, entry.payload.attachments);
    }
  }

  private scheduleWorkFollowUpRetry(threadId: string): void {
    if (this.workFollowUpRetryTimers.has(threadId)) return;
    const attempt = this.workFollowUpRetryAttempts.get(threadId) || 0;
    const delay = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
    this.workFollowUpRetryAttempts.set(threadId, attempt + 1);
    const timer = window.setTimeout(() => {
      this.workFollowUpRetryTimers.delete(threadId);
      void this.dispatchQueuedWork(threadId);
    }, delay);
    this.workFollowUpRetryTimers.set(threadId, timer);
  }

  private async dispatchQueuedWork(threadId: string): Promise<void> {
    if (this.client.state !== "ready"
      || !this.operationIsAvailable("turn/start")
      || !this.operationIsAvailable("gateway/idempotentTurnStart")) return;
    let queue: WorkFollowUpQueue<QueuedWorkFollowUpPayload>;
    try {
      queue = this.workFollowUpQueue(threadId);
    } catch (error) {
      toast(error instanceof Error ? error.message : "下一轮队列无法恢复", "error");
      return;
    }
    const entry = queue.inFlight() || queue.claimReady();
    if (!entry) return;
    this.renderQueuedWorkMessages(queue);
    if (!this.workControl.beginSubmission(threadId) && !queue.inFlight()) return;
    const article = canvasFor("work").querySelector<HTMLElement>(`.user-message[data-client-message-id='${CSS.escape(entry.clientMessageId)}']`);
    const delivery = article?.querySelector<HTMLElement>(".runtime-delivery-state");
    if (delivery) delivery.textContent = "正在发送下一轮";
    try {
      const result = await this.client.rpc<Record<string, unknown>>("turn/start", {
        threadId,
        input: entry.payload.input,
        cwd: this.config.workspacePath,
        clientUserMessageId: entry.clientMessageId,
        ...turnExecutionPolicyParams(
          normalizeWorkExecutionPolicy(entry.payload.executionPolicy),
          this.config.workspacePath,
        ),
      });
      const turnId = resultId(result, "turn");
      if (!turnId) throw new Error("服务器没有返回下一轮任务标识");
      queue.acknowledgeRpcAccepted(entry.clientMessageId, turnId);
      if (article) updateOptimisticMessage(article, "rpcAccepted");
      this.workControl.submissionAccepted(threadId, turnId, "start");
      if (entry.payload.handoffId) this.completeWorkflowHandoff(entry.payload.handoffId);
      this.workFollowUpRetryAttempts.delete(threadId);
      toast("下一轮任务已开始", "success");
    } catch (error) {
      const message = error instanceof Error ? error.message : "下一轮发送失败";
      if (requestOutcomeMayExist(error)) {
        this.workControl.beginResync(threadId);
        if (delivery) delivery.textContent = "下一轮结果待确认 · 正在恢复";
        toast("下一轮结果尚未确认，将使用同一请求标识恢复，不会新建重复指令", "error");
        this.scheduleWorkFollowUpRetry(threadId);
      } else {
        const discarded = queue.discardAfterRpcRejection(entry.clientMessageId);
        this.workFollowUpRetryAttempts.delete(threadId);
        this.workControl.submissionFailed(threadId, message);
        if (delivery) delivery.textContent = "下一轮未发送 · 内容已恢复，可修改后重试";
        const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
        if (this.activeThread.get("work") === threadId) {
          if (prompt) restoreComposerText(prompt, discarded.payload.text);
          const restoredAttachments = Array.isArray(discarded.payload.attachments)
            ? discarded.payload.attachments.map((attachment) => ({
              ...attachment,
              codexInput: { ...attachment.codexInput },
            }))
            : [];
          this.attachments.set("work", restoredAttachments);
          this.updateAttachmentChips("work");
        }
        toast(`${message}；内容已恢复到输入框`, "error");
        if (queue.snapshot().canClaimHead) this.scheduleWorkFollowUpRetry(threadId);
      }
    }
  }

  private onRemoteAction(detail: { surface?: Surface; action?: string; enabled?: boolean }): void {
    if (!detail?.surface || !detail.action) return;
    const selected = this.selectedTools.get(detail.surface)!;
    const pendingKey = `${detail.surface}:${detail.action}`;
    if (this.remoteActionPending.has(pendingKey)) {
      for (const button of document.querySelectorAll<HTMLButtonElement>(`[data-surface='${CSS.escape(detail.surface)}'][data-remote-action='${CSS.escape(detail.action)}']`)) {
        button.setAttribute("aria-pressed", String(selected.has(detail.action)));
      }
      toast("这个工具的状态仍在与服务器同步，请稍候");
      return;
    }
    if (detail.enabled) selected.add(detail.action);
    else selected.delete(detail.action);
    if (detail.surface === "chat") {
      // Chat tool selection is part of the idempotent chat/message/create
      // payload. It does not need a separate, racy activation RPC.
      this.renderChatControl(this.chatControl.snapshot());
      return;
    }
    const workViews: Record<string, { method: string; title: string }> = {
      file_tree: { method: "fs/readDirectory", title: "文件树" },
      terminal_output: { method: "thread/read", title: "执行记录" },
      skills: { method: "skills/list", title: "技能" },
      mcp: { method: "mcpServerStatus/list", title: "MCP 服务" },
      permissions: { method: "permissionProfile/list", title: "权限与审批" },
      model: { method: "model/list", title: "模型与推理" },
      other: { method: "capabilities/read", title: "服务器能力" },
    };
    const view = detail.surface === "work" ? workViews[detail.action] : undefined;
    if (view && detail.enabled) {
      (document.querySelector("[data-close-drawer='work-tools']") as HTMLButtonElement | null)?.click();
      if (detail.action === "model") void this.openWorkModelPicker();
      else this.openServerList(view.method, view.title);
      for (const button of document.querySelectorAll<HTMLButtonElement>(`[data-surface='work'][data-remote-action='${CSS.escape(detail.action)}']`)) {
        button.setAttribute("aria-pressed", "false");
      }
      this.selectedTools.get("work")?.delete(detail.action);
      return;
    }
    if (this.client.state === "ready"
      && this.serverPrincipalId
      && detail.action !== "image_generate"
      && this.operationIsAvailable("capability/activate")) {
      const activationPrincipalId = this.serverPrincipalId;
      this.remoteActionPending.add(pendingKey);
      const affected = Array.from(document.querySelectorAll<HTMLButtonElement>(`[data-surface='${CSS.escape(detail.surface)}'][data-remote-action='${CSS.escape(detail.action)}']`));
      for (const button of affected) {
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
      }
      this.client.rpc("capability/activate", {
        surface: detail.surface,
        capability: detail.action,
        enabled: Boolean(detail.enabled),
        projectId: this.config.projectId,
      }).catch((error) => {
        if (this.serverPrincipalId !== activationPrincipalId) {
          this.remoteActionPending.delete(pendingKey);
          return;
        }
        if (!this.remoteActionPending.has(pendingKey)) return;
        if (detail.enabled) selected.delete(detail.action!);
        else selected.add(detail.action!);
        for (const button of document.querySelectorAll<HTMLButtonElement>(`[data-surface='${CSS.escape(detail.surface!)}'][data-remote-action='${CSS.escape(detail.action!)}']`)) {
          button.setAttribute("aria-pressed", String(!detail.enabled));
        }
        toast(error instanceof Error ? error.message : "服务器未接受能力切换", "error");
      }).finally(() => {
        if (this.serverPrincipalId !== activationPrincipalId) {
          this.remoteActionPending.delete(pendingKey);
          return;
        }
        this.remoteActionPending.delete(pendingKey);
        const canActivate = this.client.state === "ready" && this.operationIsAvailable("capability/activate");
        for (const button of affected) {
          button.removeAttribute("aria-busy");
          const state = String(button.dataset.capabilityState || "unavailable");
          button.disabled = !canActivate || ["unavailable", "unsupported", "auth_required"].includes(state);
          button.setAttribute("aria-pressed", String(selected.has(detail.action!)));
        }
      });
    } else if (detail.enabled && detail.action !== "image_generate") {
      selected.delete(detail.action);
      for (const button of document.querySelectorAll<HTMLButtonElement>(`[data-surface='${CSS.escape(detail.surface)}'][data-remote-action='${CSS.escape(detail.action)}']`)) {
        button.setAttribute("aria-pressed", "false");
      }
      toast(this.client.state !== "ready" ? "服务器尚未连接" : "服务器没有声明此工具激活操作", "error");
    }
  }

  private installWorkflowHandoffs(principalId: string | null): void {
    this.unsubscribeWorkflowHandoffs?.();
    this.unsubscribeWorkflowHandoffs = null;
    this.workflowHandoffs = null;
    this.pendingRecoveredHandoffIds.clear();
    this.submittedVideoHandoffIds.clear();
    if (!principalId) return;
    for (const [handoffId, submittedPrincipalId] of this.submittedVideoHandoffPrincipals) {
      if (submittedPrincipalId === principalId) this.submittedVideoHandoffIds.add(handoffId);
    }
    try {
      const store = new WorkflowHandoffStore({
        principalId,
        projectId: this.config.projectId,
      });
      this.workflowHandoffs = store;
      this.reconcileAcceptedVideoHandoffs(principalId);
      this.unsubscribeWorkflowHandoffs = store.subscribe((event) => {
        window.dispatchEvent(new CustomEvent("bianhua:handoff-state", { detail: event }));
        if (event.type === "recovered") {
          window.dispatchEvent(new CustomEvent("bianhua:handoff-recovered", {
            detail: { handoffs: event.handoffs },
          }));
          const active = event.handoffs.filter((handoff) => handoff.status === "staged" || handoff.status === "ready");
          for (const targetSurface of ["work", "video"] as const) {
            const matching = active.filter((handoff) => handoff.target.surface === targetSurface);
            const resumable = matching.at(-1);
            for (const superseded of matching.slice(0, -1)) {
              if (this.submittedVideoHandoffIds.has(superseded.handoffId)) continue;
              try { store.dismiss(superseded.handoffId); } catch { /* preserve the newest recoverable handoff */ }
            }
            if (!resumable) continue;
            try {
              const delivery = store.prepareDelivery(
                resumable.handoffId,
                resumable.target.surface === "work" ? { workDisposition: "explicit-submit" } : {},
              );
              const current = store.get(resumable.handoffId) || resumable;
              if (current.target.surface === "work" && current.artifactRefs.length) {
                this.pendingRecoveredHandoffIds.add(current.handoffId);
              }
              this.dispatchWorkflowState("staged", current, delivery);
            } catch { /* malformed recovery stays fail-closed in the journal */ }
          }
          this.renderWorkControl(this.workControl.snapshot());
          if (this.client.state === "ready") queueMicrotask(() => void this.resumeRecoveredWorkflowHandoffs());
        }
      });
    } catch (error) {
      toast(error instanceof Error ? error.message : "跨模式交接日志无法恢复", "error");
    }
  }

  private reconcileAcceptedVideoHandoffs(principalId: string): void {
    const store = this.workflowHandoffs;
    if (!store || principalId !== this.serverPrincipalId) return;
    for (const [handoffId, accepted] of this.acceptedVideoHandoffs) {
      if (accepted.principalId !== principalId) continue;
      try {
        const handoff = store.get(handoffId);
        if (!handoff) continue;
        const completed = store.complete(handoffId);
        this.submittedVideoHandoffIds.delete(handoffId);
        this.submittedVideoHandoffPrincipals.delete(handoffId);
        this.acceptedVideoHandoffs.delete(handoffId);
        this.dispatchWorkflowState("completed", completed);
      } catch (error) {
        window.dispatchEvent(new CustomEvent("bianhua:handoff-error", {
          detail: {
            targetSurface: "video",
            message: error instanceof Error ? error.message : "视频任务已接受，但交接日志尚未完成确认",
          },
        }));
      }
    }
  }

  private dispatchWorkflowState(
    state: "staged" | "completed" | "error",
    handoff: WorkflowHandoff,
    delivery?: WorkflowDelivery,
    message?: string,
  ): void {
    const intent = handoff.purpose === "idea_to_video_prompt" ? "develop-video-prompt"
      : handoff.purpose === "video_prompt_to_generation" ? "generate-video"
        : handoff.purpose === "review_video" ? "review-video" : "edit-video";
    const deliveryText = delivery?.kind === "video_draft"
      ? delivery.prompt
      : delivery?.kind === "work_draft" || delivery?.kind === "work_explicit_submit"
        ? delivery.text
        : handoff.text;
    const detail = {
      handoffId: handoff.handoffId,
      purpose: handoff.purpose,
      sourceSurface: handoff.source.surface,
      targetSurface: handoff.target.surface,
      intent,
      text: deliveryText,
      assetIds: delivery?.assetIds ?? handoff.assetIds,
      artifactRefs: delivery?.artifactRefs ?? handoff.artifactRefs,
      handoff,
      delivery,
      message,
    };
    window.dispatchEvent(new CustomEvent(`bianhua:handoff-${state}`, { detail }));
  }

  private completeWorkflowHandoff(handoffId: string): boolean {
    if (!this.workflowHandoffs) return false;
    const current = this.workflowHandoffs.get(handoffId);
    if (!current) return false;
    if (current.status === "completed" || current.status === "dismissed") return true;
    try {
      const completed = this.workflowHandoffs.complete(handoffId);
      this.dispatchWorkflowState("completed", completed);
      return true;
    } catch {
      return false;
    }
  }

  private workflowTextForPurpose(
    purpose: WorkflowHandoffPurpose,
    originalText: string,
    artifactRefs: WorkflowArtifactRef[],
  ): { objective: string; text: string } {
    if (purpose === "idea_to_video_prompt") {
      return {
        objective: "把聊天想法整理成可直接生成视频的专业提示词",
        text: [
          "请只完成视频提示词设计，不修改项目文件，也不要运行命令。",
          "请把下面的聊天想法整理成可直接交给视频生成模型的中文专业提示词，并明确主体、场景、镜头、运动、光线、节奏、声音和应避免的问题。",
          "输出最终提示词后，再用三条以内说明关键设计取舍。",
          "",
          "原始想法：",
          originalText,
        ].join("\n"),
      };
    }
    if (purpose === "video_prompt_to_generation") {
      return {
        objective: "把工作结果送往视频模式，等待用户确认生成",
        text: originalText,
      };
    }
    const action = purpose === "review_video" ? "审核" : "继续剪辑";
    const refs = artifactRefs.map((ref) => `- ${ref.artifactId || ref.assetId} · ${ref.relativePath}`).join("\n");
    return {
      objective: `${action}服务器留存的视频成品`,
      text: [
        `请${action}以下由视频模式生成、且已由服务器留存的产物。`,
        purpose === "review_video"
          ? "请检查画面一致性、主体漂移、镜头连续性、节奏、字幕/水印、音画同步与交付规格；先给结论，再列问题和修改建议。"
          : "请先分析素材并提出安全的剪辑方案；需要修改文件或运行命令时，继续遵循当前工作区审批规则。",
        refs ? `\n产物引用：\n${refs}` : "",
        originalText ? `\n补充要求：\n${originalText}` : "",
      ].filter(Boolean).join("\n"),
    };
  }

  private async createWorkflowHandoff(detail: HandoffCreateDetail | undefined): Promise<void> {
    if (!detail || !this.workflowHandoffs || !this.serverPrincipalId) {
      toast("请先连接并登录服务器，再进行跨模式交接", "error");
      return;
    }
    const purpose = handoffPurpose(detail.purpose ?? detail.intent ?? detail.kind);
    if (!purpose) {
      toast("无法识别跨模式交接用途", "error");
      return;
    }
    const originalText = typeof detail.text === "string" ? detail.text.trim() : "";
    const rawRefs = Array.isArray(detail.artifactRefs) ? detail.artifactRefs : [];
    const artifactRefs: WorkflowArtifactRef[] = rawRefs
      .filter((value): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value)))
      .map((value) => ({
        assetId: String(value.assetId || value.artifactId || ""),
        ...(value.artifactId ? { artifactId: String(value.artifactId) } : {}),
        relativePath: String(value.relativePath || ""),
        ...(value.mediaType ? { mediaType: String(value.mediaType) } : {}),
        ...(value.sha256 ? { sha256: String(value.sha256) } : {}),
      }));
    if (!originalText && purpose !== "review_video" && purpose !== "edit_video") {
      toast("没有可交接的内容", "error");
      return;
    }
    if ((purpose === "review_video" || purpose === "edit_video") && artifactRefs.length === 0) {
      toast("视频产物尚未完成服务器留存，暂时不能交给工作模式", "error");
      return;
    }
    const sourceSurface = purpose === "idea_to_video_prompt" ? "chat"
      : purpose === "video_prompt_to_generation" ? "work" : "video";
    const targetSurface = sourceSurface === "work" ? "video" : "work";
    if (targetSurface === "video" && this.submittedVideoHandoffIds.size) {
      toast("上一条视频付费创建仍在确认，暂不能覆盖其交接关系", "error");
      return;
    }
    if ((purpose === "review_video" || purpose === "edit_video") && !this.handoffMaterializationAvailable) {
      toast("服务器尚未提供视频产物装入工作区能力", "error");
      return;
    }
    const requestedSource = detail.sourceSurface;
    const requestedTarget = detail.targetSurface;
    if ((requestedSource !== undefined && requestedSource !== sourceSurface)
      || (requestedTarget !== undefined && requestedTarget !== targetSurface)) {
      toast("交接来源或目标与用途不一致", "error");
      return;
    }
    const sourceThreadId = workflowIdentifier(detail.sourceThreadId)
      || (sourceSurface !== "video" ? this.activeThread.get(sourceSurface) : undefined)
      || workflowIdentifier(detail.artifactId)
      || workflowId(`${sourceSurface}-source`);
    const { objective, text } = this.workflowTextForPurpose(purpose, originalText, artifactRefs);
    const assetIds = Array.from(new Set([
      ...(Array.isArray(detail.assetIds) ? detail.assetIds.map(String) : []),
      ...artifactRefs.map((ref) => ref.assetId),
    ].filter(Boolean)));
    const idempotencyKey = workflowIdentifier(detail.requestId) || workflowId("handoff-request");
    const correlationId = workflowIdentifier(detail.correlationId) || idempotencyKey;
    const base = {
      idempotencyKey,
      correlationId,
      principalId: this.serverPrincipalId,
      projectId: this.config.projectId,
      source: {
        surface: sourceSurface,
        projectId: this.config.projectId,
        threadId: sourceThreadId,
        ...(workflowIdentifier(detail.sourceTurnId) ? { turnId: workflowIdentifier(detail.sourceTurnId)! } : {}),
        ...(workflowIdentifier(detail.sourceItemId) ? { itemId: workflowIdentifier(detail.sourceItemId)! } : {}),
        ...(workflowIdentifier(detail.artifactId) ? { artifactId: workflowIdentifier(detail.artifactId)! } : {}),
      },
      target: {
        surface: targetSurface,
        projectId: this.config.projectId,
        ...(targetSurface === "work" && this.activeThread.get("work")
          ? { threadId: this.activeThread.get("work")! }
          : {}),
      },
      objective: typeof detail.objective === "string" && detail.objective.trim()
        ? detail.objective.trim()
        : objective,
      text,
      assetIds,
      artifactRefs,
    };
    try {
      const handoff = this.workflowHandoffs.stage({ ...base, purpose } as unknown as NewWorkflowHandoff);
      for (const superseded of this.workflowHandoffs.list().filter((candidate) => (
        candidate.handoffId !== handoff.handoffId
        && candidate.target.surface === targetSurface
        && (candidate.status === "staged" || candidate.status === "ready")
        && !this.submittedVideoHandoffIds.has(candidate.handoffId)
      ))) {
        this.workflowHandoffs.dismiss(superseded.handoffId);
      }
      let delivery = this.workflowHandoffs.prepareDelivery(
        handoff.handoffId,
        targetSurface === "work" ? { workDisposition: "explicit-submit" } : {},
      );
      const targetPrompt = document.querySelector<HTMLTextAreaElement>(
        targetSurface === "work" ? "#work-prompt" : "#video-prompt",
      );
      const existingTargetDraft = targetPrompt?.value.trim() || "";
      const existingTargetAttachments = targetSurface === "work"
        ? [...(this.attachments.get("work") || [])]
        : [];
      const deliveredText = delivery.kind === "video_draft" ? delivery.prompt : delivery.text;
      if (existingTargetDraft && existingTargetDraft !== deliveredText) {
        const preservedDraft = delivery.kind === "video_draft"
          ? `${deliveredText}\n\n---\n原视频草稿（已保留）：\n${existingTargetDraft}`
          : `${deliveredText}\n\n---\n原工作草稿（已保留）：\n${existingTargetDraft}`;
        delivery = this.workflowHandoffs.updateDraft(handoff.handoffId, preservedDraft);
      }
      this.dispatchWorkflowState("staged", handoff, delivery);
      await this.deliverWorkflowHandoff(handoff, delivery, "", existingTargetAttachments);
    } catch (error) {
      const message = error instanceof WorkflowHandoffError || error instanceof Error
        ? error.message
        : "跨模式交接失败";
      toast(message, "error");
      window.dispatchEvent(new CustomEvent("bianhua:handoff-error", { detail: { message } }));
    }
  }

  private clearWorkflowDraftTimers(): void {
    for (const timer of this.workflowDraftTimers.values()) window.clearTimeout(timer);
    this.workflowDraftTimers.clear();
  }

  private scheduleWorkflowDraftPersist(
    targetSurface: "work" | "video",
    prompt: HTMLTextAreaElement,
  ): void {
    const stage = document.querySelector<HTMLElement>(`[data-handoff-stage='${targetSurface}']`);
    const handoffId = workflowIdentifier(stage?.dataset.handoffId);
    if (!handoffId || !this.workflowHandoffs || !this.serverPrincipalId) return;
    const existingTimer = this.workflowDraftTimers.get(targetSurface);
    if (existingTimer) window.clearTimeout(existingTimer);
    const principalId = this.serverPrincipalId;
    const timer = window.setTimeout(() => {
      this.workflowDraftTimers.delete(targetSurface);
      if (this.serverPrincipalId !== principalId || stage?.dataset.handoffId !== handoffId) return;
      try {
        const handoff = this.workflowHandoffs?.get(handoffId);
        if (!handoff || handoff.status !== "ready" || handoff.target.surface !== targetSurface) return;
        this.workflowHandoffs?.updateDraft(handoffId, prompt.value);
      } catch (error) {
        toast(error instanceof Error ? error.message : "交接草稿未能持久保存", "error");
      }
    }, 250);
    this.workflowDraftTimers.set(targetSurface, timer);
  }

  private persistWorkflowDraft(handoffId: string, text: string): void {
    const handoff = this.workflowHandoffs?.get(handoffId);
    if (!handoff || handoff.status !== "ready") return;
    this.workflowHandoffs?.updateDraft(handoffId, text);
  }

  private async deliverWorkflowHandoff(
    handoff: WorkflowHandoff,
    delivery: WorkflowDelivery,
    existingTargetDraft = "",
    existingTargetAttachments: Attachment[] = [],
    options: { autoSubmitWork?: boolean } = {},
  ): Promise<void> {
    window.dispatchEvent(new CustomEvent("bianhua:set-mode", {
      detail: { mode: handoff.target.surface, source: "workflow-handoff" },
    }));
    if (delivery.kind === "video_draft") {
      const prompt = document.querySelector<HTMLTextAreaElement>("#video-prompt");
      if (prompt && existingTargetDraft && existingTargetDraft !== delivery.prompt) {
        prompt.value = `${delivery.prompt}\n\n---\n原视频草稿（已保留）：\n${existingTargetDraft}`;
        prompt.dispatchEvent(new Event("input", { bubbles: true }));
        this.persistWorkflowDraft(handoff.handoffId, prompt.value);
        toast("提示词已加入视频模式；检测到原草稿，请确认参数后再点击生成");
        return;
      }
      toast("提示词已送到视频模式；确认参数后点击生成才会计费", "success");
      return;
    }
    const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
    const send = document.querySelector<HTMLButtonElement>("[data-send-for='work-prompt']");
    if (!prompt || !send) throw new Error("工作模式输入区尚未就绪");
    const existing = existingTargetDraft;
    if (delivery.artifactRefs.length) {
      if (!this.handoffMaterializationAvailable) {
        throw new Error("服务器未提供视频产物装入工作区能力");
      }
      const materialized = await this.materializeHandoffArtifacts(handoff, delivery.artifactRefs);
      if (this.serverPrincipalId !== handoff.principalId
        || this.workflowHandoffs?.get(handoff.handoffId)?.principalId !== handoff.principalId) {
        throw new Error("账户已切换，已丢弃旧账户的视频交接结果");
      }
      const combined = [...existingTargetAttachments];
      for (const attachment of materialized) {
        if (!combined.some((candidate) => candidate.uploadId === attachment.uploadId)) combined.push(attachment);
      }
      this.attachments.set("work", combined);
      this.updateAttachmentChips("work");
    }
    prompt.value = existing && existing !== delivery.text
      ? `${delivery.text}\n\n---\n原工作草稿（已保留）：\n${existing}`
      : delivery.text;
    prompt.dispatchEvent(new Event("input", { bubbles: true }));
    this.persistWorkflowDraft(handoff.handoffId, prompt.value);
    if (existing && existing !== delivery.text) {
      toast("交接内容已加入工作输入框；检测到原草稿，请确认后发送");
      return;
    }
    if (options.autoSubmitWork === false) {
      toast("视频产物已重新装入工作区；请确认后发送审核或剪辑任务", "success");
      return;
    }
    const accepted = await this.sendFromComposer(send, { handoffId: handoff.handoffId });
    if (accepted) {
      toast("交接任务已提交给工作模式", "success");
    } else {
      toast("交接内容已保留在工作输入框，尚未由服务器确认", "error");
    }
  }

  private async materializeHandoffArtifacts(
    handoff: WorkflowHandoff,
    refs: WorkflowArtifactRef[],
  ): Promise<Attachment[]> {
    if (this.client.state !== "ready") throw new Error("服务器未连接，无法把视频产物装入工作区");
    const attachments: Attachment[] = [];
    for (const ref of refs) {
      const artifactId = ref.artifactId || ref.assetId;
      const result = await this.client.rpc<MaterializedArtifact>("artifact/materializeToWorkspace", {
        requestId: `${handoff.handoffId}-${artifactId}`,
        artifactId,
        purpose: handoff.purpose === "review_video" ? "review" : "edit",
      }, { timeoutMs: 120_000 });
      const normalizedRoot = this.config.workspacePath.replace(/\\/g, "/").replace(/\/$/, "");
      const serverPath = String(result.serverPath || "").replace(/\\/g, "/");
      const returnedSha256 = String(result.sha256 || "");
      if (!serverPath.startsWith(`${normalizedRoot}/`)
        || !/^[a-f0-9]{64}$/i.test(returnedSha256)
        || (ref.sha256 !== undefined && ref.sha256.toLowerCase() !== returnedSha256.toLowerCase())
        || !Number.isSafeInteger(result.size)
        || result.size < 0
        || result.artifactId !== artifactId) {
        throw new Error("服务器返回了无效或越界的视频工作区引用");
      }
      const name = String(result.relativePath || ref.relativePath).split("/").at(-1) || "video.mp4";
      attachments.push({
        uploadId: `handoff-${artifactId}`,
        name,
        size: result.size,
        mimeType: String(result.mediaType || ref.mediaType || "video/mp4"),
        sha256: returnedSha256,
        codexInput: { type: "mention", name, path: serverPath },
      });
    }
    return attachments;
  }

  private attachmentUploadIsAvailable(surface: Surface): boolean {
    return this.client.state === "ready"
      && this.modeIsUsable(surface)
      && ATTACHMENT_UPLOAD_OPERATIONS.every((operation) => this.operationIsAvailable(operation));
  }

  private bindAttachments(): void {
    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-attachment-for]"));
    for (const button of buttons) {
      const surface: Surface = button.closest(".work-composer") ? "work" : "chat";
      const prompt = document.querySelector<HTMLTextAreaElement>(surface === "work" ? "#work-prompt" : "#chat-prompt");
      const composer = button.closest<HTMLElement>(".composer, .composer-card, .composer-shell") || prompt?.parentElement;
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.hidden = true;
      input.setAttribute("aria-label", surface === "chat" ? "选择聊天附件" : "选择工作附件");
      button.after(input);
      button.addEventListener("click", () => input.click());
      input.addEventListener("change", () => {
        const files = Array.from(input.files || []);
        input.value = "";
        this.uploadFiles(surface, files).catch((error) => toast(error instanceof Error ? error.message : "附件上传失败", "error"));
      });
      prompt?.addEventListener("paste", (event) => {
        const files = Array.from(event.clipboardData?.items || [])
          .filter((item) => item.kind === "file")
          .map((item) => item.getAsFile())
          .filter((file): file is File => Boolean(file));
        if (!files.length) return;
        event.preventDefault();
        this.uploadFiles(surface, files).catch((error) => toast(error instanceof Error ? error.message : "粘贴附件失败", "error"));
      });
      if (composer) {
        const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types || []).includes("Files");
        for (const type of ["dragenter", "dragover"] as const) {
          composer.addEventListener(type, (event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
            composer.classList.add("runtime-attachment-drop-active");
          });
        }
        composer.addEventListener("dragleave", (event) => {
          const related = event.relatedTarget;
          if (!(related instanceof Node) || !composer.contains(related)) composer.classList.remove("runtime-attachment-drop-active");
        });
        composer.addEventListener("drop", (event) => {
          if (!hasFiles(event)) return;
          event.preventDefault();
          composer.classList.remove("runtime-attachment-drop-active");
          const files = Array.from(event.dataTransfer?.files || []);
          this.uploadFiles(surface, files).catch((error) => toast(error instanceof Error ? error.message : "拖入附件失败", "error"));
        });
      }
    }
  }

  private async uploadFiles(surface: Surface, files: File[]): Promise<void> {
    if (!files.length) return;
    if (this.client.state !== "ready") throw new Error("请先连接服务器再添加附件");
    if (ATTACHMENT_UPLOAD_OPERATIONS.some((operation) => !this.operationIsAvailable(operation))) {
      throw new Error("当前服务器暂不支持附件上传");
    }
    const drafts = this.attachmentDrafts.get(surface)!;
    let totalBytes = [...(this.attachments.get(surface) || [])].reduce((total, attachment) => total + attachment.size, 0)
      + [...drafts.values()].reduce((total, draft) => total + draft.size, 0);
    const accepted: AttachmentDraft[] = [];
    for (const file of files) {
      const name = file.name.trim() || `粘贴的图片-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
      const normalizedFile = file.name.trim()
        ? file
        : new File([file], name, { type: file.type || "image/png", lastModified: file.lastModified || Date.now() });
      if (normalizedFile.size > MAX_ATTACHMENT_BYTES) {
        toast(`${name} 超过 50 MB 上限`, "error");
        continue;
      }
      if ((this.attachments.get(surface)?.length || 0) + drafts.size >= MAX_ATTACHMENTS_PER_MESSAGE) {
        toast(`每条消息最多添加 ${MAX_ATTACHMENTS_PER_MESSAGE} 个附件`, "error");
        break;
      }
      if (totalBytes + normalizedFile.size > MAX_ATTACHMENT_TOTAL_BYTES) {
        toast("单条消息的附件总量不能超过 100 MB", "error");
        break;
      }
      const draft: AttachmentDraft = {
        localId: crypto.randomUUID(),
        file: normalizedFile,
        name,
        size: normalizedFile.size,
        mimeType: normalizedFile.type || "application/octet-stream",
        status: "uploading",
        ...(normalizedFile.type.startsWith("image/") ? { localPreviewUrl: URL.createObjectURL(normalizedFile) } : {}),
      };
      drafts.set(draft.localId, draft);
      accepted.push(draft);
      totalBytes += normalizedFile.size;
    }
    this.updateAttachmentChips(surface);
    const queue = [...accepted];
    const worker = async (): Promise<void> => {
      for (let draft = queue.shift(); draft; draft = queue.shift()) {
        try {
          await this.uploadAttachmentDraft(surface, draft);
        } catch (error) {
          if (!drafts.has(draft.localId)) continue;
          draft.status = "failed";
          draft.error = error instanceof Error ? error.message : "附件上传失败";
          this.updateAttachmentChips(surface);
          toast(`${draft.name} 上传失败：${draft.error}`, "error");
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, queue.length) }, () => worker()));
  }

  private async uploadAttachmentDraft(surface: Surface, draft: AttachmentDraft): Promise<void> {
    const drafts = this.attachmentDrafts.get(surface)!;
    draft.status = "uploading";
    delete draft.error;
    this.updateAttachmentChips(surface);
    const buffer = await draft.file.arrayBuffer();
    if (!drafts.has(draft.localId)) return;
    const checksum = await sha256(buffer);
    let uploadId: string | undefined;
    let completed: Record<string, unknown>;
    try {
      const begin = await this.client.rpc<{ uploadId: string; chunkSize?: number }>("artifact/upload/begin", {
        projectId: this.config.projectId,
        name: draft.name,
        sizeBytes: draft.size,
        mimeType: draft.mimeType,
        sha256: checksum,
      });
      uploadId = begin.uploadId;
      draft.remoteUploadId = uploadId;
      const chunkSize = Math.max(64 * 1024, Math.min(512 * 1024, begin.chunkSize || 256 * 1024));
      const bytes = new Uint8Array(buffer);
      for (let offset = 0, index = 0; offset < bytes.length; offset += chunkSize, index += 1) {
        if (!drafts.has(draft.localId)) {
          this.cancelUploadedAttachment(uploadId);
          return;
        }
        await this.client.rpc("artifact/upload/chunk", {
          uploadId,
          index,
          offset,
          data: bytesToBase64(bytes.subarray(offset, Math.min(bytes.length, offset + chunkSize))),
        }, { timeoutMs: 120_000 });
      }
      completed = await this.client.rpc<Record<string, unknown>>("artifact/upload/complete", {
        uploadId,
        sha256: checksum,
      }, { timeoutMs: 120_000 });
    } catch (error) {
      if (uploadId) this.cancelUploadedAttachment(uploadId);
      delete draft.remoteUploadId;
      throw error;
    }
    if (!drafts.has(draft.localId)) {
      if (uploadId) this.cancelUploadedAttachment(uploadId);
      return;
    }
    const codexInput = codexInputForUpload(completed, draft.file);
    drafts.delete(draft.localId);
    this.attachments.get(surface)!.push({
      uploadId: uploadId!,
      name: draft.name,
      size: draft.size,
      mimeType: draft.mimeType,
      sha256: checksum,
      codexInput,
      ...(draft.localPreviewUrl ? { localPreviewUrl: draft.localPreviewUrl } : {}),
    });
    this.updateAttachmentChips(surface);
    toast(`${draft.name} 已准备发送`, "success");
  }

  private cancelUploadedAttachment(uploadId: string): void {
    if (!this.operationIsAvailable("artifact/upload/cancel") || this.client.state !== "ready") return;
    void this.client.rpc("artifact/upload/cancel", { uploadId }).catch(() => {});
  }

  private removeAttachmentDraft(surface: Surface, draft: AttachmentDraft): void {
    this.attachmentDrafts.get(surface)?.delete(draft.localId);
    if (draft.localPreviewUrl) URL.revokeObjectURL(draft.localPreviewUrl);
    if (draft.remoteUploadId) this.cancelUploadedAttachment(draft.remoteUploadId);
    this.updateAttachmentChips(surface);
  }

  private updateAttachmentChips(surface: Surface): void {
    const composer = document.querySelector<HTMLElement>(`[data-composer='${surface}']`)
      || document.querySelector<HTMLElement>(surface === "work" ? ".work-composer" : ".chat-panel .composer");
    if (!composer) return;
    composer.querySelector(".runtime-attachments")?.remove();
    const attachments = this.attachments.get(surface) || [];
    const drafts = [...(this.attachmentDrafts.get(surface)?.values() || [])];
    if (!attachments.length && !drafts.length) {
      if (surface === "work") this.renderWorkControl(this.workControl.snapshot());
      else this.renderChatControl(this.chatControl.snapshot());
      return;
    }
    const region = document.createElement("div");
    region.className = "runtime-attachments";
    region.setAttribute("aria-label", "待发送附件");
    region.setAttribute("aria-live", "polite");
    const renderChip = (attachment: MessageAttachment, status: string, onRemove: () => void, onRetry?: () => void) => {
      const chip = document.createElement("span");
      chip.className = "runtime-attachment";
      if (attachment.localPreviewUrl && attachment.mimeType.startsWith("image/")) {
        const preview = document.createElement("img");
        preview.src = attachment.localPreviewUrl;
        preview.alt = "";
        chip.append(preview);
      } else {
        const icon = document.createElement("span");
        icon.className = "runtime-attachment-file-icon";
        icon.setAttribute("aria-hidden", "true");
        icon.textContent = attachment.mimeType.startsWith("image/") ? "图" : "文";
        chip.append(icon);
      }
      const copy = document.createElement("span");
      const name = document.createElement("strong");
      name.textContent = attachment.name;
      name.title = attachment.name;
      const detail = document.createElement("small");
      detail.textContent = [attachmentSizeLabel(attachment.size), status].filter(Boolean).join(" · ");
      copy.append(name, detail);
      chip.append(copy);
      if (onRetry) {
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "runtime-attachment-retry";
        retry.textContent = "重试";
        retry.setAttribute("aria-label", `重试上传 ${attachment.name}`);
        retry.addEventListener("click", onRetry);
        chip.append(retry);
      }
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "runtime-attachment-remove";
      remove.setAttribute("aria-label", `移除 ${attachment.name}`);
      remove.textContent = "×";
      remove.addEventListener("click", onRemove);
      chip.append(remove);
      region.append(chip);
    };
    for (const draft of drafts) {
      renderChip(
        draft,
        draft.status === "uploading" ? "正在上传" : "上传失败",
        () => this.removeAttachmentDraft(surface, draft),
        draft.status === "failed" ? () => {
          void this.uploadAttachmentDraft(surface, draft).catch((error) => {
            draft.status = "failed";
            draft.error = error instanceof Error ? error.message : "附件上传失败";
            this.updateAttachmentChips(surface);
          });
        } : undefined,
      );
    }
    for (const attachment of attachments) {
      renderChip(attachment, "已就绪", () => {
        this.attachments.set(surface, attachments.filter((item) => item.uploadId !== attachment.uploadId));
        if (attachment.localPreviewUrl) URL.revokeObjectURL(attachment.localPreviewUrl);
        this.cancelUploadedAttachment(attachment.uploadId);
        this.updateAttachmentChips(surface);
      });
    }
    composer.prepend(region);
    if (surface === "work") this.renderWorkControl(this.workControl.snapshot());
    else this.renderChatControl(this.chatControl.snapshot());
  }

  private chatSubmissionStorageKey(principalId: string): string {
    return `${this.chatSubmissionStoragePrefix}:${principalId}`;
  }

  private persistChatSubmission(journal: ChatSubmissionJournal): boolean {
    try {
      const storageKey = this.chatSubmissionStorageKey(journal.principalId);
      const serialized = JSON.stringify(journal);
      localStorage.setItem(storageKey, serialized);
      if (localStorage.getItem(storageKey) !== serialized) return false;
      this.uncertainChatSubmission = journal;
      this.uncertainChatAttempt = 0;
      return true;
    } catch {
      return false;
    }
  }

  private readChatSubmission(principalId: string): ChatSubmissionJournal | null {
    const storageKey = this.chatSubmissionStorageKey(principalId);
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || "null") as Record<string, unknown> | null;
      if (!parsed) return null;
      const operation = String(parsed.operation || "") as ChatSubmissionOperation;
      const clientMessageId = workflowIdentifier(parsed.clientMessageId);
      const threadId = parsed.threadId === undefined ? undefined : workflowIdentifier(parsed.threadId);
      const messageId = parsed.messageId === undefined ? undefined : workflowIdentifier(parsed.messageId);
      const text = typeof parsed.text === "string" ? parsed.text : "";
      const tools = Array.isArray(parsed.tools)
        ? parsed.tools.filter((item): item is string => typeof item === "string" && /^[a-z0-9_.:-]{1,64}$/i.test(item))
        : [];
      const rawAttachments = Array.isArray(parsed.attachments) ? parsed.attachments : [];
      const attachments: Attachment[] = rawAttachments.map((candidate) => {
        if (!candidate || typeof candidate !== "object") throw new Error("invalid attachment");
        const record = candidate as Record<string, unknown>;
        const input = record.codexInput && typeof record.codexInput === "object"
          ? record.codexInput as Record<string, unknown>
          : null;
        if (!input) throw new Error("invalid attachment input");
        const type = input?.type;
        if ((type !== "localImage" && type !== "mention") || typeof input.path !== "string" || !input.path) {
          throw new Error("invalid attachment input");
        }
        const codexInput: CodexFileInput = type === "localImage"
          ? { type, path: input.path, detail: null }
          : { type, path: input.path, name: String(input.name || record.name || "附件") };
        return {
          uploadId: String(record.uploadId || ""),
          name: String(record.name || ""),
          size: Number(record.size || 0),
          mimeType: String(record.mimeType || "application/octet-stream"),
          sha256: String(record.sha256 || ""),
          codexInput,
        };
      });
      const journal: ChatSubmissionJournal = {
        principalId: String(parsed.principalId || ""),
        clientMessageId: clientMessageId || "",
        operation,
        text,
        attachments: attachments.map(persistedAttachment),
        tools,
        createdAt: String(parsed.createdAt || ""),
        ...(threadId ? { threadId } : {}),
        ...(messageId ? { messageId } : {}),
      };
      if (
        journal.principalId !== principalId
        || !clientMessageId
        || !["chat/message/create", "chat/message/edit", "chat/message/regenerate"].includes(operation)
        || ((operation === "chat/message/edit" || operation === "chat/message/regenerate") && !messageId)
        || (operation !== "chat/message/regenerate" && !text.trim() && attachments.length === 0)
        || Number.isNaN(Date.parse(journal.createdAt))
        || tools.length !== (Array.isArray(parsed.tools) ? parsed.tools.length : 0)
      ) throw new Error("invalid chat journal");
      return journal;
    } catch {
      try { localStorage.removeItem(storageKey); } catch { /* no-op */ }
      return null;
    }
  }

  private clearChatSubmission(clientMessageId?: string): void {
    const journal = this.uncertainChatSubmission;
    if (!journal || (clientMessageId && journal.clientMessageId !== clientMessageId)) return;
    this.uncertainChatSubmission = null;
    this.uncertainChatAttempt = 0;
    if (this.uncertainChatTimer) window.clearTimeout(this.uncertainChatTimer);
    this.uncertainChatTimer = 0;
    try { localStorage.removeItem(this.chatSubmissionStorageKey(journal.principalId)); } catch { /* no-op */ }
  }

  private dropChatSubmissionMemory(): void {
    this.uncertainChatSubmission = null;
    this.uncertainChatAttempt = 0;
    this.chatRecoveryInFlight = false;
    if (this.uncertainChatTimer) window.clearTimeout(this.uncertainChatTimer);
    this.uncertainChatTimer = 0;
  }

  private chatSubmissionRequest(journal: ChatSubmissionJournal): Record<string, unknown> {
    return {
      projectId: this.config.projectId,
      ...(journal.threadId ? { threadId: journal.threadId } : {}),
      ...(journal.messageId ? { messageId: journal.messageId } : {}),
      clientMessageId: journal.clientMessageId,
      ...(journal.operation !== "chat/message/regenerate" ? {
        text: journal.text,
        attachments: journal.attachments,
        tools: journal.tools,
      } : {}),
    };
  }

  private optimisticChatMessage(clientMessageId: string): HTMLElement | null {
    return Array.from(canvasFor("chat").querySelectorAll<HTMLElement>(".user-message[data-client-message-id]"))
      .find((message) => message.dataset.clientMessageId === clientMessageId) || null;
  }

  private clearRestoredChatComposer(journal: ChatSubmissionJournal): void {
    const prompt = document.querySelector<HTMLTextAreaElement>("#chat-prompt");
    if (!prompt) return;
    if (journal.text && prompt.value.trim() === journal.text.trim()) prompt.value = "";
    if (prompt.dataset.editClientMessageId === journal.clientMessageId) {
      delete prompt.dataset.editClientMessageId;
      delete prompt.dataset.editMessageId;
    }
    prompt.dispatchEvent(new Event("input", { bubbles: true }));
  }

  private clearChatActionRecovery(journal: ChatSubmissionJournal): void {
    if (journal.operation !== "chat/message/regenerate") return;
    const button = document.querySelector<HTMLButtonElement>(`button[data-client-request-id='${CSS.escape(journal.clientMessageId)}']`);
    if (!button) return;
    delete button.dataset.clientRequestId;
    button.disabled = false;
    button.removeAttribute("aria-busy");
  }

  private applyChatSubmissionResult(journal: ChatSubmissionJournal, result: Record<string, unknown>): void {
    const optimisticMessage = this.optimisticChatMessage(journal.clientMessageId);
    if (optimisticMessage?.dataset.optimistic && optimisticMessage.dataset.optimistic !== "confirmed") {
      updateOptimisticMessage(optimisticMessage, "rpcAccepted");
    }
    const threadId = typeof result.threadId === "string" ? result.threadId : journal.threadId || this.activeThread.get("chat");
    if (!threadId) {
      const error = new Error("聊天请求可能已被接收，但服务器没有返回会话标识") as Error & { retryable?: boolean };
      error.retryable = true;
      throw error;
    }
    this.setActiveThread("chat", threadId);
    const turnId = resultId(result, "turn") || (typeof result.runId === "string" ? result.runId : undefined);
    if (typeof result.text === "string") {
      this.renderCompletedText("chat", String(result.messageId || crypto.randomUUID()), result.text);
      if (turnId) {
        this.chatControl.submissionAccepted(threadId, turnId);
        this.chatControl.runCompleted(threadId, turnId, "completed");
      } else {
        this.chatControl.submissionCompleted(threadId);
      }
    } else {
      if (!turnId) {
        const error = new Error("聊天请求可能已被接收，但服务器没有返回可停止的生成任务标识") as Error & { retryable?: boolean };
        error.retryable = true;
        throw error;
      }
      this.chatControl.submissionAccepted(threadId, turnId);
    }
    if (journal.operation === "chat/message/edit" && journal.messageId) {
      const editedMessage = canvasFor("chat").querySelector<HTMLElement>(`.user-message[data-message-id='${CSS.escape(journal.messageId)}']`);
      if (editedMessage) {
        const canonicalUserMessageId = workflowIdentifier(result.userMessageId)
          || workflowIdentifier(editedMessage.dataset.messageId)
          || journal.messageId;
        const bubble = editedMessage.querySelector<HTMLElement>(".user-bubble");
        if (bubble) bubble.textContent = journal.text;
        editedMessage.dataset.clientMessageId = journal.clientMessageId;
        this.decorateMessage(editedMessage, "chat", "user", canonicalUserMessageId, journal.text);
        if (optimisticMessage && optimisticMessage !== editedMessage) optimisticMessage.remove();
      }
    }
    this.clearRestoredChatComposer(journal);
    if (journal.operation !== "chat/message/regenerate") {
      this.attachments.set("chat", []);
      this.updateAttachmentChips("chat");
    }
    this.clearChatActionRecovery(journal);
    this.clearChatSubmission(journal.clientMessageId);
  }

  private scheduleUncertainChatResume(delayMs?: number): void {
    const journal = this.uncertainChatSubmission;
    if (!journal || this.uncertainChatTimer || this.chatRecoveryInFlight || this.client.state !== "ready") return;
    if (!this.operationIsAvailable(journal.operation) || !this.operationIsAvailable("gateway/idempotentChatCreate")) return;
    const delay = delayMs ?? Math.min(30_000, 1_500 * 1.8 ** Math.min(this.uncertainChatAttempt, 6));
    this.uncertainChatTimer = window.setTimeout(() => {
      this.uncertainChatTimer = 0;
      void this.resumeUncertainChatSubmission();
    }, delay);
  }

  private async resumeUncertainChatSubmission(): Promise<void> {
    const journal = this.uncertainChatSubmission;
    if (!journal || journal.principalId !== this.serverPrincipalId || this.chatRecoveryInFlight) return;
    if (this.client.state !== "ready"
      || !this.operationIsAvailable(journal.operation)
      || !this.operationIsAvailable("gateway/idempotentChatCreate")) return;
    this.chatRecoveryInFlight = true;
    this.uncertainChatAttempt += 1;
    try {
      const result = await this.client.rpc<Record<string, unknown>>(journal.operation, this.chatSubmissionRequest(journal));
      this.applyChatSubmissionResult(journal, result);
    } catch (error) {
      const current = this.uncertainChatSubmission;
      if (!current || current.clientMessageId !== journal.clientMessageId) return;
      const message = error instanceof Error ? error.message : "聊天请求恢复失败";
      if (requestOutcomeMayExist(error)) {
        this.chatControl.submissionUncertain(message);
        this.scheduleUncertainChatResume();
      } else {
        const optimistic = this.optimisticChatMessage(journal.clientMessageId);
        if (journal.operation === "chat/message/edit") optimistic?.remove();
        else if (optimistic) updateOptimisticMessage(optimistic, "rpcFailed");
        this.clearChatActionRecovery(journal);
        this.clearChatSubmission(journal.clientMessageId);
        this.clearRestoredChatComposer(journal);
        const prompt = document.querySelector<HTMLTextAreaElement>("#chat-prompt");
        if (prompt && journal.text) restoreComposerText(prompt, journal.text);
        this.chatControl.submissionFailed(message);
        toast(message, "error");
      }
    } finally {
      this.chatRecoveryInFlight = false;
      if (this.uncertainChatSubmission) this.scheduleUncertainChatResume();
    }
  }

  private workSubmissionStorageKey(principalId: string): string {
    return `${this.workSubmissionStoragePrefix}:${principalId}`;
  }

  private workSubmissionSettlementKey(principalId: string): string {
    return `${this.workSubmissionStorageKey(principalId)}:settled`;
  }

  private persistWorkSubmission(journal: WorkSubmissionJournal, resetAttempt = true): boolean {
    try {
      const storageKey = this.workSubmissionStorageKey(journal.principalId);
      const serialized = JSON.stringify(journal);
      localStorage.removeItem(this.workSubmissionSettlementKey(journal.principalId));
      localStorage.setItem(storageKey, serialized);
      if (localStorage.getItem(storageKey) !== serialized) return false;
      this.uncertainWorkSubmission = journal;
      if (resetAttempt) this.uncertainWorkAttempt = 0;
      return true;
    } catch {
      return false;
    }
  }

  private workSubmissionLeaseUntil(): string {
    return new Date(Date.now() + this.config.requestTimeoutMs + 2_000).toISOString();
  }

  private workSubmissionLockName(principalId: string): string {
    return `bianhua-work-submission:${this.config.projectId}:${principalId}`;
  }

  private ownsWorkSubmission(journal: WorkSubmissionJournal): boolean {
    return journal.ownerWindowId === this.windowInstanceId;
  }

  private workSubmissionLeaseDelay(journal: WorkSubmissionJournal): number {
    if (!journal.ownerWindowId || this.ownsWorkSubmission(journal)) return 0;
    const leaseUntil = Date.parse(journal.ownerLeaseUntil || "");
    if (!Number.isFinite(leaseUntil)) return 0;
    return Math.max(0, leaseUntil - Date.now());
  }

  private async acquireWorkRecoveryOwnership(expected: WorkSubmissionJournal): Promise<WorkSubmissionJournal | null> {
    const claim = () => {
      const latest = this.readWorkSubmission(expected.principalId);
      if (!latest || latest.clientMessageId !== expected.clientMessageId) return null;
      if (this.ownsWorkSubmission(latest)) {
        const renewed = { ...latest, ownerLeaseUntil: this.workSubmissionLeaseUntil() };
        return this.persistWorkSubmission(renewed, false) ? renewed : null;
      }
      if (this.workSubmissionLeaseDelay(latest) > 0) {
        this.adoptWorkSubmission(latest);
        return null;
      }
      const claimed: WorkSubmissionJournal = {
        ...latest,
        ownerWindowId: this.windowInstanceId,
        ownerLeaseUntil: this.workSubmissionLeaseUntil(),
      };
      return this.persistWorkSubmission(claimed, false) ? claimed : null;
    };
    const lockManager = navigator.locks;
    if (!lockManager) return claim();
    return lockManager.request(
      this.workSubmissionLockName(expected.principalId),
      { mode: "exclusive" },
      claim,
    );
  }

  private async updateOwnedWorkSubmission(
    expected: WorkSubmissionJournal,
    update: (latest: WorkSubmissionJournal) => WorkSubmissionJournal,
  ): Promise<WorkSubmissionJournal | null> {
    const apply = () => {
      const latest = this.readWorkSubmission(expected.principalId);
      if (!latest
        || latest.clientMessageId !== expected.clientMessageId
        || latest.ownerWindowId !== this.windowInstanceId) return null;
      const next = update(latest);
      return this.persistWorkSubmission(next, false) ? next : null;
    };
    const lockManager = navigator.locks;
    if (!lockManager) return apply();
    return lockManager.request(
      this.workSubmissionLockName(expected.principalId),
      { mode: "exclusive" },
      apply,
    );
  }

  private async claimWorkSubmission(journal: WorkSubmissionJournal): Promise<boolean> {
    const claim = () => {
      const existing = this.readWorkSubmission(journal.principalId);
      if (existing && existing.clientMessageId !== journal.clientMessageId) {
        this.adoptWorkSubmission(existing);
        return false;
      }
      return this.persistWorkSubmission(journal);
    };
    const lockManager = navigator.locks;
    if (!lockManager) return claim();
    return lockManager.request(
      this.workSubmissionLockName(journal.principalId),
      { mode: "exclusive" },
      claim,
    );
  }

  private parseWorkInputs(value: unknown, fallbackText: string): WorkInput[] {
    if (!Array.isArray(value)) return fallbackText.trim() ? [{ type: "text", text: fallbackText }] : [];
    const inputs: WorkInput[] = [];
    for (const candidate of value) {
      if (!candidate || typeof candidate !== "object") continue;
      const item = candidate as Record<string, unknown>;
      const type = String(item.type || "");
      if (type === "text" && typeof item.text === "string" && item.text.trim()) {
        inputs.push({ type: "text", text: item.text });
      } else if (type === "localImage" && typeof item.path === "string") {
        inputs.push({ type: "localImage", path: item.path, detail: null });
      } else if (type === "mention" && typeof item.name === "string" && typeof item.path === "string") {
        inputs.push({ type: "mention", name: item.name, path: item.path });
      } else if (type === "skill" && typeof item.name === "string" && typeof item.path === "string") {
        inputs.push({ type: "skill", name: item.name, path: item.path });
      } else if (type === "image" && typeof item.url === "string") {
        inputs.push({ type: "image", url: item.url, detail: null });
      }
    }
    if (inputs.some((input) => input.type === "text")) return inputs;
    return fallbackText.trim() ? [{ type: "text", text: fallbackText }, ...inputs] : inputs;
  }

  private readWorkSubmission(principalId: string): WorkSubmissionJournal | null {
    const storageKey = this.workSubmissionStorageKey(principalId);
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || "null") as Record<string, unknown> | null;
      if (!parsed) return null;
      const handoffId = parsed.handoffId === undefined ? undefined : workflowIdentifier(parsed.handoffId);
      if (parsed.handoffId !== undefined && !handoffId) throw new Error("invalid work handoff journal");
      const version = parsed.version === 2 ? 2 : 1;
      const text = String(parsed.text || "");
      const threadId = workflowIdentifier(parsed.threadId);
      const rpcRequestId = workflowIdentifier(parsed.rpcRequestId);
      const threadStartRequestId = workflowIdentifier(parsed.threadStartRequestId);
      const expectedTurnId = workflowIdentifier(parsed.expectedTurnId);
      const ownerWindowId = workflowIdentifier(parsed.ownerWindowId);
      const ownerLeaseUntil = typeof parsed.ownerLeaseUntil === "string" && Number.isFinite(Date.parse(parsed.ownerLeaseUntil))
        ? parsed.ownerLeaseUntil
        : undefined;
      const journal: WorkSubmissionJournal = {
        version,
        principalId: String(parsed.principalId || ""),
        ...(threadId ? { threadId } : {}),
        clientMessageId: String(parsed.clientMessageId || ""),
        text,
        input: this.parseWorkInputs(parsed.input, text),
        intent: parsed.intent === "steer" ? "steer" : "start",
        createdAt: String(parsed.createdAt || ""),
        ...(rpcRequestId ? { rpcRequestId } : {}),
        ...(threadStartRequestId ? { threadStartRequestId } : {}),
        ...(expectedTurnId ? { expectedTurnId } : {}),
        ...(ownerWindowId ? { ownerWindowId } : {}),
        ...(ownerLeaseUntil ? { ownerLeaseUntil } : {}),
        ...(parsed.authoritativeOutcomeUnknown === true ? { authoritativeOutcomeUnknown: true } : {}),
        ...(typeof parsed.model === "string" && parsed.model ? { model: parsed.model } : {}),
        ...(typeof parsed.effort === "string" && parsed.effort ? { effort: parsed.effort } : {}),
        executionPolicy: normalizeWorkExecutionPolicy(parsed.executionPolicy, parsed.readOnly === true),
        ...(handoffId ? { handoffId } : {}),
      };
      if (
        journal.principalId !== principalId
        || !journal.clientMessageId
        || (!journal.text.trim() && !journal.input.some((input) => input.type === "localImage" || input.type === "mention"))
        || Number.isNaN(Date.parse(journal.createdAt))
        || (version === 1 && !journal.threadId)
        || (version === 2 && (!journal.rpcRequestId || (!journal.threadId && !journal.threadStartRequestId)))
        || (version === 2 && journal.intent === "steer" && (!journal.threadId || !journal.expectedTurnId))
      ) throw new Error("invalid work journal");
      return journal;
    } catch {
      try { localStorage.removeItem(storageKey); } catch { /* no-op */ }
      return null;
    }
  }

  private readWorkSubmissionSettlement(principalId: string): WorkSubmissionSettlement | null {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.workSubmissionSettlementKey(principalId)) || "null") as Record<string, unknown> | null;
      if (!parsed) return null;
      const outcome = parsed.outcome;
      if (!parsed.clientMessageId || !["confirmed", "failed", "abandoned"].includes(String(outcome))) return null;
      return {
        clientMessageId: String(parsed.clientMessageId),
        outcome: outcome as WorkSubmissionSettlement["outcome"],
        settledAt: String(parsed.settledAt || ""),
      };
    } catch {
      return null;
    }
  }

  private clearWorkSubmission(
    clientMessageId?: string,
    outcome: WorkSubmissionSettlement["outcome"] = "confirmed",
  ): void {
    const journal = this.uncertainWorkSubmission;
    if (!journal || (clientMessageId && journal.clientMessageId !== clientMessageId)) return;
    const settlement: WorkSubmissionSettlement = {
      clientMessageId: journal.clientMessageId,
      outcome,
      settledAt: new Date().toISOString(),
    };
    this.uncertainWorkSubmission = null;
    this.uncertainWorkAttempt = 0;
    if (this.uncertainWorkTimer) window.clearTimeout(this.uncertainWorkTimer);
    this.uncertainWorkTimer = 0;
    try {
      localStorage.setItem(this.workSubmissionSettlementKey(journal.principalId), JSON.stringify(settlement));
      const stored = this.readWorkSubmission(journal.principalId);
      if (!stored || stored.clientMessageId === journal.clientMessageId) {
        localStorage.removeItem(this.workSubmissionStorageKey(journal.principalId));
      }
    } catch { /* no-op */ }
  }

  private dropWorkSubmissionMemory(): void {
    this.uncertainWorkSubmission = null;
    this.uncertainWorkAttempt = 0;
    if (this.uncertainWorkTimer) window.clearTimeout(this.uncertainWorkTimer);
    this.uncertainWorkTimer = 0;
  }

  private adoptWorkSubmission(journal: WorkSubmissionJournal): void {
    this.dropWorkSubmissionMemory();
    this.uncertainWorkSubmission = journal;
    const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
    if (prompt && !prompt.value.trim()) restoreComposerText(prompt, journal.text);
    if (journal.threadId) {
      this.setActiveThread("work", journal.threadId);
      this.workControl.beginResync(journal.threadId);
    }
    this.scheduleUncertainWorkResume(0);
  }

  private onWorkSubmissionStorage(event: StorageEvent): void {
    const principalId = this.serverPrincipalId;
    if (!principalId || event.key !== this.workSubmissionStorageKey(principalId)) return;
    if (event.newValue) {
      const journal = this.readWorkSubmission(principalId);
      if (journal) this.adoptWorkSubmission(journal);
      return;
    }
    const journal = this.uncertainWorkSubmission;
    if (!journal) return;
    const settlement = this.readWorkSubmissionSettlement(principalId);
    this.dropWorkSubmissionMemory();
    if (settlement?.clientMessageId === journal.clientMessageId && settlement.outcome === "confirmed") {
      this.clearRestoredWorkPrompt(journal);
      this.attachments.set("work", []);
      this.updateAttachmentChips("work");
    }
    if (journal.threadId && this.client.state === "ready") {
      this.workControl.beginResync(journal.threadId);
      void this.openThread("work", journal.threadId, undefined, false);
    } else if (journal.threadId) {
      this.workControl.abandonResync(journal.threadId);
    }
    this.renderWorkControl(this.workControl.snapshot());
  }

  private scheduleUncertainWorkResume(delayMs?: number): void {
    const journal = this.uncertainWorkSubmission;
    if (!journal || this.uncertainWorkTimer || this.client.state !== "ready") return;
    const ownerDelay = journal.version === 2 ? this.workSubmissionLeaseDelay(journal) : 0;
    const retryDelay = delayMs ?? Math.min(30_000, 1_500 * 1.8 ** Math.min(this.uncertainWorkAttempt, 6));
    const delay = ownerDelay > 0 ? ownerDelay + 50 : retryDelay;
    this.uncertainWorkTimer = window.setTimeout(() => {
      this.uncertainWorkTimer = 0;
      const current = this.uncertainWorkSubmission;
      if (!current || current.principalId !== this.serverPrincipalId) return;
      this.uncertainWorkAttempt += 1;
      void this.recoverUncertainWorkSubmission(current);
    }, delay);
  }

  private async retryUncertainWorkRecovery(): Promise<void> {
    const journal = this.uncertainWorkSubmission;
    if (!journal || journal.principalId !== this.serverPrincipalId || this.client.state !== "ready") return;
    await this.recoverUncertainWorkSubmission(journal);
  }

  private workSubmissionRequest(journal: WorkSubmissionJournal): ReturnType<typeof buildWorkSubmission> | null {
    if (!journal.threadId) return null;
    return buildWorkSubmission({
      threadId: journal.threadId,
      activeTurnId: journal.intent === "steer" ? journal.expectedTurnId || null : null,
      cwd: this.config.workspacePath,
      input: journal.input,
      clientUserMessageId: journal.clientMessageId,
      ...(journal.model ? { model: journal.model } : {}),
      ...(journal.effort ? { effort: journal.effort } : {}),
      executionPolicy: journal.executionPolicy,
    });
  }

  private async recoverUncertainWorkSubmission(expected: WorkSubmissionJournal): Promise<void> {
    if (this.workRecoveryInFlight || this.client.state !== "ready") return;
    const current = this.uncertainWorkSubmission;
    if (!current || current.clientMessageId !== expected.clientMessageId || current.principalId !== this.serverPrincipalId) return;
    this.workRecoveryInFlight = true;
    try {
      let journal = current;
      if (journal.version === 2 && journal.rpcRequestId) {
        const owned = await this.acquireWorkRecoveryOwnership(journal);
        if (!owned) return;
        journal = owned;
        if (!journal.threadId) {
          if (!journal.threadStartRequestId) throw new Error("工作任务恢复记录缺少线程请求标识");
          const started = await this.client.rpc<Record<string, unknown>>("thread/start", {
            cwd: this.config.workspacePath,
            ...(journal.model ? { model: journal.model } : {}),
            ...threadExecutionPolicyParams(journal.executionPolicy),
          }, { requestId: journal.threadStartRequestId });
          const threadId = resultId(started, "thread");
          if (!threadId) throw new Error("服务器没有返回工作线程标识");
          const latest = this.uncertainWorkSubmission;
          if (!latest || latest.clientMessageId !== journal.clientMessageId) return;
          const updatedJournal = await this.updateOwnedWorkSubmission(journal, (latest) => ({
            ...latest,
            threadId,
            ownerLeaseUntil: this.workSubmissionLeaseUntil(),
          }));
          if (!updatedJournal) return;
          journal = updatedJournal;
          this.setActiveThread("work", threadId);
        }
        const request = this.workSubmissionRequest(journal);
        if (!request || request.intent !== journal.intent) throw new Error("工作指令恢复记录与请求不一致");
        this.workControl.beginResync(journal.threadId!);
        const result = await this.client.rpc<Record<string, unknown>>(
          request.method,
          request.params,
          { requestId: journal.rpcRequestId! },
        );
        const returnedTurnId = resultId(result, "turn");
        if (!returnedTurnId) throw new Error("服务器没有返回任务标识");
        if (journal.intent === "steer" && returnedTurnId !== journal.expectedTurnId) {
          throw new Error("服务器返回的任务标识与待恢复任务不一致");
        }
        this.workControl.submissionAccepted(journal.threadId!, returnedTurnId, journal.intent);
        this.scheduleWorkTerminalReconcile(journal.threadId!, 1_200);
        this.confirmUncertainWorkSubmission(journal);
        return;
      }
      if (!journal.threadId) {
        restoreComposerText(document.querySelector<HTMLTextAreaElement>("#work-prompt")!, journal.text);
        this.clearWorkSubmission(journal.clientMessageId, "failed");
        toast("旧工作指令没有可恢复的服务器任务，已自动解除锁定", "error");
        return;
      }
      this.setActiveThread("work", journal.threadId);
      this.workControl.beginResync(journal.threadId);
      await this.openThread("work", journal.threadId, undefined, false);
    } catch (error) {
      let latest = this.uncertainWorkSubmission;
      if (!latest || latest.clientMessageId !== expected.clientMessageId) return;
      const message = error instanceof Error ? error.message : "工作指令恢复失败";
      if (requestOutcomeMayExist(error)) {
        if (authoritativeMutationOutcomeUnknown(error)) {
          const updated = await this.updateOwnedWorkSubmission(latest, (currentJournal) => ({
            ...currentJournal,
            authoritativeOutcomeUnknown: true,
          }));
          if (!updated) return;
          latest = updated;
          if (!latest.threadId) {
            const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
            if (prompt) restoreComposerText(prompt, latest.text);
            this.clearWorkSubmission(latest.clientMessageId, "failed");
            this.scheduleThreadListRefresh("work", 0);
            toast("服务器重启时新任务标识尚未确认，已自动解除锁定；原内容已保留", "error");
            return;
          }
        }
        if (latest.threadId) {
          this.workControl.beginResync(latest.threadId);
          await this.openThread("work", latest.threadId, undefined, false);
        }
      } else {
        const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
        if (prompt) restoreComposerText(prompt, latest.text);
        this.clearWorkSubmission(latest.clientMessageId, "failed");
        if (latest.threadId) this.workControl.submissionFailed(latest.threadId, message);
        toast(`上一条工作指令未被服务器接受，已自动解除锁定：${message}`, "error");
      }
    } finally {
      this.workRecoveryInFlight = false;
      if (this.uncertainWorkSubmission) this.scheduleUncertainWorkResume();
    }
  }

  private confirmAbandonUncertainWorkRecovery(): void {
    const journal = this.uncertainWorkSubmission;
    if (!journal) return;
    const dialog = this.createInfoDialog(
      "解除结果待确认状态？",
      "这只会解除客户端的发送锁定，不会停止服务器上可能仍在执行的旧任务。原内容会保留在输入框中；再次发送前请先核对左侧任务记录，避免重复操作。",
    );
    const actions = dialog.querySelector<HTMLElement>(".runtime-dialog-actions");
    const close = actions?.querySelector<HTMLButtonElement>(".runtime-secondary");
    if (close) close.textContent = "继续等待";
    const unlock = document.createElement("button");
    unlock.type = "button";
    unlock.className = "runtime-danger";
    unlock.textContent = "解除锁定";
    unlock.addEventListener("click", () => {
      if (this.uncertainWorkSubmission?.clientMessageId !== journal.clientMessageId) {
        dialog.close();
        return;
      }
      this.clearWorkSubmission(journal.clientMessageId, "abandoned");
      if (journal.threadId) this.workControl.abandonResync(journal.threadId);
      dialog.close();
      toast("已解除发送锁定；发送前请先核对服务器任务记录", "success");
    });
    actions?.append(unlock);
    dialog.showModal();
    unlock.focus();
  }

  private clearRestoredWorkPrompt(journal: WorkSubmissionJournal): void {
    const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
    if (prompt && prompt.value.trim() === journal.text.trim()) {
      prompt.value = "";
      prompt.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  private confirmUncertainWorkSubmission(journal: WorkSubmissionJournal): void {
    if (journal.handoffId && !this.completeWorkflowHandoff(journal.handoffId)) {
      this.scheduleUncertainWorkResume();
      return;
    }
    this.clearRestoredWorkPrompt(journal);
    this.attachments.set("work", []);
    this.updateAttachmentChips("work");
    this.clearWorkSubmission(journal.clientMessageId, "confirmed");
    if (journal.threadId) this.workControl.submissionConfirmed(journal.threadId);
    // submissionAccepted may already have cleared `submissionPending`, in
    // which case submissionConfirmed intentionally emits nothing. The journal
    // itself also controls whether recovery actions are visible, so repaint
    // after clearing it even when the state-store snapshot did not change.
    this.renderWorkControl(this.workControl.snapshot());
  }

  private workThreadStorageKey(principalId: string): string {
    return `${this.workThreadStoragePrefix}:${principalId}`;
  }

  private workModelStorageKey(principalId: string): string {
    return `${this.workModelStoragePrefix}:${principalId}`;
  }

  private workExecutionPolicyStorageKey(principalId: string): string {
    return `${this.workExecutionPolicyStoragePrefix}:${principalId}`;
  }

  private readWorkExecutionPolicy(principalId: string | null): WorkExecutionPolicy {
    if (!principalId) return "workspace";
    try {
      const storageKey = this.workExecutionPolicyStorageKey(principalId);
      const stored = localStorage.getItem(storageKey);
      const policy = normalizeWorkExecutionPolicy(stored);
      if (stored === "auto") localStorage.setItem(storageKey, policy);
      return policy;
    } catch {
      return "workspace";
    }
  }

  private persistWorkExecutionPolicy(policy: WorkExecutionPolicy): boolean {
    if (!this.serverPrincipalId) return false;
    try {
      localStorage.setItem(this.workExecutionPolicyStorageKey(this.serverPrincipalId), policy);
      this.selectedWorkExecutionPolicy = policy;
      this.renderWorkExecutionPolicy();
      return true;
    } catch {
      return false;
    }
  }

  private renderWorkExecutionPolicy(): void {
    const label = document.querySelector<HTMLElement>("[data-app-action='permissions'] span:not(.permission-dot)");
    if (label) label.textContent = `远程 · ${WORK_EXECUTION_POLICY_LABELS[this.selectedWorkExecutionPolicy]}`;
  }

  private readWorkModelSelection(principalId: string | null): WorkModelSelection | null {
    if (!principalId) return null;
    try {
      const parsed = JSON.parse(localStorage.getItem(this.workModelStorageKey(principalId)) || "null") as Partial<WorkModelSelection> | null;
      if (!parsed || typeof parsed.model !== "string" || !parsed.model) return null;
      return {
        model: parsed.model,
        displayName: typeof parsed.displayName === "string" && parsed.displayName ? parsed.displayName : parsed.model,
        effort: typeof parsed.effort === "string" && parsed.effort ? parsed.effort : null,
      };
    } catch {
      return null;
    }
  }

  private persistWorkModelSelection(selection: WorkModelSelection): boolean {
    if (!this.serverPrincipalId) return false;
    try {
      localStorage.setItem(this.workModelStorageKey(this.serverPrincipalId), JSON.stringify(selection));
      this.selectedWorkModel = selection;
      this.renderWorkModelSelection();
      return true;
    } catch {
      return false;
    }
  }

  private renderWorkModelSelection(): void {
    const label = document.querySelector<HTMLElement>("[data-app-action='models'] span");
    if (!label) return;
    label.textContent = this.selectedWorkModel
      ? `${this.selectedWorkModel.displayName}${this.selectedWorkModel.effort ? ` · ${reasoningEffortLabel(this.selectedWorkModel.effort)}` : ""}`
      : "服务器模型";
  }

  private setActiveThread(surface: Surface, threadId: string | null): void {
    const previousThreadId = this.activeThread.get(surface) || null;
    if (surface === "chat" && previousThreadId !== threadId) {
      const prompt = document.querySelector<HTMLTextAreaElement>("#chat-prompt");
      if (prompt) {
        delete prompt.dataset.editMessageId;
        delete prompt.dataset.editClientMessageId;
      }
    }
    if (threadId) this.activeThread.set(surface, threadId);
    else this.activeThread.delete(surface);
    this.syncThreadListSelection(surface);
    this.refreshStaticActionAvailability();
    if (surface === "chat") {
      if (!this.serverPrincipalId) return;
      try {
        const storageKey = `${this.chatThreadStoragePrefix}:${this.serverPrincipalId}`;
        if (threadId) localStorage.setItem(storageKey, threadId);
        else localStorage.removeItem(storageKey);
      } catch { /* in-memory selection remains usable */ }
      return;
    }
    if (surface !== "work") return;
    this.workControl.selectThread(threadId);
    if (!this.serverPrincipalId) return;
    try {
      const storageKey = this.workThreadStorageKey(this.serverPrincipalId);
      if (threadId) localStorage.setItem(storageKey, threadId);
      else localStorage.removeItem(storageKey);
    } catch { /* in-memory selection remains usable */ }
  }

  private onPrincipalWelcome(payload: Record<string, unknown>): void {
    const nextPrincipalId = typeof payload.principalId === "string" && payload.principalId.length >= 16
      ? payload.principalId
      : null;
    const previousPrincipalId = this.serverPrincipalId;
    if (previousPrincipalId && previousPrincipalId !== nextPrincipalId) {
      window.dispatchEvent(new CustomEvent("bianhua:principal-reset", {
        detail: { reason: "authenticated-principal-changed" },
      }));
    }
    if (previousPrincipalId && previousPrincipalId !== nextPrincipalId) {
      this.artifactReceiver.resetForPrincipalChange();
      this.workArtifactLocalPaths.clear();
      this.workArtifactInFlight.clear();
      this.pendingRecoveredHandoffIds.clear();
      this.recoveringWorkflowHandoffIds.clear();
      this.clearRecoveredHandoffRetry();
      this.submittedVideoHandoffIds.clear();
      this.clearWorkflowDraftTimers();
      for (const [handoffId, submittedPrincipalId] of this.submittedVideoHandoffPrincipals) {
        if (submittedPrincipalId !== nextPrincipalId) this.submittedVideoHandoffPrincipals.delete(handoffId);
      }
      for (const [handoffId, accepted] of this.acceptedVideoHandoffs) {
        if (accepted.principalId !== nextPrincipalId) this.acceptedVideoHandoffs.delete(handoffId);
      }
      this.setHandoffMaterializationCapability(false);
      for (const surface of ["chat", "work"] as const) {
        this.threadOpenEpoch.set(surface, (this.threadOpenEpoch.get(surface) || 0) + 1);
        for (const draft of this.attachmentDrafts.get(surface)?.values() || []) {
          if (draft.localPreviewUrl) URL.revokeObjectURL(draft.localPreviewUrl);
          if (draft.remoteUploadId) this.cancelUploadedAttachment(draft.remoteUploadId);
        }
        this.attachmentDrafts.get(surface)?.clear();
        for (const attachment of this.attachments.get(surface) || []) {
          if (attachment.localPreviewUrl) URL.revokeObjectURL(attachment.localPreviewUrl);
        }
        this.attachments.set(surface, []);
        this.updateAttachmentChips(surface);
        const prompt = document.querySelector<HTMLTextAreaElement>(surface === "work" ? "#work-prompt" : "#chat-prompt");
        if (prompt) {
          prompt.value = "";
          if (surface === "chat") {
            delete prompt.dataset.editMessageId;
            delete prompt.dataset.editClientMessageId;
          }
          prompt.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
      this.threadLoading.clear();
      this.workRequestInFlight = false;
      this.activeThread.clear();
      this.threadHistoryCache.clear();
      this.threadHistoryCacheUpdatedAt.clear();
      this.dirtyThreadHistories.clear();
      this.renderers.clear();
      this.workControl.selectThread(null);
      this.chatControl.reset();
      this.workFollowUpQueues.clear();
      for (const timer of this.workFollowUpRetryTimers.values()) window.clearTimeout(timer);
      this.workFollowUpRetryTimers.clear();
      this.workFollowUpRetryAttempts.clear();
      for (const timer of this.workTerminalReconcileTimers.values()) window.clearTimeout(timer);
      this.workTerminalReconcileTimers.clear();
      this.workTerminalReconcileAttempts.clear();
      this.dropChatSubmissionMemory();
      this.dropWorkSubmissionMemory();
      this.resetElicitations();
      this.prepareRuntimeShell();
      this.resetCapabilities("正在同步新账户能力");
    }
    this.serverPrincipalId = nextPrincipalId;
    if (previousPrincipalId !== nextPrincipalId) this.loadWorkArtifactPaths(nextPrincipalId);
    this.selectedWorkModel = this.readWorkModelSelection(nextPrincipalId);
    this.selectedWorkExecutionPolicy = this.readWorkExecutionPolicy(nextPrincipalId);
    this.renderWorkModelSelection();
    this.renderWorkExecutionPolicy();
    if (previousPrincipalId !== nextPrincipalId && nextPrincipalId && this.client.state === "ready") {
      queueMicrotask(() => {
        if (this.serverPrincipalId === nextPrincipalId && this.client.state === "ready") {
          void this.onGatewayReady();
        }
      });
    }
    this.workFollowUpBehavior = this.readWorkFollowUpBehavior(nextPrincipalId);
    this.composerSubmitShortcut = this.readComposerSubmitShortcut(nextPrincipalId);
    this.renderWorkControl(this.workControl.snapshot());
    if (previousPrincipalId !== nextPrincipalId || !this.workflowHandoffs) {
      this.installWorkflowHandoffs(nextPrincipalId);
    }
    if (!nextPrincipalId) return;
    if (!this.uncertainChatSubmission) {
      this.uncertainChatSubmission = this.readChatSubmission(nextPrincipalId);
    }
    if (this.uncertainChatSubmission) {
      const journal = this.uncertainChatSubmission;
      if (journal.threadId) this.setActiveThread("chat", journal.threadId);
      const prompt = document.querySelector<HTMLTextAreaElement>("#chat-prompt");
      if (prompt) {
        if (!prompt.value.trim() && journal.text) prompt.value = journal.text;
        if (journal.operation === "chat/message/edit" && journal.messageId) {
          prompt.dataset.editMessageId = journal.messageId;
          prompt.dataset.editClientMessageId = journal.clientMessageId;
        }
        prompt.dispatchEvent(new Event("input", { bubbles: true }));
      }
      if (journal.operation !== "chat/message/regenerate") {
        this.attachments.set("chat", journal.attachments);
        this.updateAttachmentChips("chat");
        if (!this.optimisticChatMessage(journal.clientMessageId)) {
          const optimistic = renderUserMessage("chat", journal.text, {
            clientMessageId: journal.clientMessageId,
            intent: "start",
          });
          renderMessageAttachments(optimistic, journal.attachments);
          if (journal.messageId) optimistic.dataset.editedMessageId = journal.messageId;
          const delivery = optimistic.querySelector<HTMLElement>(".runtime-delivery-state");
          if (delivery) delivery.textContent = "结果待确认 · 正在用同一请求恢复";
        }
      }
      this.chatControl.submissionUncertain("正在恢复上次结果未知的聊天请求");
    }
    if (!this.activeThread.get("chat")) {
      try {
        const restoredChatThreadId = localStorage.getItem(`${this.chatThreadStoragePrefix}:${nextPrincipalId}`) || "";
        if (/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(restoredChatThreadId)) this.setActiveThread("chat", restoredChatThreadId);
      } catch { /* no persisted chat to resume */ }
    }
    if (!this.uncertainWorkSubmission) {
      this.uncertainWorkSubmission = this.readWorkSubmission(nextPrincipalId);
    }
    if (this.uncertainWorkSubmission) {
      const journal = this.uncertainWorkSubmission;
      if (journal.threadId) {
        this.setActiveThread("work", journal.threadId);
        this.workControl.beginResync(journal.threadId);
      }
      const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
      if (prompt && !prompt.value.trim()) {
        prompt.value = journal.text;
        prompt.dispatchEvent(new Event("input", { bubbles: true }));
      }
      if (!canvasFor("work").querySelector(`[data-client-message-id='${CSS.escape(journal.clientMessageId)}']`)) {
        const optimistic = renderUserMessage("work", journal.text, {
          clientMessageId: journal.clientMessageId,
          intent: journal.intent,
          createdAt: journal.createdAt,
        });
        renderMessageAttachments(optimistic, messageAttachmentsFromInputs(journal.input));
        const delivery = optimistic.querySelector<HTMLElement>(".runtime-delivery-state");
        if (delivery) delivery.textContent = "结果待确认 · 正在用同一请求恢复";
      }
      this.scheduleUncertainWorkResume(0);
      return;
    }
    if (this.activeThread.get("work")) return;
    try {
      const restoredThreadId = localStorage.getItem(this.workThreadStorageKey(nextPrincipalId)) || "";
      if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(restoredThreadId)) return;
      this.setActiveThread("work", restoredThreadId);
      this.workControl.beginResync(restoredThreadId);
      queueMicrotask(() => void this.openThread("work", restoredThreadId, undefined, false));
    } catch { /* no persisted thread to resume */ }
  }

  private onEnvelope(envelope: SessionEnvelope): void {
    const payload = envelope.payload as Record<string, unknown>;
    if (envelope.kind === "connection.welcome") this.onPrincipalWelcome(payload);
    if (envelope.kind === "domain.event") this.onDomainEvent(payload);
    if (envelope.kind === "capabilities.snapshot") this.applyCapabilities(payload);
    if (["artifact.manifest", "artifact.chunk", "artifact.url"].includes(envelope.kind)) this.artifactReceiver.handle(envelope);
    if (envelope.kind === "elicitation.request") this.enqueueElicitation(String(payload.requestId), payload);
  }

  private onDomainEvent(payload: Record<string, unknown>): void {
    const topic = String(payload.topic || payload.type || "");
    const data = (payload.data && typeof payload.data === "object" ? payload.data : payload) as Record<string, unknown>;
    if (topic === "client.update.available") {
      window.dispatchEvent(new CustomEvent("bianhua:client-update-available", { detail: data }));
      return;
    }
    const nestedItem = data.item && typeof data.item === "object" ? data.item as Record<string, unknown> : undefined;
    const surface: Surface = data.surface === "chat" || topic.startsWith("chat.")
      ? "chat"
      : data.surface === "work" || topic.startsWith("codex.") || topic.startsWith("turn.") || topic.startsWith("turn/") || topic.startsWith("thread/") || topic.startsWith("item/")
        ? "work"
        : document.body.dataset.mode === "work" ? "work" : "chat";
    if (this.applyChatControlEvent(topic, data)) return;
    this.workControl.applyEvent(topic, data);
    const eventThreadId = domainEventThreadId(data, nestedItem);
    if (eventThreadId) this.dirtyThreadHistories.add(`${surface}:${eventThreadId}`);
    const selectedThreadId = this.activeThread.get(surface) || "";
    const canRenderEvent = !eventThreadId || !selectedThreadId || eventThreadId === selectedThreadId;
    const itemId = String(nestedItem?.id || data.itemId || data.messageId || data.turnId || "current");
    if (["message.started", "chat.message.started", "codex.item.started", "codex.agent.started", "item/started"].includes(topic)) {
      if (!canRenderEvent) return;
      if (String(nestedItem?.type || "") !== "userMessage" && !this.renderers.has(itemId)) {
        const renderer = createAssistantMessage(surface);
        this.renderers.set(itemId, renderer);
        if ([
          "commandExecution",
          "fileChange",
          "mcpToolCall",
          "dynamicToolCall",
          "collabToolCall",
          "collabAgentToolCall",
          "imageGeneration",
          "webSearch",
          "imageView",
          "enteredReviewMode",
          "exitedReviewMode",
          "contextCompaction",
        ].includes(String(nestedItem?.type || ""))) {
          renderStructuredItem(renderer, nestedItem!);
        }
      }
    } else if ([
      "message.delta",
      "chat.message.delta",
      "codex.agentMessage.delta",
      "codex.reasoning.delta",
      "codex.plan.delta",
      "item/agentMessage/delta",
      "item/reasoning/textDelta",
      "item/reasoning/summaryTextDelta",
      "item/plan/delta",
    ].includes(topic)) {
      if (!canRenderEvent) return;
      const renderer = this.renderers.get(itemId) || createAssistantMessage(surface, topic.includes("reasoning") ? "正在推理" : "正在执行");
      this.renderers.set(itemId, renderer);
      renderer.text += String(data.delta || data.text || "");
      if (topic.includes("reasoning")) {
        renderExpandableItem(
          renderer,
          `正在推理 · ${lastReadableLine(renderer.text, "正在分析目标与上下文")}`,
          renderer.text,
          { kind: "reasoning", running: true },
        );
        renderer.status.textContent = "正在推理";
      } else {
        renderRichText(renderer.body, renderer.text);
        renderer.status.textContent = "正在执行";
      }
      scrollConversation(surface);
    } else if (["message.completed", "chat.message.completed", "codex.item.completed", "codex.agentMessage.completed", "item/completed"].includes(topic)) {
      if (!canRenderEvent) return;
      const item = nestedItem;
      if (String(item?.type || "") === "userMessage") {
        const userText = textFrom(item);
        const userAttachments = messageAttachmentsFromUserItem(item || {});
        const clientId = String(item?.clientId || item?.clientUserMessageId || "");
        const journal = this.uncertainWorkSubmission;
        if (surface === "work" && clientId && journal?.clientMessageId === clientId) {
          this.confirmUncertainWorkSubmission(journal);
        }
        const byClientId = clientId
          ? Array.from(canvasFor(surface).querySelectorAll<HTMLElement>(".user-message[data-client-message-id]"))
            .find((message) => message.dataset.clientMessageId === clientId) || null
          : null;
        const byMessageId = itemId
          ? canvasFor(surface).querySelector<HTMLElement>(`.user-message[data-message-id='${CSS.escape(itemId)}']`)
          : null;
        const optimistic = byClientId || byMessageId || Array.from(canvasFor(surface).querySelectorAll<HTMLElement>(".user-message[data-optimistic]:not([data-optimistic='confirmed'])"))
          .find((message) => message.querySelector(".user-bubble")?.textContent === userText);
        if (optimistic) {
          if (optimistic.dataset.optimistic) updateOptimisticMessage(optimistic, "serverConfirmed");
          const editedTargetId = workflowIdentifier(optimistic.dataset.editedMessageId);
          const editedTarget = editedTargetId
            ? canvasFor(surface).querySelector<HTMLElement>(`.user-message[data-message-id='${CSS.escape(editedTargetId)}']`)
            : null;
          if (editedTarget && editedTarget !== optimistic) {
            const bubble = editedTarget.querySelector<HTMLElement>(".user-bubble");
            if (bubble) bubble.textContent = userText;
            if (clientId) editedTarget.dataset.clientMessageId = clientId;
            this.decorateMessage(editedTarget, surface, "user", itemId, userText);
            renderMessageAttachments(editedTarget, userAttachments);
            optimistic.remove();
          } else {
            optimistic.dataset.messageId = itemId;
            this.decorateMessage(optimistic, surface, "user", itemId, userText);
            if (userAttachments.length) renderMessageAttachments(optimistic, userAttachments);
          }
        } else if (userText || userAttachments.length) {
          const rendered = renderUserMessage(surface, userText);
          this.decorateMessage(rendered, surface, "user", itemId, userText);
          renderMessageAttachments(rendered, userAttachments);
        }
        const chatJournal = this.uncertainChatSubmission;
        if (surface === "chat" && clientId && chatJournal?.clientMessageId === clientId) {
          const eventThreadId = workflowIdentifier(data.threadId)
            || workflowIdentifier((data.turn as Record<string, unknown> | undefined)?.threadId);
          if (eventThreadId) this.setActiveThread("chat", eventThreadId);
          this.clearRestoredChatComposer(chatJournal);
          if (chatJournal.operation !== "chat/message/regenerate") {
            this.attachments.set("chat", []);
            this.updateAttachmentChips("chat");
          }
          this.clearChatSubmission(clientId);
        }
        this.renderers.delete(itemId);
        return;
      }
      const renderer = this.renderers.get(itemId) || createAssistantMessage(surface);
      if (item && !["agentMessage", "userMessage"].includes(String(item.type))) {
        renderStructuredItem(renderer, item);
      } else {
        const finalText = textFrom(data.text ?? data.content ?? item ?? renderer.text) || "已完成。";
        renderer.text = finalText;
        renderRichText(renderer.body, finalText);
        renderer.status.textContent = "已完成";
        appendMessageHandoffAction(renderer.article, surface, finalText, { itemId });
        this.decorateMessage(renderer.article, surface, "assistant", itemId, finalText);
        if (surface === "work" && String(item?.phase || "") === "final_answer") {
          const threadId = workflowIdentifier(data.threadId)
            || workflowIdentifier((data.turn as Record<string, unknown> | undefined)?.threadId)
            || this.activeThread.get("work");
          if (threadId) this.scheduleWorkTerminalReconcile(threadId, 250);
        }
      }
      this.renderers.delete(itemId);
    } else if ([
      "codex.command.output.delta",
      "command/exec/outputDelta",
      "process/outputDelta",
      "item/commandExecution/outputDelta",
      "item/commandExecution/terminalInteraction",
    ].includes(topic)) {
      if (!canRenderEvent) return;
      const renderer = this.renderers.get(itemId) || createAssistantMessage("work", "后台命令");
      this.renderers.set(itemId, renderer);
      renderer.text += String(data.delta || data.stdin || base64ToText(data.deltaBase64));
      renderExpandableItem(
        renderer,
        `正在执行 · ${lastReadableLine(renderer.text, "等待命令输出")}`,
        renderer.text,
        { kind: "command", running: true },
      );
    } else if (topic === "thread/started") {
      const thread = data.thread && typeof data.thread === "object" ? data.thread as Record<string, unknown> : data;
      const threadId = String(thread.id || data.threadId || "");
      const selectedThreadId = this.activeThread.get("work");
      if (threadId && (!selectedThreadId || selectedThreadId === threadId)) {
        this.setActiveThread("work", threadId);
      }
      if (threadId) {
        this.recentlyStartedWorkThreads.set(threadId, {
          item: { ...thread, id: threadId },
          expiresAt: Date.now() + 10_000,
        });
      }
      this.scheduleThreadListRefresh("work", 80);
    } else if (topic === "thread/name/updated") {
      this.scheduleThreadListRefresh("work", 0);
    } else if (topic === "thread/archived") {
      const thread = data.thread && typeof data.thread === "object" ? data.thread as Record<string, unknown> : data;
      const threadId = String(thread.id || data.threadId || "");
      if (threadId && this.activeThread.get("work") === threadId) {
        this.clearWorkThreadRecoveryState(threadId);
        this.setActiveThread("work", null);
        this.workControl.selectThread(null);
      }
      this.scheduleThreadListRefresh("work", 0);
    } else if (topic === "thread/deleted") {
      const thread = data.thread && typeof data.thread === "object" ? data.thread as Record<string, unknown> : data;
      const threadId = String(thread.id || data.threadId || "");
      if (threadId) {
        this.recentlyStartedWorkThreads.delete(threadId);
        this.clearWorkThreadRecoveryState(threadId);
        void this.removeLocalThreadCache("work", threadId, true);
      }
      if (threadId && this.activeThread.get("work") === threadId) {
        this.setActiveThread("work", null);
        const canvas = canvasFor("work");
        for (const message of canvas.querySelectorAll(".message")) message.remove();
        for (const empty of canvas.querySelectorAll(".runtime-empty-state")) empty.remove();
        const empty = document.createElement("p");
        empty.className = "runtime-empty-state";
        empty.textContent = "任务已在服务器删除。可以新建任务继续工作。";
        canvas.append(empty);
      }
      this.scheduleThreadListRefresh("work", 0);
    } else if (["turn.started", "turn/started", "codex.turn.started"].includes(topic)) {
      const turn = data.turn && typeof data.turn === "object" ? data.turn as Record<string, unknown> : data;
      const threadId = String(data.threadId || turn.threadId || "");
      const selectedThreadId = this.activeThread.get("work");
      if (threadId && (!selectedThreadId || selectedThreadId === threadId)) {
        this.setActiveThread("work", threadId);
      }
    } else if (["turn.completed", "turn/completed", "codex.turn.completed"].includes(topic)) {
      const turn = data.turn && typeof data.turn === "object" ? data.turn as Record<string, unknown> : data;
      const threadId = String(data.threadId || turn.threadId || "");
      const turnId = String(turn.id || data.turnId || "");
      if (threadId) this.clearWorkTerminalReconcile(threadId);
      if (!threadId || threadId === this.activeThread.get("work")) {
        const status = String(turn.status || data.status || "");
        if (status === "interrupted") toast("任务已停止，服务器已确认最终状态", "success");
        if (status === "failed") toast(String((turn.error as Record<string, unknown> | undefined)?.message || "任务未完成，可以修改后重试"), "error");
      }
      if (threadId && turnId && this.serverPrincipalId) {
        try {
          const queue = this.workFollowUpQueue(threadId);
          queue.noteTurnCompleted(turnId);
          this.renderQueuedWorkMessages(queue);
          queueMicrotask(() => void this.dispatchQueuedWork(threadId));
        } catch (error) {
          toast(error instanceof Error ? error.message : "下一轮队列状态无法更新", "error");
        }
      }
      this.scheduleThreadListRefresh("work", 120);
    } else if (["turn.plan.updated", "turn/plan/updated", "codex.plan.updated"].includes(topic)) {
      if (!canRenderEvent) return;
      const renderer = this.renderers.get(`plan-${itemId}`) || createAssistantMessage("work", "执行计划");
      this.renderers.set(`plan-${itemId}`, renderer);
      renderStructuredItem(renderer, {
        type: "plan",
        text: textFrom(data.plan || data.steps || ""),
        status: "inProgress",
      });
    } else if (["turn.diff.updated", "turn/diff/updated", "codex.diff.updated"].includes(topic)) {
      if (!canRenderEvent) return;
      const renderer = this.renderers.get(`diff-${itemId}`) || createAssistantMessage("work", "文件差异");
      this.renderers.set(`diff-${itemId}`, renderer);
      renderCompactItem(renderer, "文件变更正在更新", { kind: "file", running: true });
    } else if (["item/fileChange/outputDelta", "item/fileChange/patchUpdated"].includes(topic)) {
      if (!canRenderEvent) return;
      const renderer = this.renderers.get(`file-${itemId}`) || createAssistantMessage("work", "文件变更");
      this.renderers.set(`file-${itemId}`, renderer);
      renderCompactItem(renderer, "文件变更正在更新", { kind: "file", running: true });
    } else if (topic === "item/mcpToolCall/progress") {
      if (!canRenderEvent) return;
      const renderer = this.renderers.get(`mcp-${itemId}`) || createAssistantMessage("work", "MCP 进度");
      this.renderers.set(`mcp-${itemId}`, renderer);
      renderCompactItem(renderer, "工具正在处理", { kind: "tool", running: true });
    } else if (topic === "mcpServer/startupStatus/updated") {
      const status = String(data.status || "").toLowerCase();
      const failure = textFrom(data.error || data.failureReason);
      if (failure || ["failed", "error"].includes(status)) {
        toast(failure || "MCP 服务启动失败，请打开连接详情检查", "error");
      }
    } else if (["capabilities.changed", "capabilities.updated"].includes(topic)) {
      if (this.client.state === "ready") {
        this.client.rpc<Record<string, unknown>>("capabilities/read", {})
          .then((snapshot) => this.publishCapabilities(snapshot))
          .catch(() => this.resetCapabilities("服务器能力清单刷新失败"));
      }
    } else if (["thread/realtime/transcript/delta", "thread/realtime/transcript/done"].includes(topic)) {
      if (!canRenderEvent) return;
      const renderer = this.renderers.get(`realtime-${itemId}`) || createAssistantMessage("work", "实时会话");
      this.renderers.set(`realtime-${itemId}`, renderer);
      renderer.text += String(data.delta || data.text || "");
      renderer.body.textContent = renderer.text;
    } else if (topic === "thread/realtime/outputAudio/delta") {
      window.dispatchEvent(new CustomEvent("bianhua:realtime-audio", { detail: data }));
    } else if (topic === "fs/changed") {
      const button = document.querySelector<HTMLElement>(".change-button span");
      if (button) button.textContent = "文件已更新";
    } else if (["skills/changed", "app/list/updated"].includes(topic) && this.client.state === "ready") {
      this.client.rpc<Record<string, unknown>>("capabilities/read", {})
        .then((snapshot) => this.publishCapabilities(snapshot))
        .catch(() => this.resetCapabilities("服务器能力清单刷新失败"));
    } else if (topic === "serverRequest/resolved") {
      const requestId = String(data.requestId || data.id || "");
      if (requestId) {
        this.client.acknowledgeElicitation(requestId);
        this.dismissElicitation(requestId, false);
      }
    } else if (["model/rerouted", "model/verification", "account/rateLimits/updated"].includes(topic)) {
      toast(textFrom(data) || "服务器运行状态已更新");
    } else if (["warning", "guardianWarning", "deprecationNotice", "configWarning", "error", "thread/realtime/error"].includes(topic)) {
      toast(String(data.message || "服务器发出警告"), "error");
    } else {
      window.dispatchEvent(new CustomEvent("bianhua:unhandled-domain-event", { detail: { topic, data } }));
    }
  }

  private renderCompletedText(surface: Surface, id: string, text: string): void {
    const renderer = createAssistantMessage(surface, "已完成");
    renderRichText(renderer.body, text);
    renderer.text = text;
    appendMessageHandoffAction(renderer.article, surface, text, { itemId: id });
    this.decorateMessage(renderer.article, surface, "assistant", id, text);
    this.renderers.delete(id);
  }

  private async copyMessageText(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const field = document.createElement("textarea");
    field.value = text;
    field.readOnly = true;
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.append(field);
    field.select();
    const copied = document.execCommand("copy");
    field.remove();
    if (!copied) throw new Error("浏览器没有授予复制权限");
  }

  private workArtifactCacheKey(principalId = this.serverPrincipalId): string | null {
    return principalId ? `${this.workArtifactStoragePrefix}:${principalId}` : null;
  }

  private loadWorkArtifactPaths(principalId: string | null): void {
    this.workArtifactLocalPaths.clear();
    const key = this.workArtifactCacheKey(principalId);
    if (!key) return;
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || "{}") as Record<string, unknown>;
      for (const [serverPath, localPath] of Object.entries(parsed).slice(-200)) {
        if (serverPath.startsWith("/srv/bianhua/workspaces/")
          && typeof localPath === "string"
          && localPath.trim()
          && localPath.length <= 2_048) {
          this.workArtifactLocalPaths.set(serverPath, localPath.trim());
        }
      }
    } catch { /* invalid cache is ignored */ }
  }

  private persistWorkArtifactPaths(): void {
    const key = this.workArtifactCacheKey();
    if (!key) return;
    try {
      const entries = [...this.workArtifactLocalPaths.entries()].slice(-200);
      localStorage.setItem(key, JSON.stringify(Object.fromEntries(entries)));
    } catch { /* local path cache is an optimization */ }
  }

  private forgetWorkArtifactPath(serverPath: string): void {
    if (!this.workArtifactLocalPaths.delete(serverPath)) return;
    this.persistWorkArtifactPaths();
  }

  private setWorkArtifactButtonState(button: HTMLButtonElement, state: "pending" | "saving" | "saved" | "failed", detail?: string): void {
    button.dataset.workArtifactState = state;
    button.setAttribute("aria-busy", String(state === "saving"));
    const status = button.querySelector<HTMLElement>("[data-work-artifact-status]");
    const displayPath = state === "saved" && detail
      ? window.BianhuaArtifactSync?.displayLocalPath(detail) || detail
      : "";
    const failure = state === "failed" && detail
      ? detail.replace(/\s+/g, " ").trim().slice(0, 160)
      : "";
    if (status) status.textContent = state === "saved"
      ? `已保存到：${displayPath}`
      : state === "saving"
        ? "正在保存到本地产物目录…"
        : state === "failed"
          ? failure ? `保存失败：${failure}；点击重试` : "保存失败，点击重试"
          : "连接服务器后自动保存到产物目录";
    button.title = state === "saved"
      ? `点击打开文件；右键可打开所在目录\n${displayPath}`
      : state === "failed" && failure
        ? `${failure}\n点击重试保存`
        : "点击保存到本地产物目录";
  }

  private updateWorkArtifactButtons(serverPath: string, state: "pending" | "saving" | "saved" | "failed", path?: string): void {
    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-work-artifact-server-path]")) {
      if (button.dataset.workArtifactServerPath === serverPath) this.setWorkArtifactButtonState(button, state, path);
    }
  }

  private async receiveWorkspaceArtifact(transfer: WorkspaceArtifactTransfer): Promise<string> {
    const manifest = transfer.artifactManifest;
    const url = transfer.artifactUrl;
    const artifactId = String(manifest?.artifactId || "");
    if (!artifactId
      || artifactId !== String(url?.artifactId || "")
      || String(manifest?.projectId || "") !== this.config.projectId
      || String(url?.sha256 || "").toLowerCase() !== String(manifest?.sha256 || "").toLowerCase()) {
      throw new Error("服务器返回了无效的工作产物清单");
    }
    const completion = new Promise<string>((resolve, reject) => {
      const timeout = window.setTimeout(() => finish(new Error("保存工作产物超时")), 130_000);
      const onComplete = (event: Event) => {
        const detail = (event as CustomEvent).detail;
        if (String(detail?.artifactId || "") !== artifactId) return;
        if (detail?.result?.status === "permission-required") {
          finish(new Error("网页端需要先授权产物目录；原生客户端会自动保存"));
          return;
        }
        const path = typeof detail?.result?.path === "string" ? detail.result.path.trim() : "";
        finish(path || new Error("客户端没有返回已保存产物的本地路径"));
      };
      const onError = (event: Event) => {
        const detail = (event as CustomEvent).detail;
        if (String(detail?.artifactId || "") !== artifactId) return;
        finish(detail?.error instanceof Error ? detail.error : new Error("工作产物保存失败"));
      };
      const finish = (value: string | Error) => {
        window.clearTimeout(timeout);
        this.artifactReceiver.removeEventListener("complete", onComplete);
        this.artifactReceiver.removeEventListener("error", onError);
        if (value instanceof Error) reject(value);
        else resolve(value);
      };
      this.artifactReceiver.addEventListener("complete", onComplete);
      this.artifactReceiver.addEventListener("error", onError);
    });
    this.artifactReceiver.handle(createSessionEnvelope({
      id: workflowId("work-artifact-manifest"),
      kind: "artifact.manifest",
      payload: manifest,
    }));
    this.artifactReceiver.handle(createSessionEnvelope({
      id: workflowId("work-artifact-url"),
      kind: "artifact.url",
      payload: url,
    }));
    return completion;
  }

  private materializeWorkspaceArtifact(serverPath: string, force = false): Promise<string> {
    const cached = this.workArtifactLocalPaths.get(serverPath);
    if (cached && !force) return Promise.resolve(cached);
    const inFlight = this.workArtifactInFlight.get(serverPath);
    if (inFlight) return inFlight;
    const task = (async () => {
      const principalId = this.serverPrincipalId;
      if (!principalId) throw new Error("当前账户状态尚未完成");
      if (this.client.state !== "ready" || !this.operationIsAvailable("workspace/artifact/materialize")) {
        throw new Error("服务器尚未提供工作产物同步能力");
      }
      this.updateWorkArtifactButtons(serverPath, "saving");
      const transfer = await this.client.rpc<WorkspaceArtifactTransfer>("workspace/artifact/materialize", { serverPath }, { timeoutMs: 120_000 });
      const localPath = await this.receiveWorkspaceArtifact(transfer);
      if (this.serverPrincipalId !== principalId) throw new Error("账户已经切换，已停止登记原账户产物");
      if (localPath) {
        this.workArtifactLocalPaths.set(serverPath, localPath);
        this.persistWorkArtifactPaths();
      }
      this.updateWorkArtifactButtons(serverPath, "saved", localPath);
      return localPath;
    })().catch((error) => {
      const message = error instanceof Error ? error.message : "工作产物保存失败";
      this.updateWorkArtifactButtons(serverPath, "failed", message);
      throw error;
    }).finally(() => {
      this.workArtifactInFlight.delete(serverPath);
    });
    this.workArtifactInFlight.set(serverPath, task);
    return task;
  }

  private async runWorkArtifactAction(serverPath: string, action: "open" | "reveal" | "copy"): Promise<void> {
    let path = await this.materializeWorkspaceArtifact(serverPath);
    const bridge = window.BianhuaArtifactSync;
    if (!bridge) throw new Error("客户端产物服务尚未初始化");
    if (action === "copy") {
      await copyPlainText(bridge.displayLocalPath(path));
      toast("本地路径已复制", "success");
      return;
    }
    try {
      if (action === "open") await bridge.openLocalFile(path);
      else await bridge.revealLocalFile(path);
    } catch (error) {
      if (!isTauri()) throw error;
      this.forgetWorkArtifactPath(serverPath);
      path = await this.materializeWorkspaceArtifact(serverPath, true);
      if (action === "open") await bridge.openLocalFile(path);
      else await bridge.revealLocalFile(path);
    }
  }

  private bindWorkArtifactLinks(article: HTMLElement): void {
    for (const button of article.querySelectorAll<HTMLButtonElement>("[data-work-artifact-server-path]")) {
      const serverPath = String(button.dataset.workArtifactServerPath || "");
      if (!serverPath.startsWith("/srv/bianhua/workspaces/")) continue;
      const cached = this.workArtifactLocalPaths.get(serverPath);
      this.setWorkArtifactButtonState(button, cached ? "saved" : "pending", cached);
      if (button.dataset.workArtifactBound !== "true") {
        button.dataset.workArtifactBound = "true";
        button.addEventListener("click", () => {
          void this.runWorkArtifactAction(serverPath, "open")
            .catch((error) => toast(error instanceof Error ? error.message : "无法打开工作产物", "error"));
        });
        button.addEventListener("contextmenu", (event) => {
          event.preventDefault();
          showLocalArtifactMenu(button, {
            open: () => this.runWorkArtifactAction(serverPath, "open"),
            reveal: () => this.runWorkArtifactAction(serverPath, "reveal"),
            copy: () => this.runWorkArtifactAction(serverPath, "copy"),
          }, event.clientX, event.clientY);
        });
      }
      if (!cached
        && button.dataset.workArtifactAutoRequested !== "true"
        && this.operationIsAvailable("workspace/artifact/materialize")
        && this.client.state === "ready") {
        button.dataset.workArtifactAutoRequested = "true";
        queueMicrotask(() => void this.materializeWorkspaceArtifact(serverPath).catch(() => {
          delete button.dataset.workArtifactAutoRequested;
        }));
      }
    }
  }

  private decorateMessage(
    article: HTMLElement,
    surface: Surface,
    role: "user" | "assistant",
    messageId: string | undefined,
    text: string,
  ): void {
    const normalizedText = text.trim();
    article.dataset.messageSurface = surface;
    article.dataset.messageRole = role;
    article.dataset.messageText = normalizedText;
    if (messageId) article.dataset.messageId = messageId;
    if (surface === "work" && role === "assistant") this.bindWorkArtifactLinks(article);
    article.querySelector<HTMLElement>("[data-message-actions]")?.remove();
    if (!normalizedText) return;

    const actions = document.createElement("div");
    actions.dataset.messageActions = "";
    actions.setAttribute("aria-label", role === "user" ? "用户消息操作" : "回复操作");
    const addAction = (label: string, handler: (button: HTMLButtonElement) => void): HTMLButtonElement => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = label;
      button.setAttribute("aria-label", label);
      button.addEventListener("click", () => handler(button));
      actions.append(button);
      return button;
    };

    addAction("复制", (button) => {
      button.disabled = true;
      void this.copyMessageText(normalizedText)
        .then(() => toast("已复制", "success"))
        .catch((error) => toast(error instanceof Error ? error.message : "复制失败", "error"))
        .finally(() => { button.disabled = false; });
    });

    if (surface === "chat" && role === "user" && messageId && this.operationIsAvailable("chat/message/edit")) {
      addAction("编辑", () => {
        if (this.chatControl.snapshot().turnId) return toast("请先停止或等待当前生成完成", "error");
        const prompt = document.querySelector<HTMLTextAreaElement>("#chat-prompt");
        if (!prompt) return;
        prompt.value = normalizedText;
        if (prompt.dataset.editMessageId !== messageId) delete prompt.dataset.editClientMessageId;
        prompt.dataset.editMessageId = messageId;
        prompt.dispatchEvent(new Event("input", { bubbles: true }));
        prompt.focus();
        toast("编辑后发送；服务器确认前原消息不会改变");
      });
    }

    if (surface === "chat" && role === "assistant" && messageId && this.operationIsAvailable("chat/message/regenerate")) {
      addAction("重新生成", (button) => {
        void this.runChatMessageAction("chat/message/regenerate", { messageId }, button);
      });
    }

    if (surface === "chat" && role === "assistant" && messageId && this.operationIsAvailable("chat/message/feedback")) {
      for (const [label, rating] of [["赞", "positive"], ["踩", "negative"]] as const) {
        addAction(label, (button) => {
          button.disabled = true;
          void this.client.rpc("chat/message/feedback", {
            projectId: this.config.projectId,
            threadId: this.activeThread.get("chat"),
            messageId,
            rating,
          }).then(() => {
            for (const peer of actions.querySelectorAll<HTMLButtonElement>("button[aria-pressed]")) peer.setAttribute("aria-pressed", "false");
            button.setAttribute("aria-pressed", "true");
            toast("反馈已送达", "success");
          }).catch((error) => toast(error instanceof Error ? error.message : "反馈发送失败", "error"))
            .finally(() => { button.disabled = false; });
        }).setAttribute("aria-pressed", "false");
      }
    }
    article.append(actions);
  }

  private decorateExistingMessages(): void {
    for (const article of document.querySelectorAll<HTMLElement>(".runtime-message")) {
      const surface = article.dataset.messageSurface === "work" ? "work" : "chat";
      const role = article.dataset.messageRole === "user" ? "user" : "assistant";
      const text = article.dataset.messageText
        || article.querySelector<HTMLElement>(role === "user" ? ".user-bubble" : ".runtime-stream")?.textContent
        || "";
      this.decorateMessage(article, surface, role, workflowIdentifier(article.dataset.messageId), text);
    }
  }

  private async runChatMessageAction(
    method: "chat/message/regenerate",
    params: { messageId: string },
    button: HTMLButtonElement,
  ): Promise<void> {
    if (!this.operationIsAvailable(method)
      || !this.operationIsAvailable("gateway/idempotentChatCreate")
      || this.client.state !== "ready"
      || !this.modeIsUsable("chat")
      || !this.serverPrincipalId) {
      toast("当前聊天服务器未提供此操作", "error");
      return;
    }
    if (!this.chatControl.beginSubmission()) {
      toast("当前聊天请求仍在进行", "error");
      return;
    }
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    const clientMessageId = button.dataset.clientRequestId || crypto.randomUUID();
    button.dataset.clientRequestId = clientMessageId;
    const actionPrincipalId = this.serverPrincipalId;
    const activeChatThreadId = this.activeThread.get("chat");
    const journal: ChatSubmissionJournal = {
      principalId: this.serverPrincipalId,
      clientMessageId,
      operation: method,
      text: "",
      attachments: [],
      tools: [],
      createdAt: new Date().toISOString(),
      ...(activeChatThreadId ? { threadId: activeChatThreadId } : {}),
      messageId: params.messageId,
    };
    try {
      if (!this.persistChatSubmission(journal)) {
        throw new Error("无法写入重新生成恢复日志；为避免重复请求，操作没有提交");
      }
      const result = await this.client.rpc<Record<string, unknown>>(method, this.chatSubmissionRequest(journal));
      if (this.serverPrincipalId !== actionPrincipalId) return;
      this.applyChatSubmissionResult(journal, result);
      delete button.dataset.clientRequestId;
    } catch (error) {
      if (this.serverPrincipalId !== actionPrincipalId) return;
      const message = error instanceof Error ? error.message : "重新生成失败";
      if (requestOutcomeMayExist(error)) {
        this.chatControl.submissionUncertain(message);
        this.scheduleUncertainChatResume(1_200);
        toast("重新生成请求结果尚未确认，正在用同一请求标识恢复", "error");
      } else {
        this.clearChatSubmission(clientMessageId);
        delete button.dataset.clientRequestId;
        this.chatControl.submissionFailed(message);
        toast(message, "error");
      }
    } finally {
      if (this.serverPrincipalId !== actionPrincipalId) return;
      const recovering = this.uncertainChatSubmission?.clientMessageId === clientMessageId;
      button.disabled = recovering;
      if (!recovering) button.removeAttribute("aria-busy");
    }
  }

  private applyChatControlEvent(topic: string, data: Record<string, unknown>): boolean {
    const turn = data.turn && typeof data.turn === "object" ? data.turn as Record<string, unknown> : data;
    const threadId = String(data.threadId || turn.threadId || this.activeThread.get("chat") || "");
    const turnId = String(data.turnId || turn.id || data.runId || "");
    if (["chat.turn.started", "chat/turn/started", "chat.run.started"].includes(topic)) {
      if (threadId && turnId) {
        this.setActiveThread("chat", threadId);
        this.chatControl.runStarted(threadId, turnId);
      }
      return true;
    }
    if (["chat.turn.completed", "chat/turn/completed", "chat.run.completed"].includes(topic)) {
      const status = String(turn.status || data.status || "completed");
      const error = turn.error && typeof turn.error === "object"
        ? String((turn.error as Record<string, unknown>).message || "")
        : String(data.message || "");
      if (this.chatControl.runCompleted(threadId, turnId, status, error)) {
        if (status === "interrupted") toast("生成已停止，服务器已确认最终状态", "success");
        if (status === "failed") toast(error || "聊天生成失败，可以修改后重试", "error");
      }
      return true;
    }
    return false;
  }

  private setHandoffMaterializationCapability(available: boolean): void {
    if (this.handoffMaterializationAvailable === available) return;
    this.handoffMaterializationAvailable = available;
    window.dispatchEvent(new CustomEvent("bianhua:handoff-capability", {
      detail: { materializeToWorkspace: available },
    }));
    if (available && this.client.state === "ready" && this.pendingRecoveredHandoffIds.size) {
      queueMicrotask(() => void this.resumeRecoveredWorkflowHandoffs());
    }
  }

  private applyCapabilities(payload: Record<string, unknown>): void {
    const capabilities = Array.isArray(payload.capabilities) ? payload.capabilities : [];
    const protocolFeatures = Array.isArray(payload.features) ? payload.features : [];
    const allCapabilities = [...capabilities, ...protocolFeatures];
    this.supportedOperations.clear();
    for (const operation of collectSupportedOperations(payload)) this.supportedOperations.add(operation);
    const byKey = new Map<string, Record<string, unknown>>();
    for (const item of allCapabilities) {
      const record = item as Record<string, unknown>;
      const key = String(record.key || record.id || "");
      if (!key) continue;
      byKey.set(key, record);
      byKey.set(key.split(".").at(-1) || key, record);
    }
    const handoffFeature = byKey.get("workflow.handoff");
    const handoffStatus = String(handoffFeature?.status || "unavailable");
    const handoffOperations = Array.isArray(handoffFeature?.operations)
      ? handoffFeature.operations.map(String)
      : [];
    this.setHandoffMaterializationCapability(
      !["unavailable", "unsupported", "auth_required"].includes(handoffStatus)
      && handoffOperations.includes("artifact/materializeToWorkspace"),
    );
    const modes = payload.modes && typeof payload.modes === "object"
      ? payload.modes as Record<string, Record<string, unknown>>
      : {};
    for (const surface of ["chat", "work"] as const) {
      const mode = modes[surface];
      const status = String(mode?.status || "unavailable");
      this.modeStatuses.set(surface, status);
      this.modeReasons.set(surface, String(mode?.reason || "服务器没有声明此模式"));
    }
    const chatModeStatus = this.modeStatuses.get("chat");
    const modelCopy = document.querySelector<HTMLElement>(".model-button > span:nth-child(2)");
    if (modelCopy) {
      modelCopy.textContent = chatModeStatus === "available" || chatModeStatus === "degraded" || chatModeStatus === "experimental"
        ? "服务器聊天 · 自动"
        : chatModeStatus === "auth_required"
          ? "聊天能力 · 需要登录"
          : "聊天能力 · 未提供";
    }
    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-remote-action]")) {
      const surface = String(button.dataset.surface || "chat");
      const action = String(button.dataset.remoteAction || "");
      const capability = byKey.get(`${surface}.${action}`) || byKey.get(action);
      const modeCapability = modes[surface];
      const isSubscriptionImageBridge = surface === "chat"
        && action === "image_generate"
        && Boolean(window.BianhuaRemoteConfig?.transport || window.BianhuaRemoteConfig?.url);
      if (!capability) {
        if (isSubscriptionImageBridge) {
          button.disabled = false;
          button.dataset.capabilityState = "degraded";
          button.title = "订阅图像测试桥已配置";
          continue;
        }
        const modeStatus = String(modeCapability?.status || "unavailable");
        button.disabled = true;
        button.dataset.capabilityState = "unavailable";
        button.setAttribute("aria-pressed", "false");
        this.selectedTools.get(surface as Surface)?.delete(action);
        button.title = String(modeCapability?.reason || (modeStatus === "auth_required" ? "登录服务器后可用" : "服务器能力清单未声明此功能"));
        continue;
      }
      const status = String(capability.status || "unavailable");
      const requiredOperations = surface === "work"
        ? [WORK_REMOTE_ACTION_OPERATIONS[action] || "capability/activate"]
        : ["chat/message/create", "gateway/idempotentChatCreate"];
      const missingOperation = requiredOperations.find((operation) => !this.supportedOperations.has(operation));
      const underDevelopment = surface === "work" && action === "plugins";
      const activationPending = this.remoteActionPending.has(`${surface}:${action}`);
      const availability = remoteActionAvailability(status, underDevelopment, Boolean(missingOperation), activationPending);
      const unavailable = availability.disabled;
      button.disabled = availability.disabled;
      if (activationPending) button.setAttribute("aria-busy", "true");
      else button.removeAttribute("aria-busy");
      button.dataset.capabilityState = underDevelopment ? "unavailable" : missingOperation ? "unsupported" : status;
      button.title = unavailable
        ? activationPending
          ? "正在与服务器同步工具状态"
          : underDevelopment
          ? "官方插件安装接口仍在开发，本版本不启用"
          : missingOperation
            ? `服务器没有声明 ${missingOperation}，此入口保持禁用`
            : String(capability.reason || modeCapability?.reason || (status === "auth_required" ? "登录服务器后可用" : "当前服务器未提供此能力"))
        : status === "experimental"
          ? "实验能力，服务器升级后可能变化"
          : "服务器已提供此能力";
      if (availability.clearSelection) {
        button.setAttribute("aria-pressed", "false");
        this.selectedTools.get(surface as Surface)?.delete(action);
      } else {
        button.setAttribute("aria-pressed", String(this.selectedTools.get(surface as Surface)?.has(action) === true));
      }
    }
    for (const drawer of document.querySelectorAll<HTMLElement>(".capability-drawer")) {
      const count = drawer.querySelectorAll(".capability-item:not(:disabled)").length;
      const label = drawer.querySelector<HTMLElement>("[data-capability-sync-copy]");
      if (label) label.textContent = `已同步 ${count} 项`;
    }
    this.capabilitiesReady = true;
    this.renderChatControl(this.chatControl.snapshot());
    this.renderWorkControl(this.workControl.snapshot());
    this.refreshStaticActionAvailability();
    this.decorateExistingMessages();
  }

  private modeIsUsable(surface: Surface): boolean {
    if (!this.capabilitiesReady) return false;
    return ["available", "degraded", "experimental"].includes(String(this.modeStatuses.get(surface)));
  }

  private operationIsAvailable(operation: string): boolean {
    return this.capabilitiesReady && this.supportedOperations.has(operation);
  }

  private refreshStaticActionAvailability(): void {
    if (this.visualDemo) return;
    const ready = this.client.state === "ready" && this.capabilitiesReady;
    const setEnabled = (selector: string, enabled: boolean, unavailableReason: string): void => {
      for (const button of document.querySelectorAll<HTMLButtonElement>(selector)) {
        // Product-declared permanent placeholders remain disabled even if a
        // later capability snapshot contains a similarly named operation.
        if (button.getAttribute("aria-disabled") === "true" && /待接入|接入后开放/.test(button.textContent || button.title)) continue;
        button.disabled = !enabled;
        button.setAttribute("aria-disabled", String(!enabled));
        if (!enabled) button.title = unavailableReason;
        else if (button.title === unavailableReason || /尚未连接|没有声明|正在同步/.test(button.title)) button.removeAttribute("title");
      }
    };

    setEnabled("[data-app-action='search'], [data-app-action='history-options']", ready && this.operationIsAvailable("session/search"), "服务器没有声明统一搜索能力");
    setEnabled("[data-app-action='new-chat']", ready && this.modeIsUsable("chat") && this.operationIsAvailable("chat/message/create") && this.operationIsAvailable("gateway/idempotentChatCreate"), "聊天服务尚未连接或缺少断线恢复能力");
    setEnabled("[data-app-action='new-work']", ready && this.modeIsUsable("work") && this.operationIsAvailable("thread/start"), "工作服务尚未连接或不可用");
    setEnabled("[data-app-action='workspace'], [data-app-action='add-workspace']", ready && this.operationIsAvailable("workspace/list"), "服务器没有声明工作区列表能力");
    setEnabled("[data-app-action='permissions']", ready && this.operationIsAvailable("permissionProfile/list"), "服务器没有声明权限配置能力");
    setEnabled("[data-app-action='models']", ready && this.operationIsAvailable("model/list"), "服务器没有声明模型列表能力");
    setEnabled("[data-app-action='chat-options']", ready && this.modeIsUsable("chat"), "聊天服务尚未提供");
    setEnabled("[data-app-action='task-options'], .change-button, .trace-detail", ready && Boolean(this.activeThread.get("work")) && this.operationIsAvailable("thread/read"), "当前没有可读取的工作任务");
    setEnabled(".model-button", ready && this.modeIsUsable("chat") && this.operationIsAvailable("chat/model/list"), "服务器没有声明聊天模型列表能力");
    setEnabled("[data-attachment-for='chat']", ready && this.attachmentUploadIsAvailable("chat"), "服务器没有声明完整的聊天附件上传能力");
    setEnabled("[data-attachment-for='work']", ready && this.attachmentUploadIsAvailable("work"), "服务器没有声明完整的工作附件上传能力");

    for (const surface of ["chat", "work"] as const) {
      const tab = document.querySelector<HTMLButtonElement>(`[data-mode-target='${surface}']`);
      if (!tab) continue;
      const enabled = surface === "chat"
        ? (ready && this.modeIsUsable(surface)) || Boolean(window.BianhuaRemoteConfig?.transport || window.BianhuaRemoteConfig?.url)
        : ready && this.modeIsUsable(surface);
      tab.disabled = !enabled;
      tab.setAttribute("aria-disabled", String(!enabled));
      tab.title = enabled ? "" : this.modeReasons.get(surface) || "当前服务器未提供此模式";
    }
  }

  private resetCapabilities(reason: string): void {
    this.capabilitiesReady = false;
    this.supportedOperations.clear();
    this.remoteActionPending.clear();
    this.setHandoffMaterializationCapability(false);
    this.modeStatuses.set("chat", "unavailable");
    this.modeStatuses.set("work", "unavailable");
    this.modeReasons.set("chat", reason);
    this.modeReasons.set("work", reason);
    for (const selected of this.selectedTools.values()) selected.clear();
    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-remote-action]")) {
      const isSubscriptionImageBridge = button.dataset.surface === "chat"
        && button.dataset.remoteAction === "image_generate"
        && Boolean(window.BianhuaRemoteConfig?.transport || window.BianhuaRemoteConfig?.url);
      button.disabled = !isSubscriptionImageBridge;
      button.removeAttribute("aria-busy");
      button.dataset.capabilityState = isSubscriptionImageBridge ? "degraded" : "unavailable";
      button.setAttribute("aria-pressed", "false");
      button.title = isSubscriptionImageBridge ? "订阅图像测试桥已配置" : reason;
    }
    const modelCopy = document.querySelector<HTMLElement>(".model-button > span:nth-child(2)");
    if (modelCopy) modelCopy.textContent = "聊天能力 · 未确认";
    for (const drawer of document.querySelectorAll<HTMLElement>(".capability-drawer")) {
      const label = drawer.querySelector<HTMLElement>("[data-capability-sync-copy]");
      if (label) label.textContent = reason;
    }
    this.renderChatControl(this.chatControl.snapshot());
    this.renderWorkControl(this.workControl.snapshot());
    this.refreshStaticActionAvailability();
  }

  private publishCapabilities(snapshot: Record<string, unknown>): void {
    this.applyCapabilities(snapshot);
    window.dispatchEvent(new CustomEvent("bianhua:capabilities-snapshot", {
      detail: snapshot,
    }));
  }

  private enqueueElicitation(requestId: string, payload: Record<string, unknown>): void {
    if (!requestId || this.elicitationIds.has(requestId)) return;
    const params = payload.params && typeof payload.params === "object"
      ? payload.params as Record<string, unknown>
      : {};
    const threadId = String(params.threadId || this.activeThread.get("work") || "");
    const turnId = String(params.turnId || "");
    if (threadId) this.workControl.markAwaitingApproval(threadId, turnId || null, String(payload.method || ""));
    if (threadId) this.elicitationThreads.set(requestId, { threadId, turnId: turnId || null });
    this.elicitationIds.add(requestId);
    this.elicitationQueue.push({ requestId, payload });
    this.showNextElicitation();
  }

  private showNextElicitation(): void {
    if (this.elicitationActive) return;
    const next = this.elicitationQueue.shift();
    if (!next) return;
    const method = String(next.payload.method || "");
    const interactiveMethods = new Set([
      "item/commandExecution/requestApproval",
      "item/fileChange/requestApproval",
      "item/permissions/requestApproval",
      "mcpServer/elicitation/request",
      "item/tool/requestUserInput",
      "applyPatchApproval",
      "execCommandApproval",
    ]);
    if (!interactiveMethods.has(method)) {
      try {
        this.client.respond(next.requestId, buildElicitationResponse(method, next.payload.params, "cancel"));
      } catch (error) {
        toast(error instanceof Error ? error.message : "无法拒绝不受支持的服务器请求", "error");
      }
      this.elicitationIds.delete(next.requestId);
      const message = method === "item/tool/call"
        ? "动态工具未在可信服务器注册，已安全拒绝"
        : method === "account/chatgptAuthTokens/refresh" || method === "attestation/generate"
          ? "服务器认证或设备证明请求已交回服务器处理"
          : "收到未支持的服务器请求，已安全拒绝";
      toast(message, "error");
      queueMicrotask(() => this.showNextElicitation());
      return;
    }
    this.elicitationActive = true;
    this.activeElicitationRequestId = next.requestId;
    this.activeElicitationPayload = next.payload;
    this.presentElicitation(next.requestId, next.payload);
    this.renderWorkControl(this.workControl.snapshot());
  }

  private resetElicitations(): void {
    this.elicitationQueue = [];
    this.elicitationIds.clear();
    this.elicitationActive = false;
    this.activeElicitationRequestId = null;
    this.activeElicitationPayload = null;
    this.elicitationThreads.clear();
    const dialog = document.querySelector<HTMLDialogElement>("#runtime-approval");
    if (dialog?.open) dialog.close();
    dialog?.remove();
    this.restoreElicitationFocus();
  }

  private dismissElicitation(requestId: string, notify = true): void {
    this.elicitationQueue = this.elicitationQueue.filter((entry) => entry.requestId !== requestId);
    this.elicitationIds.delete(requestId);
    const requestThread = this.elicitationThreads.get(requestId);
    this.elicitationThreads.delete(requestId);
    if (requestThread) this.workControl.approvalResolved(requestThread.threadId, requestThread.turnId);
    if (this.activeElicitationRequestId !== requestId) return;
    const dialog = document.querySelector<HTMLDialogElement>(`#runtime-approval[data-request-id='${CSS.escape(requestId)}']`);
    if (dialog?.open) dialog.close();
    dialog?.remove();
    this.restoreElicitationFocus();
    this.elicitationActive = false;
    this.activeElicitationRequestId = null;
    this.activeElicitationPayload = null;
    if (notify) toast("这项服务器请求已在其他位置解决");
    queueMicrotask(() => this.showNextElicitation());
  }

  private focusActiveElicitation(): void {
    const requestId = this.activeElicitationRequestId;
    if (!requestId) {
      toast("服务器确认请求正在恢复，请稍候");
      return;
    }
    const existing = document.querySelector<HTMLDialogElement>(`#runtime-approval[data-request-id='${CSS.escape(requestId)}']`);
    if (existing) {
      if (!existing.open) {
        try { existing.showModal(); } catch { existing.setAttribute("open", ""); }
      }
      existing.querySelector<HTMLButtonElement>(".runtime-primary, .runtime-secondary")?.focus();
      return;
    }
    if (this.activeElicitationPayload) this.presentElicitation(requestId, this.activeElicitationPayload);
  }

  private presentElicitation(requestId: string, payload: Record<string, unknown>): void {
    this.elicitationRestoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.querySelector<HTMLDialogElement>("#runtime-approval")?.remove();
    const dialog = document.createElement("dialog");
    dialog.id = "runtime-approval";
    dialog.dataset.requestId = requestId;
    dialog.className = "runtime-dialog";
    dialog.setAttribute("aria-labelledby", "runtime-approval-title");
    const card = document.createElement("div");
    card.className = "runtime-dialog-card";
    const params = payload.params && typeof payload.params === "object"
      ? payload.params as Record<string, unknown>
      : {};
    const method = String(payload.method || "");
    const category = String(payload.category || "approval");
    const eyebrow = document.createElement("span");
    eyebrow.className = "runtime-dialog-eyebrow";
    eyebrow.textContent = "服务器操作确认";
    const title = document.createElement("h2");
    title.id = "runtime-approval-title";
    title.textContent = String(payload.title || "任务正在等待你的确认");
    const description = document.createElement("p");
    const command = Array.isArray(params.command) ? params.command.join(" ") : params.command;
    description.textContent = String(params.message || params.reason || (
      method === "item/commandExecution/requestApproval"
        ? "任务需要运行一项本地操作才能继续。"
        : "服务器端任务正在等待决定。"
    ));
    const details = document.createElement("dl");
    details.className = "runtime-approval-details";
    const addDetail = (label: string, value: unknown) => {
      if (value === undefined || value === null || value === "") return;
      if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") return;
      let copy = String(value);
      if (!copy) return;
      if (copy.length > 8_000) copy = `${copy.slice(0, 8_000)}\n…内容过长，已截断`;
      const term = document.createElement("dt");
      term.textContent = label;
      const definition = document.createElement("dd");
      definition.textContent = copy;
      details.append(term, definition);
    };
    if (method !== "item/commandExecution/requestApproval") addDetail("操作", command);
    addDetail("请求原因", params.reason);
    if (method === "mcpServer/elicitation/request") {
      addDetail("MCP 服务器", params.serverName);
      addDetail("交互模式", params.mode);
    }
    const answers = new Map<string, HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>();
    const inputList = document.createElement("div");
    inputList.className = "runtime-input-list";
    if (category === "user_input" || category === "mcp_elicitation") {
      let questions = Array.isArray(params.questions) ? params.questions as Array<Record<string, unknown>> : [];
      if (!questions.length && category === "mcp_elicitation") {
        const requestedSchema = params.requestedSchema && typeof params.requestedSchema === "object"
          ? params.requestedSchema as Record<string, unknown>
          : {};
        const properties = requestedSchema.properties && typeof requestedSchema.properties === "object"
          ? requestedSchema.properties as Record<string, unknown>
          : {};
        const requiredFields = new Set(Array.isArray(requestedSchema.required)
          ? requestedSchema.required.filter((item): item is string => typeof item === "string")
          : []);
        questions = Object.entries(properties).map(([id, value]) => {
          const schema = value && typeof value === "object" ? value as Record<string, unknown> : {};
          const itemSchema = schema.type === "array" && schema.items && typeof schema.items === "object"
            ? schema.items as Record<string, unknown>
            : schema;
          const enumValues = Array.isArray(itemSchema.enum)
            ? itemSchema.enum
            : Array.isArray(itemSchema.oneOf) || Array.isArray(itemSchema.anyOf)
              ? ((itemSchema.oneOf || itemSchema.anyOf) as Array<Record<string, unknown>>).map((option) => option.const)
              : [];
          const enumLabels = Array.isArray(itemSchema.enumNames)
            ? itemSchema.enumNames
            : Array.isArray(itemSchema.oneOf) || Array.isArray(itemSchema.anyOf)
              ? ((itemSchema.oneOf || itemSchema.anyOf) as Array<Record<string, unknown>>).map((option) => option.title || option.const)
              : enumValues;
          return {
            id,
            question: schema.title || schema.description || id,
            type: schema.type,
            required: requiredFields.has(id),
            minimum: schema.minimum,
            maximum: schema.maximum,
            multiple: schema.type === "array",
            default: schema.default,
            options: enumValues.map((value, index) => ({ value, label: enumLabels[index] || value })),
          };
        });
      }
      const normalizedQuestions = questions.length
        ? questions
        : category === "mcp_elicitation" && params.mode === "url"
          ? []
          : [{ id: "value", question: params.prompt || "请输入回复" }];
      for (const [index, question] of normalizedQuestions.entries()) {
        const fieldId = String(question.id || `answer-${index}`);
        const label = document.createElement("label");
        label.textContent = String(question.question || question.header || "请输入回复");
        const options = Array.isArray(question.options) ? question.options as Array<Record<string, unknown>> : [];
        let control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        let otherInput: HTMLInputElement | null = null;
        if (options.length || question.type === "boolean") {
          const select = document.createElement("select");
          select.multiple = question.multiple === true;
          const normalizedOptions = options.length ? options : [{ value: "true", label: "是" }, { value: "false", label: "否" }];
          for (const option of normalizedOptions) {
            const element = document.createElement("option");
            element.value = String(option.value || option.label || "");
            element.textContent = String(option.label || option.value || "");
            select.append(element);
          }
          if (question.isOther === true && !select.multiple) {
            const otherOption = document.createElement("option");
            otherOption.value = "__bianhua_other__";
            otherOption.textContent = "其他…";
            select.append(otherOption);
            otherInput = document.createElement("input");
            otherInput.type = "text";
            otherInput.hidden = true;
            otherInput.autocomplete = "off";
            otherInput.placeholder = "输入其他答案";
            const updateOther = () => {
              const selected = select.value === "__bianhua_other__";
              otherInput!.hidden = !selected;
              otherInput!.required = selected;
              if (!selected) otherInput!.value = "";
            };
            select.addEventListener("change", updateOther);
            select.dataset.otherInput = "true";
            updateOther();
          }
          if (Array.isArray(question.default)) {
            for (const option of select.options) option.selected = question.default.map(String).includes(option.value);
          } else if (question.default !== undefined && question.default !== null) select.value = String(question.default);
          control = select;
        } else if (question.isSecret === true || question.type === "number" || question.type === "integer") {
          const input = document.createElement("input");
          input.type = question.isSecret === true ? "password" : "number";
          input.name = fieldId;
          input.autocomplete = question.isSecret === true ? "new-password" : "off";
          if (question.default !== undefined && question.default !== null) input.value = String(question.default);
          control = input;
        } else {
          const input = document.createElement("textarea");
          input.rows = 2;
          input.name = fieldId;
          input.autocomplete = "off";
          input.placeholder = "输入回复…";
          control = input;
        }
        control.required = question.required === true;
        if (control instanceof HTMLInputElement && control.type === "number") {
          if (question.type === "integer") control.step = "1";
          if (typeof question.minimum === "number" && Number.isFinite(question.minimum)) control.min = String(question.minimum);
          if (typeof question.maximum === "number" && Number.isFinite(question.maximum)) control.max = String(question.maximum);
        }
        label.append(control);
        if (otherInput) label.append(otherInput);
        inputList.append(label);
        answers.set(fieldId, control);
      }
    }
    if (category === "mcp_elicitation" && params.mode === "url" && typeof params.url === "string") {
      try {
        const url = new URL(params.url);
        if (url.protocol === "https:") {
          const link = document.createElement("a");
          link.className = "runtime-external-link";
          link.href = url.href;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.textContent = "在浏览器中完成服务器授权";
          inputList.append(link);
        }
      } catch { /* Invalid server URLs are intentionally not rendered. */ }
    }
    const actions = document.createElement("div");
    actions.className = "runtime-dialog-actions";
    const resolve = (decision: UiDecision) => {
      const accepting = decision !== "decline" && decision !== "cancel";
      if (accepting) {
        for (const control of inputList.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input, select, textarea")) {
          if (!control.checkValidity()) {
            control.reportValidity();
            toast("请先完成必填项并修正无效输入", "error");
            return;
          }
        }
      }
      const values = Object.fromEntries(Array.from(answers, ([key, control]) => {
        const value = control instanceof HTMLSelectElement && control.multiple
          ? Array.from(control.selectedOptions, (option) => option.value)
          : control instanceof HTMLSelectElement && control.dataset.otherInput === "true" && control.value === "__bianhua_other__"
            ? (control.parentElement?.querySelector<HTMLInputElement>("input[placeholder='输入其他答案']")?.value || "")
            : control.value;
        return [key, value];
      }));
      try {
        this.client.respond(requestId, buildElicitationResponse(method, params, decision, values));
      } catch (error) {
        toast(error instanceof Error ? error.message : "审批回复发送失败", "error");
        return;
      }
      const requestThread = this.elicitationThreads.get(requestId);
      if (requestThread) this.workControl.approvalResolved(requestThread.threadId, requestThread.turnId);
      this.elicitationActive = false;
      this.activeElicitationRequestId = null;
      this.activeElicitationPayload = null;
      this.elicitationIds.delete(requestId);
      if (dialog.open) dialog.close();
      dialog.remove();
      this.restoreElicitationFocus();
      this.renderWorkControl(this.workControl.snapshot());
      queueMicrotask(() => this.showNextElicitation());
    };
    const addDecision = (decision: UiDecision, label: string, primary = false) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = primary ? "runtime-primary" : "runtime-secondary";
      button.textContent = label;
      button.addEventListener("click", () => resolve(decision));
      actions.append(button);
    };
    let cancelAllowed = true;
    if (method === "item/commandExecution/requestApproval") {
      const declared = commandApprovalDecisions(params);
      const execpolicy = Array.isArray(params.proposedExecpolicyAmendment)
        ? params.proposedExecpolicyAmendment.filter((item): item is string => typeof item === "string")
        : [];
      const networkAmendments = Array.isArray(params.proposedNetworkPolicyAmendments)
        ? params.proposedNetworkPolicyAmendments as Array<Record<string, unknown>>
        : [];
      const defaults: UiDecision[] = ["decline", "acceptForSession"];
      if (execpolicy.length) defaults.push({ acceptWithExecpolicyAmendment: { execpolicy_amendment: execpolicy } });
      for (const amendment of networkAmendments) {
        const host = String(amendment.host || "").trim();
        const action = amendment.action === "deny" ? "deny" : "allow";
        if (host) defaults.push({ applyNetworkPolicyAmendment: { network_policy_amendment: { host, action } } });
      }
      defaults.push("accept");
      const decisions = declared ?? defaults;
      cancelAllowed = declared === null || decisions.includes("cancel");
      for (const decision of decisions) {
        if (decision === "accept") addDecision(decision, "允许一次", true);
        else if (decision === "acceptForSession") addDecision(decision, "本次会话允许");
        else if (decision === "decline") addDecision(decision, "拒绝并继续");
        else if (decision === "cancel") addDecision(decision, "拒绝并停止任务");
        else if ("acceptWithExecpolicyAmendment" in decision) addDecision(decision, "允许并记住命令规则");
        else {
          const amendment = decision.applyNetworkPolicyAmendment.network_policy_amendment;
          addDecision(decision, `${amendment.action === "allow" ? "允许" : "拒绝"}并记住 ${amendment.host}`);
        }
      }
      if (!decisions.length) {
        const unavailable = document.createElement("button");
        unavailable.type = "button";
        unavailable.className = "runtime-secondary";
        unavailable.disabled = true;
        unavailable.textContent = "服务器没有提供可用决定";
        actions.append(unavailable);
      }
    } else {
      addDecision("decline", "拒绝");
      if (["item/fileChange/requestApproval", "item/permissions/requestApproval", "applyPatchApproval", "execCommandApproval"].includes(method)) {
        addDecision("acceptForSession", "本次会话允许");
      }
      addDecision("accept", answers.size ? "提交回复" : "允许一次", true);
    }
    card.append(eyebrow, title, description);
    if (details.childElementCount) card.append(details);
    if (inputList.childElementCount) card.append(inputList);
    card.append(actions);
    dialog.append(card);
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      if (!cancelAllowed) {
        toast("当前命令请求没有提供“取消任务”决定，请选择上方可用操作", "error");
        return;
      }
      resolve("cancel");
    });
    document.body.append(dialog);
    try { dialog.showModal(); } catch { dialog.setAttribute("open", ""); }
    (answers.values().next().value as HTMLElement | undefined)?.focus();
  }

  private restoreElicitationFocus(): void {
    const target = this.elicitationRestoreFocus;
    this.elicitationRestoreFocus = null;
    if (target?.isConnected) queueMicrotask(() => target.focus());
  }

  private clearRecoveredHandoffRetry(): void {
    if (this.recoveredHandoffRetryTimer) window.clearTimeout(this.recoveredHandoffRetryTimer);
    this.recoveredHandoffRetryTimer = 0;
    this.recoveredHandoffRetryAttempt = 0;
  }

  private scheduleRecoveredHandoffRetry(): void {
    if (this.recoveredHandoffRetryTimer || !this.pendingRecoveredHandoffIds.size) return;
    const delay = Math.min(30_000, 1_000 * 2 ** Math.min(this.recoveredHandoffRetryAttempt, 5));
    this.recoveredHandoffRetryAttempt += 1;
    this.recoveredHandoffRetryTimer = window.setTimeout(() => {
      this.recoveredHandoffRetryTimer = 0;
      void this.resumeRecoveredWorkflowHandoffs();
    }, delay);
  }

  private async resumeRecoveredWorkflowHandoffs(): Promise<void> {
    if (this.client.state !== "ready" || !this.handoffMaterializationAvailable || !this.workflowHandoffs) return;
    const store = this.workflowHandoffs;
    for (const handoffId of [...this.pendingRecoveredHandoffIds]) {
      if (this.recoveringWorkflowHandoffIds.has(handoffId)) continue;
      const handoff = store.get(handoffId);
      if (!handoff
        || handoff.target.surface !== "work"
        || !handoff.artifactRefs.length
        || (handoff.status !== "staged" && handoff.status !== "ready")) {
        this.pendingRecoveredHandoffIds.delete(handoffId);
        continue;
      }
      this.recoveringWorkflowHandoffIds.add(handoffId);
      try {
        const delivery = store.prepareDelivery(handoffId, { workDisposition: "explicit-submit" });
        const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
        await this.deliverWorkflowHandoff(
          store.get(handoffId) || handoff,
          delivery,
          prompt?.value.trim() || "",
          [...(this.attachments.get("work") || [])],
          { autoSubmitWork: false },
        );
        this.pendingRecoveredHandoffIds.delete(handoffId);
      } catch (error) {
        const message = error instanceof Error ? error.message : "视频交接恢复失败";
        toast(message, "error");
        window.dispatchEvent(new CustomEvent("bianhua:handoff-error", {
          detail: { targetSurface: "work", message },
        }));
        const candidate = error && typeof error === "object" ? error as Record<string, unknown> : null;
        if (candidate?.retryable === true || this.client.state !== "ready") this.scheduleRecoveredHandoffRetry();
      } finally {
        this.recoveringWorkflowHandoffIds.delete(handoffId);
        this.renderWorkControl(this.workControl.snapshot());
      }
    }
    if (!this.pendingRecoveredHandoffIds.size) this.clearRecoveredHandoffRetry();
  }

  private async onGatewayReady(): Promise<void> {
    const readyPrincipalId = this.serverPrincipalId;
    if (!readyPrincipalId) {
      this.resetCapabilities("服务器未提供账户恢复标识");
      return;
    }
    if (this.recoveredHandoffRetryTimer) {
      window.clearTimeout(this.recoveredHandoffRetryTimer);
      this.recoveredHandoffRetryTimer = 0;
    }
    // A new or reconnected authenticated session must prove its capabilities
    // before controls or private gateway methods are re-enabled. Never inherit
    // the previous principal's snapshot.
    this.resetCapabilities("正在同步服务器能力");
    let snapshot: Record<string, unknown> | null = null;
    try {
      snapshot = await this.client.rpc<Record<string, unknown>>("capabilities/read", {});
      if (this.serverPrincipalId !== readyPrincipalId) return;
      this.publishCapabilities(snapshot);
    } catch {
      if (this.serverPrincipalId !== readyPrincipalId) return;
      this.resetCapabilities("服务器能力清单读取失败");
      toast("服务器能力清单读取失败，远程功能保持禁用", "error");
    }

    const cachedHistoryRequests: Array<Promise<void>> = [];
    if (snapshot && this.modeIsUsable("work")) cachedHistoryRequests.push(this.renderCachedThreadList("work", readyPrincipalId));
    if (snapshot && this.modeIsUsable("chat")) cachedHistoryRequests.push(this.renderCachedThreadList("chat", readyPrincipalId));
    await Promise.all(cachedHistoryRequests);
    if (this.serverPrincipalId !== readyPrincipalId) return;

    const historyRequests: Array<Promise<void>> = [];
    if (snapshot && this.modeIsUsable("work") && this.operationIsAvailable("thread/list")) {
      historyRequests.push(this.refreshThreadList("work"));
    }
    if (snapshot && this.modeIsUsable("chat") && this.operationIsAvailable("chat/thread/list")) {
      historyRequests.push(this.refreshThreadList("chat"));
    }
    await Promise.all(historyRequests);
    if (this.serverPrincipalId !== readyPrincipalId) return;
    await this.resumeRecoveredWorkflowHandoffs();
    if (this.serverPrincipalId !== readyPrincipalId) return;
    const activeChatThread = this.activeThread.get("chat");
    if (this.modeIsUsable("chat")
      && this.operationIsAvailable("chat/thread/read")
      && activeChatThread
      && !this.threadLoading.has("chat")) {
      await this.openThread("chat", activeChatThread, undefined, false, true);
    }
    if (this.uncertainChatSubmission) this.scheduleUncertainChatResume(0);
    const activeWorkThread = this.activeThread.get("work");
    if (this.modeIsUsable("work")
      && this.operationIsAvailable("thread/resume")
      && activeWorkThread
      && !this.threadLoading.has("work")) {
      this.workControl.beginResync(activeWorkThread);
      await this.openThread("work", activeWorkThread, undefined, false, true);
      await this.dispatchQueuedWork(activeWorkThread);
    }
    // connection.welcome arrives before the transport enters `ready`, so the
    // initial recovery schedule may have been intentionally ignored. Schedule
    // it again here, including journals that crashed during thread/start and
    // therefore do not have a thread id yet.
    if (this.uncertainWorkSubmission && this.modeIsUsable("work")) {
      this.scheduleUncertainWorkResume(0);
    }
    for (const threadId of this.workFollowUpQueues.keys()) {
      if (threadId !== activeWorkThread) void this.dispatchQueuedWork(threadId);
    }
  }

  private scheduleThreadListRefresh(surface: Surface, delay = 120): void {
    const current = this.threadListRefreshTimers.get(surface);
    if (current) window.clearTimeout(current);
    const timer = window.setTimeout(() => {
      this.threadListRefreshTimers.delete(surface);
      void this.refreshThreadList(surface);
    }, delay);
    this.threadListRefreshTimers.set(surface, timer);
  }

  private clearWorkTerminalReconcile(threadId: string): void {
    const timer = this.workTerminalReconcileTimers.get(threadId);
    if (timer) window.clearTimeout(timer);
    this.workTerminalReconcileTimers.delete(threadId);
    this.workTerminalReconcileAttempts.delete(threadId);
  }

  private clearWorkThreadRecoveryState(threadId: string): void {
    this.clearWorkTerminalReconcile(threadId);
    const retryTimer = this.workFollowUpRetryTimers.get(threadId);
    if (retryTimer) window.clearTimeout(retryTimer);
    this.workFollowUpRetryTimers.delete(threadId);
    this.workFollowUpRetryAttempts.delete(threadId);
    const queue = this.workFollowUpQueues.get(threadId);
    this.workFollowUpQueues.delete(threadId);
    if (this.uncertainWorkSubmission?.threadId === threadId) this.clearWorkSubmission();
    if (!this.serverPrincipalId) return;
    try {
      localStorage.removeItem(queue?.storageKey || workFollowUpQueueStorageKey(
        this.serverPrincipalId,
        this.config.projectId,
        threadId,
      ));
    } catch {
      // The authoritative server deletion still wins when local persistence is unavailable.
    }
  }

  private scheduleWorkTerminalReconcile(threadId: string, delay = 1_000): void {
    if (!threadId || this.workTerminalReconcileTimers.has(threadId)) return;
    const timer = window.setTimeout(() => {
      this.workTerminalReconcileTimers.delete(threadId);
      void this.reconcileWorkTerminal(threadId);
    }, delay);
    this.workTerminalReconcileTimers.set(threadId, timer);
  }

  private async reconcileWorkTerminal(threadId: string): Promise<void> {
    const principalId = this.serverPrincipalId;
    if (!principalId
      || this.client.state !== "ready"
      || !this.modeIsUsable("work")
      || !this.operationIsAvailable("thread/read")
      || !this.workControl.activeTurnId(threadId)) {
      this.clearWorkTerminalReconcile(threadId);
      return;
    }
    const attempt = (this.workTerminalReconcileAttempts.get(threadId) || 0) + 1;
    this.workTerminalReconcileAttempts.set(threadId, attempt);
    try {
      // Polling thread/resume has side effects in Codex app-server: every call
      // emits tokenUsage and goal metadata. thread/read is authoritative for
      // terminal reconciliation without creating more durable events.
      const response = await this.client.rpc<Record<string, unknown>>("thread/read", {
        threadId,
        includeTurns: true,
      });
      if (this.serverPrincipalId !== principalId || this.activeThread.get("work") !== threadId) return;
      const thread = response.thread && typeof response.thread === "object"
        ? response.thread as Record<string, unknown>
        : response;
      if (!Array.isArray(thread.turns)) throw new Error("服务器没有返回可核对的任务历史");
      this.rememberThreadHistory(principalId, "work", threadId, thread);
      const turns = thread.turns.filter((turn): turn is Record<string, unknown> => Boolean(turn && typeof turn === "object"));
      if (!turns.length) throw new Error("服务器任务历史暂未同步");
      this.workControl.syncThreadHistory(threadId, turns);
      if (!this.workControl.activeTurnId(threadId)) {
        this.clearWorkTerminalReconcile(threadId);
        this.scheduleThreadListRefresh("work", 0);
        return;
      }
    } catch {
      if (this.serverPrincipalId !== principalId) return;
    }
    if (attempt >= 6) {
      if (attempt === 6) toast("服务器已返回结果，但结束状态仍在同步；客户端会继续自动恢复", "error");
      this.scheduleWorkTerminalReconcile(threadId, 30_000);
      return;
    }
    this.scheduleWorkTerminalReconcile(threadId, Math.min(8_000, 600 * (2 ** attempt)));
  }

  private threadHistoryCacheKey(surface: Surface, threadId: string): string {
    return `${surface}:${threadId}`;
  }

  private async cachedThreadHistory(
    principalId: string,
    surface: Surface,
    threadId: string,
  ): Promise<{ thread: Record<string, unknown>; updatedAt: number } | null> {
    const key = this.threadHistoryCacheKey(surface, threadId);
    const memory = this.threadHistoryCache.get(key);
    if (memory) return { thread: memory, updatedAt: this.threadHistoryCacheUpdatedAt.get(key) || 0 };
    const persisted = await this.threadHistoryPersistence.getThread(principalId, surface, threadId);
    if (!persisted) return null;
    this.threadHistoryCache.set(key, persisted.thread);
    this.threadHistoryCacheUpdatedAt.set(key, persisted.updatedAt);
    return persisted;
  }

  private rememberThreadHistory(
    principalId: string,
    surface: Surface,
    threadId: string,
    thread: Record<string, unknown>,
  ): void {
    const key = this.threadHistoryCacheKey(surface, threadId);
    const updatedAt = Date.now();
    this.threadHistoryCache.set(key, thread);
    this.threadHistoryCacheUpdatedAt.set(key, updatedAt);
    this.dirtyThreadHistories.delete(key);
    void this.threadHistoryPersistence.setThread(principalId, surface, threadId, thread);
  }

  private async removeLocalThreadCache(surface: Surface, threadId: string, removeFromList = false): Promise<void> {
    const principalId = this.serverPrincipalId;
    const key = this.threadHistoryCacheKey(surface, threadId);
    this.threadHistoryCache.delete(key);
    this.threadHistoryCacheUpdatedAt.delete(key);
    this.dirtyThreadHistories.delete(key);
    if (!principalId) return;
    await this.threadHistoryPersistence.deleteThread(principalId, surface, threadId);
    if (removeFromList) await this.threadHistoryPersistence.removeThreadFromList(principalId, surface, threadId);
  }

  private async renderCachedThreadList(surface: Surface, principalId: string): Promise<void> {
    const cached = await this.threadHistoryPersistence.getList(principalId, surface);
    if (!cached || this.serverPrincipalId !== principalId) return;
    this.renderThreadList(surface, { items: cached.items });
  }

  private async refreshThreadList(surface: Surface): Promise<void> {
    const principalId = this.serverPrincipalId;
    const method = surface === "work" ? "thread/list" : "chat/thread/list";
    if (!principalId || this.client.state !== "ready" || !this.modeIsUsable(surface) || !this.operationIsAvailable(method)) return;
    const epoch = (this.threadListEpoch.get(surface) || 0) + 1;
    this.threadListEpoch.set(surface, epoch);
    const items: Record<string, unknown>[] = [];
    const seen = new Set<string>();
    let cursor: string | null = null;
    try {
      for (let page = 0; page < 4; page += 1) {
        const response = await this.client.rpc<Record<string, unknown>>(method, {
          limit: 50,
          ...(surface === "work" ? {
            cwd: this.config.workspacePath,
            sourceKinds: ["appServer", "cli", "vscode"],
            sortKey: "updated_at",
            sortDirection: "desc",
          } : {}),
          ...(cursor ? { cursor } : {}),
        });
        if (this.serverPrincipalId !== principalId || this.threadListEpoch.get(surface) !== epoch) return;
        for (const item of threadListItems(response)) {
          const threadId = String(item.id || item.threadId || "");
          if (!threadId || seen.has(threadId)) continue;
          seen.add(threadId);
          items.push(item);
        }
        cursor = threadListNextCursor(response);
        if (!cursor) break;
      }
      if (surface === "work") {
        const now = Date.now();
        for (const [threadId, pending] of this.recentlyStartedWorkThreads) {
          if (seen.has(threadId)) {
            this.recentlyStartedWorkThreads.delete(threadId);
            continue;
          }
          if (pending.expiresAt <= now) {
            this.recentlyStartedWorkThreads.delete(threadId);
            continue;
          }
          seen.add(threadId);
          items.unshift(pending.item);
        }
      }
      this.renderThreadList(surface, { items });
      void this.threadHistoryPersistence.setList(principalId, surface, items);
    } catch {
      // Keep the last successfully rendered server list. A reconnect or the
      // next authoritative thread event schedules another refresh.
    } finally {
      if (this.serverPrincipalId === principalId && this.client.state === "ready") {
        this.scheduleThreadListRefresh(surface, surface === "work" ? 15_000 : 30_000);
      }
    }
  }

  private syncThreadListSelection(surface: Surface): void {
    const activeThreadId = this.activeThread.get(surface) || "";
    const nav = document.querySelector<HTMLElement>(`[data-sidebar-mode='${surface}'] .history`);
    if (!nav) return;
    for (const button of nav.querySelectorAll<HTMLButtonElement>(".history-item[data-thread-id]")) {
      const active = Boolean(activeThreadId) && button.dataset.threadId === activeThreadId;
      button.classList.toggle("active", active);
      if (active) button.setAttribute("aria-current", "page");
      else button.removeAttribute("aria-current");
      const indicator = button.querySelector<HTMLElement>(".petal-indicator, .history-spacer");
      if (indicator) indicator.className = active ? "petal-indicator" : "history-spacer";
    }
  }

  private workPinnedStorageKey(): string | null {
    return this.serverPrincipalId ? `${this.workPinnedStoragePrefix}:${this.serverPrincipalId}` : null;
  }

  private pinnedWorkThreadIds(): Set<string> {
    const key = this.workPinnedStorageKey();
    if (!key) return new Set();
    try {
      const value = JSON.parse(localStorage.getItem(key) || "[]");
      return new Set(Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []);
    } catch {
      return new Set();
    }
  }

  private setWorkThreadPinned(threadId: string, pinned: boolean): void {
    const key = this.workPinnedStorageKey();
    if (!key) throw new Error("服务器尚未提供账户标识");
    const values = this.pinnedWorkThreadIds();
    if (pinned) values.add(threadId);
    else values.delete(threadId);
    localStorage.setItem(key, JSON.stringify([...values]));
  }

  private renderThreadList(surface: Surface, response: Record<string, unknown>): void {
    const pinnedThreadIds = surface === "work" ? this.pinnedWorkThreadIds() : new Set<string>();
    const candidates = [...threadListItems(response)].sort((left, right) => {
      if (surface !== "work") return 0;
      return Number(pinnedThreadIds.has(String(right.id || right.threadId || "")))
        - Number(pinnedThreadIds.has(String(left.id || left.threadId || "")));
    });
    const nav = document.querySelector<HTMLElement>(`[data-sidebar-mode='${surface}'] .history`);
    if (!nav) return;
    nav.replaceChildren();
    const heading = document.createElement("div");
    heading.className = "history-heading";
    const headingLabel = document.createElement("span");
    headingLabel.textContent = surface === "work" ? "服务器任务" : "服务器聊天";
    heading.append(headingLabel);
    nav.append(heading);
    if (!candidates.length) {
      const empty = document.createElement("p");
      empty.className = "runtime-history-empty";
      empty.textContent = surface === "work" ? "暂无远程任务" : "暂无聊天记录";
      nav.append(empty);
      return;
    }
    const activeThreadId = this.activeThread.get(surface) || "";
    for (const candidate of candidates) {
      const thread = candidate;
      const threadId = String(thread.id || thread.threadId || "");
      if (!threadId) continue;
      const active = threadId === activeThreadId;
      const button = document.createElement("button");
      button.type = "button";
      button.className = `history-item${active ? " active" : ""}`;
      button.dataset.threadId = threadId;
      button.dataset.pinned = String(pinnedThreadIds.has(threadId));
      if (active) button.setAttribute("aria-current", "page");
      const indicator = document.createElement("span");
      indicator.className = active ? "petal-indicator" : "history-spacer";
      indicator.setAttribute("aria-hidden", "true");
      const copy = document.createElement("span");
      const title = document.createElement("strong");
      title.textContent = String(thread.name || thread.title || thread.preview || thread.firstUserMessage || "未命名会话");
      const detail = document.createElement("small");
      detail.textContent = String(thread.name || thread.title
        ? thread.preview || thread.status || thread.cwd || "服务器已保存"
        : thread.status || thread.cwd || "服务器已保存");
      copy.append(title, detail);
      button.append(indicator, copy);
      button.addEventListener("click", () => this.openThread(surface, threadId, button));
      button.addEventListener("contextmenu", (event) => {
        event.preventDefault();
        this.openThreadContextMenu(surface, threadId, title.textContent || "未命名任务", button, event.clientX, event.clientY);
      });
      button.addEventListener("keydown", (event) => {
        if (!(event.key === "ContextMenu" || (event.shiftKey && event.key === "F10"))) return;
        event.preventDefault();
        const bounds = button.getBoundingClientRect();
        this.openThreadContextMenu(surface, threadId, title.textContent || "未命名任务", button, bounds.left + 24, bounds.top + 24);
      });
      nav.append(button);
    }
  }

  private closeThreadContextMenu(restoreFocus = false): void {
    const restoreTarget = restoreFocus
      ? this.threadContextMenu?.dataset.restoreThreadId
      : undefined;
    this.threadContextMenuCleanup?.();
    this.threadContextMenuCleanup = null;
    this.threadContextMenu?.remove();
    this.threadContextMenu = null;
    if (restoreTarget) {
      document.querySelector<HTMLButtonElement>(`.history-item[data-thread-id="${CSS.escape(restoreTarget)}"]`)?.focus();
    }
  }

  private openThreadContextMenu(
    surface: Surface,
    threadId: string,
    title: string,
    trigger: HTMLButtonElement,
    clientX: number,
    clientY: number,
  ): void {
    this.closeThreadContextMenu();
    const menu = document.createElement("div");
    menu.className = "runtime-context-menu";
    menu.setAttribute("role", "menu");
    menu.setAttribute("aria-label", surface === "work" ? "任务操作" : "聊天操作");
    menu.dataset.restoreThreadId = threadId;
    const active = surface === "work"
      ? Boolean(this.workControl.activeTurnId(threadId))
      : this.chatControl.snapshot().threadId === threadId && Boolean(this.chatControl.snapshot().turnId);
    const addItem = (
      label: string,
      action: () => void,
      options: { disabled?: boolean; title?: string; danger?: boolean } = {},
    ): HTMLButtonElement => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("role", "menuitem");
      button.textContent = label;
      button.disabled = Boolean(options.disabled);
      button.title = options.title || "";
      if (options.danger) button.className = "runtime-context-menu-danger";
      button.addEventListener("click", action);
      menu.append(button);
      return button;
    };

    if (surface === "work") {
      addItem("重命名", () => {
        this.closeThreadContextMenu();
        this.showRenameThreadDialog(threadId, title, trigger);
      }, {
        disabled: !this.operationIsAvailable("thread/name/set"),
        title: this.operationIsAvailable("thread/name/set") ? "" : "服务器没有声明重命名能力",
      });
      const pinned = this.pinnedWorkThreadIds().has(threadId);
      addItem(pinned ? "取消置顶" : "置顶任务", () => {
        this.closeThreadContextMenu();
        try {
          this.setWorkThreadPinned(threadId, !pinned);
          this.scheduleThreadListRefresh("work", 0);
          toast(pinned ? "已取消置顶" : "任务已置顶", "success");
        } catch (error) {
          toast(error instanceof Error ? error.message : "置顶状态保存失败", "error");
        }
      });
      addItem("复制为新任务", () => {
        this.closeThreadContextMenu();
        void this.forkThread(threadId, title);
      }, {
        disabled: active || !this.operationIsAvailable("thread/fork"),
        title: active ? "任务运行时不能复制" : this.operationIsAvailable("thread/fork") ? "" : "服务器没有声明复制能力",
      });
      addItem("归档任务", () => {
        this.closeThreadContextMenu();
        void this.archiveThread(threadId);
      }, {
        disabled: active || !this.operationIsAvailable("thread/archive"),
        title: active ? "请先停止正在运行的任务" : this.operationIsAvailable("thread/archive") ? "" : "服务器没有声明归档能力",
      });
      const separator = document.createElement("hr");
      separator.setAttribute("role", "separator");
      menu.append(separator);
    }
    addItem("删除本地缓存", () => {
      this.closeThreadContextMenu();
      void this.removeLocalThreadCache(surface, threadId).then(() => {
        toast("本地缓存已删除；服务器记录仍然保留", "success");
      }).catch(() => {
        toast("本地缓存删除失败，请稍后重试", "error");
      });
    }, {
      title: "仅删除这台设备上的内容副本，服务器记录不会删除",
    });
    const serverDeleteSeparator = document.createElement("hr");
    serverDeleteSeparator.setAttribute("role", "separator");
    menu.append(serverDeleteSeparator);
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "runtime-context-menu-danger";
    remove.setAttribute("role", "menuitem");
    remove.textContent = surface === "work" ? "删除任务" : "删除聊天";
    const operation = surface === "work" ? "thread/delete" : "chat/thread/delete";
    remove.disabled = active || !this.operationIsAvailable(operation);
    remove.title = active
      ? "请先停止正在运行的任务"
      : remove.disabled ? "服务器没有声明删除能力" : "";
    remove.addEventListener("click", () => {
      this.closeThreadContextMenu();
      this.confirmDeleteThread(surface, threadId, title, trigger);
    });
    menu.append(remove);
    document.body.append(menu);
    const bounds = menu.getBoundingClientRect();
    menu.style.left = `${Math.max(8, Math.min(clientX, window.innerWidth - bounds.width - 8))}px`;
    menu.style.top = `${Math.max(8, Math.min(clientY, window.innerHeight - bounds.height - 8))}px`;
    this.threadContextMenu = menu;
    const onPointerDown = (event: PointerEvent) => {
      if (!menu.contains(event.target as Node)) this.closeThreadContextMenu();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeThreadContextMenu(true);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    this.threadContextMenuCleanup = () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
    menu.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
  }

  private showRenameThreadDialog(threadId: string, currentTitle: string, trigger: HTMLButtonElement): void {
    const dialog = this.createInfoDialog("重命名任务", "名称会同步到服务器，并显示在所有客户端的任务列表中。");
    const card = dialog.querySelector<HTMLElement>(".runtime-dialog-card");
    const actions = dialog.querySelector<HTMLElement>(".runtime-dialog-actions");
    if (!card || !actions) return;
    const fields = document.createElement("div");
    fields.className = "runtime-input-list";
    const label = document.createElement("label");
    label.textContent = "任务名称";
    const input = document.createElement("input");
    input.type = "text";
    input.maxLength = 160;
    input.value = currentTitle;
    input.autocomplete = "off";
    input.setAttribute("aria-label", "任务名称");
    label.append(input);
    fields.append(label);
    card.insertBefore(fields, actions);
    const save = document.createElement("button");
    save.type = "button";
    save.className = "runtime-primary";
    save.textContent = "保存";
    const submit = async () => {
      const name = input.value.trim();
      if (!name) {
        toast("任务名称不能为空", "error");
        input.focus();
        return;
      }
      save.disabled = true;
      try {
        await this.client.rpc("thread/name/set", { threadId, name });
        dialog.close();
        this.scheduleThreadListRefresh("work", 0);
        toast("任务名称已更新", "success");
      } catch (error) {
        save.disabled = false;
        toast(error instanceof Error ? error.message : "任务重命名失败", "error");
      }
    };
    save.addEventListener("click", () => void submit());
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.isComposing) {
        event.preventDefault();
        void submit();
      }
    });
    actions.append(save);
    dialog.addEventListener("close", () => {
      if (trigger.isConnected) queueMicrotask(() => trigger.focus());
    }, { once: true });
    dialog.showModal();
    input.select();
  }

  private async forkThread(threadId: string, title: string): Promise<void> {
    try {
      const result = await this.client.rpc<Record<string, unknown>>("thread/fork", {
        threadId,
        ...threadExecutionPolicyParams(this.selectedWorkExecutionPolicy),
      });
      const forkedThreadId = resultId(result, "thread");
      if (!forkedThreadId) throw new Error("服务器没有返回新任务标识");
      if (this.operationIsAvailable("thread/name/set")) {
        await this.client.rpc("thread/name/set", { threadId: forkedThreadId, name: `${title} · 副本` });
      }
      this.scheduleThreadListRefresh("work", 0);
      await this.openThread("work", forkedThreadId, undefined, false);
      toast("已复制为新任务", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "复制任务失败", "error");
    }
  }

  private async archiveThread(threadId: string): Promise<void> {
    try {
      await this.client.rpc("thread/archive", { threadId });
      if (this.activeThread.get("work") === threadId) {
        this.clearWorkThreadRecoveryState(threadId);
        this.setActiveThread("work", null);
        this.workControl.selectThread(null);
        const canvas = canvasFor("work");
        for (const message of canvas.querySelectorAll(".message")) message.remove();
      }
      this.scheduleThreadListRefresh("work", 0);
      toast("任务已归档", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "归档任务失败", "error");
    }
  }

  private confirmDeleteThread(surface: Surface, threadId: string, title: string, trigger: HTMLButtonElement): void {
    const dialog = this.createInfoDialog(
      surface === "work" ? "删除这个任务？" : "删除这个聊天？",
      `“${title}”将从服务器历史中永久删除，此操作无法撤销。`,
    );
    const actions = dialog.querySelector<HTMLElement>(".runtime-dialog-actions");
    const close = actions?.querySelector<HTMLButtonElement>(".runtime-secondary");
    if (close) close.textContent = "取消";
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "runtime-danger";
    remove.textContent = "删除";
    remove.addEventListener("click", async () => {
      remove.disabled = true;
      remove.setAttribute("aria-busy", "true");
      try {
        await this.deleteThread(surface, threadId);
        dialog.close();
        toast(surface === "work" ? "任务已删除" : "聊天已删除", "success");
      } catch (error) {
        remove.disabled = false;
        remove.removeAttribute("aria-busy");
        toast(error instanceof Error ? error.message : "删除失败，请稍后重试", "error");
      }
    });
    actions?.append(remove);
    dialog.addEventListener("close", () => {
      if (trigger.isConnected) queueMicrotask(() => trigger.focus());
    }, { once: true });
    dialog.showModal();
    close?.focus();
  }

  private async deleteThread(surface: Surface, threadId: string): Promise<void> {
    const method = surface === "work" ? "thread/delete" : "chat/thread/delete";
    if (!this.operationIsAvailable(method)) throw new Error("服务器没有声明删除能力");
    await this.client.rpc(method, { threadId });
    if (surface === "work") this.recentlyStartedWorkThreads.delete(threadId);
    await this.removeLocalThreadCache(surface, threadId, true);
    document.querySelector<HTMLElement>(`[data-sidebar-mode='${surface}'] .history-item[data-thread-id='${CSS.escape(threadId)}']`)?.remove();
    if (surface === "work") this.clearWorkThreadRecoveryState(threadId);
    if (this.activeThread.get(surface) === threadId) {
      this.setActiveThread(surface, null);
      if (surface === "work") {
        this.workControl.selectThread(null);
      } else {
        this.chatControl.reset();
      }
      const canvas = canvasFor(surface);
      for (const message of canvas.querySelectorAll(".message")) message.remove();
      for (const empty of canvas.querySelectorAll(".runtime-empty-state")) empty.remove();
      const empty = createAssistantMessage(surface, "服务器记录");
      empty.body.textContent = surface === "work" ? "任务已删除。可以新建任务继续工作。" : "聊天已删除。可以开始新的聊天。";
    }
    await this.refreshThreadList(surface);
  }

  private clearMissingThread(surface: Surface, threadId: string): void {
    void this.removeLocalThreadCache(surface, threadId, true);
    if (surface === "work") this.clearWorkThreadRecoveryState(threadId);
    if (surface === "chat" && this.uncertainChatSubmission?.threadId === threadId) this.clearChatSubmission();
    this.setActiveThread(surface, null);
    const canvas = canvasFor(surface);
    for (const message of canvas.querySelectorAll(".message")) message.remove();
    for (const empty of canvas.querySelectorAll(".runtime-empty-state")) empty.remove();
    const empty = document.createElement("p");
    empty.className = "runtime-empty-state";
    empty.textContent = surface === "work"
      ? "这个任务已不存在。请选择其他任务或新建任务。"
      : "这个聊天已不存在。请选择其他聊天或新建聊天。";
    canvas.append(empty);
    this.scheduleThreadListRefresh(surface, 0);
  }

  private async openThread(
    surface: Surface,
    threadId: string,
    button?: HTMLButtonElement,
    activateMode = true,
    forceServerRefresh = false,
  ): Promise<void> {
    if (this.client.state !== "ready") return;
    const readOperation = surface === "work" ? "thread/resume" : "chat/thread/read";
    if (!this.modeIsUsable(surface) || !this.operationIsAvailable(readOperation)) {
      toast(this.modeReasons.get(surface) || "服务器没有声明读取此会话的能力", "error");
      return;
    }
    const activeChat = this.chatControl.snapshot();
    if (surface === "chat" && activeChat.turnId && activeChat.threadId && activeChat.threadId !== threadId) {
      toast("当前聊天仍在生成；请先停止或等待完成再切换会话", "error");
      return;
    }
    const epoch = (this.threadOpenEpoch.get(surface) || 0) + 1;
    this.threadOpenEpoch.set(surface, epoch);
    this.threadLoading.add(surface);
    if (button) {
      for (const peer of button.closest(".history")?.querySelectorAll(".history-item") || []) peer.classList.remove("active");
      button.classList.add("active");
    }
    this.setActiveThread(surface, threadId);
    if (surface === "work") this.workControl.beginResync(threadId);
    if (activateMode) (document.querySelector(`[data-mode-target='${surface}']`) as HTMLButtonElement | null)?.click();
    const principalId = this.serverPrincipalId;
    if (!principalId) return;
    const cacheKey = this.threadHistoryCacheKey(surface, threadId);
    const cached = await this.cachedThreadHistory(principalId, surface, threadId);
    if (this.threadOpenEpoch.get(surface) !== epoch || this.activeThread.get(surface) !== threadId) return;
    if (cached) {
      this.renderThreadHistory(surface, cached.thread);
    } else {
      const canvas = canvasFor(surface);
      for (const message of canvas.querySelectorAll(".message")) message.remove();
      for (const empty of canvas.querySelectorAll(".runtime-empty-state")) empty.remove();
      const loading = document.createElement("p");
      loading.className = "runtime-empty-state runtime-thread-loading";
      loading.textContent = surface === "work" ? "正在从服务器读取任务…" : "正在从服务器读取聊天…";
      canvas.append(loading);
    }
    const cacheIsFresh = Boolean(
      cached
      && !forceServerRefresh
      && !this.dirtyThreadHistories.has(cacheKey)
      && Date.now() - cached.updatedAt < THREAD_HISTORY_FRESH_MS,
    );
    if (cacheIsFresh) {
      this.threadLoading.delete(surface);
      if (surface === "work") this.renderWorkControl(this.workControl.snapshot());
      return;
    }
    try {
      const response = await this.client.rpc<Record<string, unknown>>(
        surface === "work" ? "thread/resume" : "chat/thread/read",
        {
          threadId,
          ...(surface === "work" ? threadExecutionPolicyParams(this.selectedWorkExecutionPolicy) : {}),
        },
      );
      if (this.threadOpenEpoch.get(surface) !== epoch || this.activeThread.get(surface) !== threadId) return;
      const resolvedThread = response.thread && typeof response.thread === "object"
        ? response.thread as Record<string, unknown>
        : response;
      this.rememberThreadHistory(principalId, surface, threadId, resolvedThread);
      this.renderThreadHistory(surface, resolvedThread);
    } catch (error) {
      if (this.threadOpenEpoch.get(surface) === epoch && this.activeThread.get(surface) === threadId) {
        if (threadReadFailureIsMissing(error)) {
          this.clearMissingThread(surface, threadId);
          toast(surface === "work" ? "这个任务已被删除，已清除本机恢复记录" : "这个聊天已被删除，已清除本机恢复记录", "error");
          return;
        }
        const message = error instanceof Error ? error.message : "读取会话失败";
        if (surface === "work" && this.uncertainWorkSubmission?.threadId === threadId) {
          this.workControl.beginResync(threadId);
          this.scheduleUncertainWorkResume();
        } else if (surface === "work") {
          this.workControl.resyncFailed(threadId, message);
        }
        toast(message, "error");
      }
    } finally {
      if (this.threadOpenEpoch.get(surface) === epoch) {
        this.threadLoading.delete(surface);
        if (surface === "work") this.renderWorkControl(this.workControl.snapshot());
      }
    }
  }

  private renderThreadHistory(surface: Surface, thread: Record<string, unknown>): void {
    const renderedThreadId = workflowIdentifier(thread.id || thread.threadId);
    const activeThreadId = this.activeThread.get(surface) || "";
    if (renderedThreadId && activeThreadId && renderedThreadId !== activeThreadId) return;
    const turns = Array.isArray(thread.turns) ? thread.turns : [];
    if (surface === "chat") {
      const threadId = String(thread.id || this.activeThread.get("chat") || "");
      const chatTurns = turns.filter((turn): turn is Record<string, unknown> => Boolean(turn && typeof turn === "object"));
      const journal = this.uncertainChatSubmission;
      const journalConfirmed = Boolean(journal && chatTurns.some((turn) => Array.isArray(turn.items) && turn.items.some((candidate) => {
        if (!candidate || typeof candidate !== "object") return false;
        const item = candidate as Record<string, unknown>;
        return item.type === "userMessage"
          && String(item.clientId || item.clientMessageId || item.clientUserMessageId || "") === journal.clientMessageId;
      })));
      if (journal && journalConfirmed) {
        this.clearRestoredChatComposer(journal);
        if (journal.operation !== "chat/message/regenerate") {
          this.attachments.set("chat", []);
          this.updateAttachmentChips("chat");
        }
        this.clearChatSubmission(journal.clientMessageId);
      }
      const activeTurn = [...chatTurns].reverse().find((turn) => ["inProgress", "in_progress", "running"].includes(String(turn.status || "")));
      const activeTurnId = workflowIdentifier(activeTurn?.id);
      if (threadId && activeTurnId) {
        this.chatControl.runStarted(threadId, activeTurnId);
      } else {
        const latest = chatTurns.at(-1);
        const latestTurnId = workflowIdentifier(latest?.id);
        const latestStatus = String(latest?.status || "");
        if (threadId && latestTurnId && ["completed", "interrupted", "failed"].includes(latestStatus)) {
          this.chatControl.runCompleted(threadId, latestTurnId, latestStatus, textFrom(latest?.error));
        } else if (!activeTurnId && this.uncertainChatSubmission) {
          this.chatControl.submissionUncertain("正在恢复上次结果未知的聊天请求");
          this.scheduleUncertainChatResume();
        } else if (!activeTurnId) {
          this.chatControl.reset();
        }
      }
    }
    if (surface === "work") {
      const threadId = String(thread.id || this.activeThread.get("work") || "");
      const workTurns = turns.filter((turn): turn is Record<string, unknown> => Boolean(turn && typeof turn === "object"));
      if (threadId) {
        this.workControl.syncThreadHistory(threadId, workTurns);
        if (this.workControl.activeTurnId(threadId)) this.scheduleWorkTerminalReconcile(threadId, 1_200);
        else this.clearWorkTerminalReconcile(threadId);
      }
      if (threadId && this.serverPrincipalId) {
        try {
          const queue = this.workFollowUpQueue(threadId);
          const snapshot = queue.snapshot();
          const blocking = snapshot.blockingTurnId
            ? workTurns.find((turn) => turn.id === snapshot.blockingTurnId && ["completed", "interrupted", "failed"].includes(String(turn.status || "")))
            : undefined;
          if (blocking && snapshot.blockingTurnId) queue.noteTurnCompleted(snapshot.blockingTurnId);
          const inFlight = queue.inFlight();
          if (inFlight) {
            const acceptedTurn = workTurns.find((turn) => Array.isArray(turn.items) && turn.items.some((candidate) => {
              if (!candidate || typeof candidate !== "object") return false;
              const item = candidate as Record<string, unknown>;
              return item.type === "userMessage"
                && String(item.clientId || item.clientUserMessageId || "") === inFlight.clientMessageId;
            }));
            const acceptedTurnId = workflowIdentifier(acceptedTurn?.id);
            if (acceptedTurnId && ["completed", "interrupted", "failed"].includes(String(acceptedTurn?.status || ""))) {
              queue.noteTurnCompleted(acceptedTurnId);
            }
          }
          queueMicrotask(() => {
            this.renderQueuedWorkMessages(queue);
            void this.dispatchQueuedWork(threadId);
          });
        } catch (error) {
          toast(error instanceof Error ? error.message : "下一轮队列恢复失败", "error");
        }
      }
      const journal = this.uncertainWorkSubmission;
      if (threadId && journal?.threadId === threadId) {
        const resolution = resolveWorkJournalAgainstHistory(
          journal,
          workTurns,
          this.uncertainWorkAttempt,
        );
        if (resolution === "confirmed") {
          this.confirmUncertainWorkSubmission(journal);
        } else if (resolution === "notAccepted") {
          const prompt = document.querySelector<HTMLTextAreaElement>("#work-prompt");
          if (prompt) restoreComposerText(prompt, journal.text);
          this.clearWorkSubmission(journal.clientMessageId, "failed");
          this.workControl.abandonResync(threadId);
          toast("服务器已确认上一条工作指令未被接收，已自动解除锁定；原内容已保留", "success");
        } else {
          this.workControl.beginResync(threadId);
          this.scheduleUncertainWorkResume();
        }
      }
    }
    const canvas = canvasFor(surface);
    for (const [id, renderer] of this.renderers) {
      if (!renderer.article.isConnected || canvas.contains(renderer.article)) this.renderers.delete(id);
    }
    for (const empty of canvas.querySelectorAll(".runtime-empty-state")) empty.remove();
    for (const message of canvas.querySelectorAll(".message")) message.remove();
    for (const turn of turns) {
      if (!turn || typeof turn !== "object") continue;
      const items = Array.isArray((turn as Record<string, unknown>).items)
        ? (turn as Record<string, unknown>).items as Array<Record<string, unknown>>
        : [];
      for (const item of items) {
        const type = String(item.type || "");
        if (type === "userMessage") {
          const text = textFrom(item);
          const userAttachments = messageAttachmentsFromUserItem(item);
          if (text || userAttachments.length) {
            const rendered = renderUserMessage(surface, text);
            this.decorateMessage(rendered, surface, "user", workflowIdentifier(item.id), text);
            renderMessageAttachments(rendered, userAttachments);
          }
          continue;
        }
        const renderer = createAssistantMessage(surface, type === "agentMessage" ? "已完成" : "服务器记录");
        if (type === "agentMessage") {
          const text = textFrom(item) || "已完成。";
          renderRichText(renderer.body, text);
          this.decorateMessage(renderer.article, surface, "assistant", workflowIdentifier(item.id), text);
        } else {
          renderStructuredItem(renderer, item);
        }
      }
    }
    if (!turns.length) {
      const empty = createAssistantMessage(surface, "服务器记录");
      empty.body.textContent = "这个会话目前还没有消息。";
    }
    if (surface === "chat" && this.uncertainChatSubmission && !this.optimisticChatMessage(this.uncertainChatSubmission.clientMessageId)) {
      const journal = this.uncertainChatSubmission;
      if (journal.operation !== "chat/message/regenerate") {
        const optimistic = renderUserMessage("chat", journal.text, {
          clientMessageId: journal.clientMessageId,
          intent: "start",
        });
        renderMessageAttachments(optimistic, journal.attachments);
        if (journal.messageId) optimistic.dataset.editedMessageId = journal.messageId;
        const delivery = optimistic.querySelector<HTMLElement>(".runtime-delivery-state");
        if (delivery) delivery.textContent = "结果待确认 · 正在用同一请求恢复";
      }
    }
    if (surface === "work" && this.uncertainWorkSubmission && !canvas.querySelector(
      `[data-client-message-id='${CSS.escape(this.uncertainWorkSubmission.clientMessageId)}']`,
    )) {
      const journal = this.uncertainWorkSubmission;
      const optimistic = renderUserMessage("work", journal.text, {
        clientMessageId: journal.clientMessageId,
        intent: journal.intent,
        createdAt: journal.createdAt,
      });
      renderMessageAttachments(optimistic, messageAttachmentsFromInputs(journal.input));
      const delivery = optimistic.querySelector<HTMLElement>(".runtime-delivery-state");
      if (delivery) delivery.textContent = "结果待确认 · 正在用同一请求恢复";
    }
  }

  private bindStaticActions(): void {
    document.querySelector(".server-state")?.addEventListener("click", () => this.showConnectionDialog());
    document.querySelector("[data-app-action='settings']")?.addEventListener("click", () => this.showConnectionDialog());
    for (const search of document.querySelectorAll("[data-app-action='search']")) search.addEventListener("click", () => this.showSearchDialog());
    document.querySelector("[data-app-action='new-chat']")?.addEventListener("click", () => this.newSession("chat"));
    document.querySelector("[data-app-action='new-work']")?.addEventListener("click", () => this.newSession("work"));
    document.querySelector(".model-button")?.addEventListener("click", () => this.openServerList("chat/model/list", "聊天模型"));
    for (const workspace of document.querySelectorAll("[data-app-action='workspace'], [data-app-action='add-workspace']")) {
      workspace.addEventListener("click", () => void this.openWorkspacePicker());
    }
    document.querySelector("[data-app-action='permissions']")?.addEventListener("click", () => void this.openWorkPermissionPicker());
    document.querySelector("[data-app-action='models']")?.addEventListener("click", () => void this.openWorkModelPicker());
    document.querySelector("[data-app-action='account']")?.addEventListener("click", () => {
      if (window.BianhuaAuth) {
        window.BianhuaAuth.openAccount();
        return;
      }
      if (this.client.state === "ready") this.openServerList("account/read", "账户与订阅");
      else this.showConnectionDialog();
    });
    document.querySelector("[data-app-action='history-options']")?.addEventListener("click", () => this.showSearchDialog());
    document.querySelector("[data-app-action='chat-options']")?.addEventListener("click", () => this.openServerList("capabilities/read", "聊天能力"));
    document.querySelector("[data-app-action='task-options']")?.addEventListener("click", () => this.openServerList("thread/read", "任务详情"));
    document.querySelector(".change-button")?.addEventListener("click", () => this.openServerList("thread/read", "当前变更"));
    document.querySelector(".trace-detail")?.addEventListener("click", () => this.openServerList("thread/read", "执行记录"));
    document.querySelector("[data-work-stop]")?.addEventListener("click", () => void this.interruptCurrentWorkTurn());
    document.querySelector("[data-chat-stop]")?.addEventListener("click", () => void this.interruptCurrentChatTurn());
  }

  private async interruptCurrentChatTurn(): Promise<void> {
    if (!this.operationIsAvailable("chat/turn/interrupt")) {
      toast("当前聊天服务没有声明停止能力", "error");
      return;
    }
    const active = this.chatControl.beginInterrupt();
    if (!active) return;
    try {
      await this.client.rpc("chat/turn/interrupt", active);
      this.chatControl.interruptRequestAccepted(active.threadId, active.turnId);
      toast("停止请求已送达，正在等待服务器确认结束");
    } catch (error) {
      const message = error instanceof Error ? error.message : "停止生成失败";
      this.chatControl.interruptRequestFailed(active.threadId, active.turnId, message);
      toast(message, "error");
    }
  }

  private async interruptCurrentWorkTurn(): Promise<void> {
    const threadId = this.activeThread.get("work");
    const turnId = threadId ? this.workControl.activeTurnId(threadId) : null;
    if (!threadId || !turnId) {
      toast("当前没有正在运行的任务");
      return;
    }
    if (!this.workControl.beginInterrupt(threadId)) return;
    const request = buildWorkInterrupt(threadId, turnId);
    try {
      await this.client.rpc(request.method, request.params);
      this.workControl.interruptRequestAccepted(threadId, turnId);
      toast("停止请求已送达，正在等待服务器确认结束");
    } catch (error) {
      const message = error instanceof Error ? error.message : "停止请求失败";
      this.workControl.interruptRequestFailed(threadId, turnId, message);
      toast(message, "error");
    }
  }

  private bindKeyboardShortcuts(): void {
    document.addEventListener("keydown", (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "n") {
        event.preventDefault();
        const mode = document.body.dataset.mode;
        if (mode === "video") {
          toast("视频项目的新建接口尚未接入，当前任务不会被静默清除", "error");
          return;
        }
        this.newSession(mode === "work" ? "work" : "chat");
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "g" && !event.shiftKey) {
        event.preventDefault();
        this.showSearchDialog();
      }
      if ((event.ctrlKey || event.metaKey) && event.key === ",") {
        event.preventDefault();
        this.showConnectionDialog();
      }
    });
  }

  private newSession(surface: Surface): void {
    const modeStatus = this.modeStatuses.get(surface);
    if (this.client.state === "ready" && ["unavailable", "unsupported", "auth_required"].includes(String(modeStatus))) {
      toast(this.modeReasons.get(surface) || "当前服务器未提供此模式", "error");
      return;
    }
    const requiredOperation = surface === "chat" ? "chat/message/create" : "thread/start";
    if (this.client.state !== "ready" || !this.modeIsUsable(surface) || !this.operationIsAvailable(requiredOperation)) {
      toast(this.modeReasons.get(surface) || "当前服务器未提供新建会话能力", "error");
      return;
    }
    if (surface === "chat" && !this.operationIsAvailable("gateway/idempotentChatCreate")) {
      toast("聊天服务缺少断线幂等恢复能力，不能安全新建会话", "error");
      return;
    }
    if (surface === "chat" && (this.chatControl.snapshot().turnId || this.chatControl.snapshot().submitting)) {
      toast("当前聊天仍在生成；请先停止或等待完成", "error");
      return;
    }
    if (surface === "work" && this.workRequestInFlight) {
      toast("当前指令仍在确认，请等待服务器返回后再新建任务");
      return;
    }
    if (surface === "work" && this.uncertainWorkSubmission) {
      toast("上一条工作指令结果尚未确认；为避免重复执行，暂不能新建任务", "error");
      return;
    }
    this.threadOpenEpoch.set(surface, (this.threadOpenEpoch.get(surface) || 0) + 1);
    this.threadLoading.delete(surface);
    this.setActiveThread(surface, null);
    const canvas = canvasFor(surface);
    for (const message of canvas.querySelectorAll(".message")) message.remove();
    const previousEmpty = canvas.querySelector<HTMLElement>(".runtime-empty-state");
    if (previousEmpty) previousEmpty.textContent = surface === "work"
      ? "输入工作指令后，服务器会建立新任务。"
      : "输入内容后开始新聊天。";
    else {
      const empty = document.createElement("p");
      empty.className = "runtime-empty-state";
      empty.textContent = surface === "work"
        ? "输入工作指令后，服务器会建立新任务。"
        : "输入内容后开始新聊天。";
      canvas.append(empty);
    }
    const prompt = document.querySelector<HTMLTextAreaElement>(surface === "work" ? "#work-prompt" : "#chat-prompt");
    if (surface === "chat" && prompt) {
      delete prompt.dataset.editMessageId;
      delete prompt.dataset.editClientMessageId;
    }
    prompt?.focus();
  }

  private showConnectionDialog(): void {
    const dialog = this.createInfoDialog("连接与保存", this.config.gatewayUrl ? `服务器：${new URL(this.config.gatewayUrl).host}` : "尚未配置服务器网关");
    const card = dialog.querySelector(".runtime-dialog-card")!;
    const list = document.createElement("dl");
    list.className = "runtime-status-list";
    const platform = getClientPlatform();
    const values = [
      ["连接状态", this.client.state === "ready" ? "已连接" : this.client.state === "offline" ? "未连接" : "连接中"],
      ["客户端", platform === "android" ? "Android" : platform === "windows" ? "Windows" : "网页"],
      ["产物目录", platform === "android"
        ? "应用私有存储（可通过系统保存器导出）"
        : platform === "windows"
          ? `D:\\${this.config.projectName}\\`
          : "需授权浏览器文件夹"],
      ["服务器副本", this.client.state === "ready" ? "按服务器产物清单确认保留" : "未确认（连接后以清单为准）"],
    ];
    for (const [term, value] of values) {
      const dt = document.createElement("dt");
      const dd = document.createElement("dd");
      dt.textContent = term;
      dd.textContent = value;
      list.append(dt, dd);
    }
    card.insertBefore(list, card.querySelector(".runtime-dialog-actions"));
    const followUpSettings = document.createElement("div");
    followUpSettings.className = "runtime-input-list";
    const followUpLabel = document.createElement("label");
    const followUpTitle = document.createElement("span");
    followUpTitle.textContent = "任务运行中再次发送";
    const followUpSelect = document.createElement("select");
    followUpSelect.name = "work_follow_up_behavior";
    followUpSelect.setAttribute("aria-label", "任务运行中再次发送的处理方式");
    const steer = document.createElement("option");
    steer.value = "steer-current";
    steer.textContent = "立即引导当前任务";
    const wait = document.createElement("option");
    wait.value = "wait-for-next";
    wait.textContent = "排队，当前任务完成后开始下一轮";
    wait.disabled = !this.operationIsAvailable("gateway/idempotentTurnStart")
      || !this.operationIsAvailable("turn/start");
    followUpSelect.append(steer, wait);
    followUpSelect.value = this.workFollowUpBehavior;
    followUpSelect.disabled = !this.serverPrincipalId;
    followUpSelect.addEventListener("change", () => {
      const next = followUpSelect.value === "wait-for-next" ? "wait-for-next" : "steer-current";
      if (!this.setWorkFollowUpBehavior(next)) {
        followUpSelect.value = this.workFollowUpBehavior;
        toast(next === "wait-for-next"
          ? "服务器未提供可恢复的下一轮队列，设置没有更改"
          : "设置未能持久保存", "error");
        return;
      }
      toast(next === "wait-for-next" ? "后续指令将排队到下一轮" : "后续指令将立即引导当前任务", "success");
    });
    followUpLabel.append(followUpTitle, followUpSelect);
    const submitLabel = document.createElement("label");
    const submitTitle = document.createElement("span");
    submitTitle.textContent = "输入框发送快捷键";
    const submitSelect = document.createElement("select");
    submitSelect.name = "composer_submit_shortcut";
    submitSelect.setAttribute("aria-label", "输入框发送快捷键");
    const enterOption = document.createElement("option");
    enterOption.value = "enter";
    enterOption.textContent = "Enter 发送，Shift + Enter 换行";
    const modifierOption = document.createElement("option");
    modifierOption.value = "modifier-enter";
    modifierOption.textContent = "Ctrl/⌘ + Enter 发送，Enter 换行";
    submitSelect.append(enterOption, modifierOption);
    submitSelect.value = this.composerSubmitShortcut;
    submitSelect.disabled = !this.serverPrincipalId;
    submitSelect.addEventListener("change", () => {
      const next = submitSelect.value === "modifier-enter" ? "modifier-enter" : "enter";
      if (!this.setComposerSubmitShortcut(next)) {
        submitSelect.value = this.composerSubmitShortcut;
        toast("输入快捷键设置未能持久保存", "error");
      }
    });
    submitLabel.append(submitTitle, submitSelect);
    const followUpHint = document.createElement("small");
    followUpHint.textContent = wait.disabled
      ? "下一轮模式要求服务器声明幂等恢复能力；当前仅可引导正在运行的任务。"
      : "队列使用同一请求标识恢复，服务器明确接受后才会移除。";
    followUpSettings.append(followUpLabel, submitLabel, followUpHint);
    card.insertBefore(followUpSettings, card.querySelector(".runtime-dialog-actions"));
    if (platform === "web") {
      const choose = document.createElement("button");
      choose.type = "button";
      choose.className = "runtime-primary";
      choose.textContent = "选择网页保存文件夹";
      choose.addEventListener("click", () => this.artifacts.chooseWebDirectory()
        .then((granted) => toast(granted ? "网页保存目录已授权" : "未授予写入权限", granted ? "success" : "error"))
        .catch((error) => toast(error instanceof Error ? error.message : "目录授权失败", "error")));
      card.querySelector(".runtime-dialog-actions")?.prepend(choose);
    }
    dialog.showModal();
  }

  private showSearchDialog(): void {
    if (this.client.state !== "ready" || !this.operationIsAvailable("session/search")) {
      toast("当前服务器未提供统一搜索能力", "error");
      return;
    }
    const dialog = this.createInfoDialog("搜索", "搜索服务器上的聊天、任务与工作区");
    const card = dialog.querySelector(".runtime-dialog-card")!;
    const input = document.createElement("input");
    input.className = "runtime-search";
    input.type = "search";
    input.name = "session_search";
    input.autocomplete = "off";
    input.setAttribute("aria-label", "搜索聊天、任务与工作区");
    input.placeholder = "输入关键词…";
    const results = document.createElement("div");
    results.className = "runtime-results";
    results.setAttribute("role", "status");
    results.setAttribute("aria-live", "polite");
    results.setAttribute("aria-atomic", "true");
    let timer = 0;
    let requestEpoch = 0;
    input.addEventListener("input", () => {
      window.clearTimeout(timer);
      requestEpoch += 1;
      const epoch = requestEpoch;
      const query = input.value.trim();
      if (!query) {
        results.replaceChildren();
        results.removeAttribute("aria-busy");
        return;
      }
      timer = window.setTimeout(() => {
        if (this.client.state !== "ready") {
          results.textContent = "服务器连接已断开，搜索未发送";
          return;
        }
        results.textContent = "正在搜索…";
        results.setAttribute("aria-busy", "true");
        this.client.rpc<{ items?: Array<Record<string, unknown>> }>("session/search", { query, limit: 30 })
          .then((response) => {
            if (epoch !== requestEpoch || query !== input.value.trim()) return;
            results.replaceChildren();
            results.removeAttribute("aria-busy");
            const items = response.items || [];
            if (!items.length) {
              results.textContent = "没有找到匹配的服务器记录";
              return;
            }
            for (const item of items) {
              const button = document.createElement("button");
              button.type = "button";
              button.textContent = String(item.title || item.name || "未命名会话");
              button.addEventListener("click", () => {
                const surface: Surface = item.surface === "work" ? "work" : "chat";
                const threadId = String(item.threadId || item.id || "");
                if (!threadId) return;
                dialog.close();
                void this.openThread(surface, threadId);
              });
              results.append(button);
            }
          }).catch((error) => {
            if (epoch !== requestEpoch) return;
            results.removeAttribute("aria-busy");
            results.textContent = error instanceof Error ? `搜索失败：${error.message}` : "搜索失败，请稍后重试";
          });
      }, 240);
    });
    card.insertBefore(input, card.querySelector(".runtime-dialog-actions"));
    card.insertBefore(results, card.querySelector(".runtime-dialog-actions"));
    dialog.showModal();
    input.focus();
  }

  private openServerList(method: string, title: string): void {
    if (this.client.state !== "ready") return toast("服务器尚未连接", "error");
    if (method !== "capabilities/read" && !this.operationIsAvailable(method)) {
      return toast("服务器能力清单没有声明此操作", "error");
    }
    const threadId = this.activeThread.get("work");
    if (method === "thread/read" && !threadId) return toast("当前还没有工作线程", "error");
    const params = method === "thread/read"
      ? { threadId, includeTurns: true }
      : method === "model/list"
        ? { limit: 50, includeHidden: false }
        : method === "permissionProfile/list" || method === "mcpServerStatus/list" || method === "plugin/list" || method === "account/read"
          ? {}
        : method === "fs/readDirectory"
          ? { path: this.config.workspacePath, depth: 2 }
          : method === "skills/list"
            ? { cwds: [this.config.workspacePath], forceReload: false }
            : method === "thread/list"
              ? { limit: 50 }
              : { projectId: this.config.projectId };
    this.client.rpc(method, params)
      .then((result) => {
        const dialog = this.createInfoDialog(title, "数据来自当前服务器会话");
        const pre = document.createElement("pre");
        pre.className = "runtime-json";
        pre.textContent = JSON.stringify(result, null, 2);
        dialog.querySelector(".runtime-dialog-card")?.insertBefore(pre, dialog.querySelector(".runtime-dialog-actions"));
        dialog.showModal();
      }).catch((error) => toast(error instanceof Error ? error.message : "读取失败", "error"));
  }

  private async openWorkspacePicker(): Promise<void> {
    if (this.client.state !== "ready") return toast("服务器尚未连接", "error");
    if (!this.operationIsAvailable("workspace/list")) return toast("服务器没有声明工作区列表能力", "error");
    const principalId = this.serverPrincipalId;
    if (!principalId) return toast("当前登录状态尚未完成", "error");

    const dialog = this.createInfoDialog("远程工作区", "工作文件保存在美国服务器的专属目录；客户端只显示当前账户有权访问的工作区。");
    const card = dialog.querySelector<HTMLElement>(".runtime-dialog-card")!;
    const actions = dialog.querySelector<HTMLElement>(".runtime-dialog-actions")!;
    const loading = document.createElement("div");
    loading.className = "runtime-model-loading";
    loading.setAttribute("role", "status");
    loading.textContent = "正在读取远程工作区…";
    card.insertBefore(loading, actions);
    dialog.showModal();

    const trigger = document.querySelector<HTMLButtonElement>("[data-app-action='workspace']");
    trigger?.setAttribute("aria-busy", "true");
    try {
      const response = await this.client.rpc<Record<string, unknown>>("workspace/list", {});
      if (this.serverPrincipalId !== principalId || !dialog.open) return;
      const workspaces = threadListItems(response);
      if (!workspaces.length) {
        loading.textContent = "当前账户还没有可用的远程工作区。";
        return;
      }
      const list = document.createElement("div");
      list.className = "runtime-model-list";
      list.setAttribute("role", "list");
      for (const workspace of workspaces) {
        const item = document.createElement("div");
        item.className = "runtime-workspace-option";
        item.setAttribute("role", "listitem");
        const icon = document.createElement("span");
        icon.className = "runtime-workspace-status";
        icon.setAttribute("aria-hidden", "true");
        const copy = document.createElement("span");
        copy.className = "runtime-model-copy";
        const name = document.createElement("strong");
        name.textContent = String(workspace.name || "彼岸花·智体");
        const path = document.createElement("small");
        path.textContent = String(workspace.path || workspace.cwd || "服务器工作区");
        copy.append(name, path);
        const state = document.createElement("small");
        state.className = "runtime-workspace-selected";
        state.textContent = workspace.writable === false ? "只读" : "当前使用";
        item.append(icon, copy, state);
        list.append(item);
      }
      loading.replaceWith(list);
    } catch (error) {
      loading.textContent = error instanceof Error ? `读取工作区失败：${error.message}` : "读取工作区失败，请稍后重试";
    } finally {
      trigger?.removeAttribute("aria-busy");
    }
  }

  private async openWorkPermissionPicker(): Promise<void> {
    if (this.client.state !== "ready") return toast("服务器尚未连接", "error");
    if (!this.operationIsAvailable("permissionProfile/list")) return toast("服务器没有声明权限配置能力", "error");
    const principalId = this.serverPrincipalId;
    if (!principalId) return toast("当前登录状态尚未完成", "error");

    const dialog = this.createInfoDialog(
      "远程运行权限",
      "选择将在下一次新任务或新一轮任务生效；当前正在运行或等待确认的任务不会改变。",
    );
    const card = dialog.querySelector<HTMLElement>(".runtime-dialog-card")!;
    const actions = dialog.querySelector<HTMLElement>(".runtime-dialog-actions")!;
    const close = actions.querySelector<HTMLButtonElement>("button")!;
    close.textContent = "取消";
    const loading = document.createElement("div");
    loading.className = "runtime-model-loading";
    loading.setAttribute("role", "status");
    loading.textContent = "正在确认服务器权限配置…";
    card.insertBefore(loading, actions);
    dialog.showModal();

    const trigger = document.querySelector<HTMLButtonElement>("[data-app-action='permissions']");
    trigger?.setAttribute("aria-busy", "true");
    try {
      const response = await this.client.rpc<Record<string, unknown>>("permissionProfile/list", {});
      if (this.serverPrincipalId !== principalId || !dialog.open) return;
      const serverProfiles = threadListItems(response);
      let draft = this.selectedWorkExecutionPolicy;
      const list = document.createElement("div");
      list.className = "runtime-model-list";
      list.setAttribute("role", "radiogroup");
      list.setAttribute("aria-label", "远程运行权限");
      const options: Array<{
        id: WorkExecutionPolicy;
        title: string;
        description: string;
        recommended?: boolean;
        danger?: boolean;
      }> = [
        {
          id: "read-only",
          title: "只读",
          description: "仅分析和读取远程工作区；写入、联网或更高权限会请求你确认。",
        },
        {
          id: "workspace",
          title: "工作区",
          description: serverProfiles.length
            ? `可在远程工作区内读写；访问网络或工作区外位置时请求确认（已同步 ${serverProfiles.length} 个服务器配置）。`
            : "可在远程工作区内读写；访问网络或工作区外位置时请求确认。",
          recommended: true,
        },
        {
          id: "full-access",
          title: "完全访问",
          description: "高风险：解除 Codex 沙箱并关闭审批；命令、写入与联网不会再逐次询问。仅在信任当前任务和内容时使用。",
          danger: true,
        },
      ];
      for (const option of options) {
        const label = document.createElement("label");
        label.className = "runtime-model-option";
        if (option.danger) label.classList.add("runtime-permission-option-danger");
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "runtime-work-policy";
        radio.value = option.id;
        radio.checked = draft === option.id;
        const copy = document.createElement("span");
        copy.className = "runtime-model-copy";
        const heading = document.createElement("span");
        heading.className = "runtime-model-heading";
        const title = document.createElement("strong");
        title.textContent = option.title;
        heading.append(title);
        if (option.recommended) {
          const badge = document.createElement("small");
          badge.className = "runtime-model-recommended";
          badge.textContent = "推荐";
          heading.append(badge);
        }
        if (option.danger) {
          const badge = document.createElement("small");
          badge.className = "runtime-permission-danger-badge";
          badge.textContent = "高风险";
          heading.append(badge);
        }
        const description = document.createElement("small");
        if (option.danger) description.className = "runtime-permission-risk";
        description.textContent = option.description;
        copy.append(heading, description);
        label.append(radio, copy);
        radio.addEventListener("change", () => { if (radio.checked) draft = option.id; });
        list.append(label);
      }
      loading.replaceWith(list);
      const apply = document.createElement("button");
      apply.type = "button";
      apply.className = "runtime-primary";
      apply.textContent = "应用";
      apply.addEventListener("click", () => {
        if (!this.persistWorkExecutionPolicy(draft)) {
          toast("权限选择无法保存，请检查本地存储权限", "error");
          return;
        }
        dialog.close();
        toast(`已切换为远程${WORK_EXECUTION_POLICY_LABELS[draft]}，将在下一次新任务或新一轮生效`, "success");
      });
      actions.append(apply);
    } catch (error) {
      loading.textContent = error instanceof Error ? `读取权限配置失败：${error.message}` : "读取权限配置失败，请稍后重试";
    } finally {
      trigger?.removeAttribute("aria-busy");
    }
  }

  private async openWorkModelPicker(): Promise<void> {
    if (this.client.state !== "ready") return toast("服务器尚未连接", "error");
    if (!this.operationIsAvailable("model/list")) return toast("服务器没有声明模型列表能力", "error");
    const principalId = this.serverPrincipalId;
    if (!principalId) return toast("当前登录状态尚未完成", "error");

    const dialog = this.createInfoDialog("选择工作模型", "模型与推理强度来自当前服务器；选择会应用到下一次新任务或新一轮任务。");
    const card = dialog.querySelector<HTMLElement>(".runtime-dialog-card")!;
    const actions = dialog.querySelector<HTMLElement>(".runtime-dialog-actions")!;
    const close = actions.querySelector<HTMLButtonElement>("button")!;
    close.textContent = "取消";
    const loading = document.createElement("div");
    loading.className = "runtime-model-loading";
    loading.setAttribute("role", "status");
    loading.textContent = "正在读取可用模型…";
    card.insertBefore(loading, actions);
    dialog.showModal();

    const trigger = document.querySelector<HTMLButtonElement>("[data-app-action='models']");
    if (trigger) trigger.setAttribute("aria-busy", "true");
    try {
      const catalog: WorkModelOption[] = [];
      const seen = new Set<string>();
      let cursor: string | null = null;
      for (let page = 0; page < 4; page += 1) {
        const response = await this.client.rpc<Record<string, unknown>>("model/list", {
          limit: 50,
          includeHidden: false,
          ...(cursor ? { cursor } : {}),
        });
        if (this.serverPrincipalId !== principalId || !dialog.open) return;
        for (const option of parseWorkModelCatalog(response)) {
          if (seen.has(option.model)) continue;
          seen.add(option.model);
          catalog.push(option);
        }
        cursor = threadListNextCursor(response);
        if (!cursor) break;
      }
      if (!catalog.length) {
        loading.textContent = "服务器没有返回可选择的模型。";
        return;
      }

      let draftModel = catalog.find((option) => option.model === this.selectedWorkModel?.model)
        || catalog.find((option) => option.isDefault)
        || catalog[0]!;
      let draftEffort = this.selectedWorkModel?.model === draftModel.model
        && this.selectedWorkModel.effort
        && draftModel.efforts.some((option) => option.effort === this.selectedWorkModel?.effort)
        ? this.selectedWorkModel.effort
        : draftModel.defaultEffort;

      const picker = document.createElement("div");
      picker.className = "runtime-model-picker";
      const list = document.createElement("div");
      list.className = "runtime-model-list";
      list.setAttribute("role", "radiogroup");
      list.setAttribute("aria-label", "工作模型");
      const effortPanel = document.createElement("div");
      effortPanel.className = "runtime-model-effort";

      const renderEfforts = (): void => {
        effortPanel.replaceChildren();
        const label = document.createElement("label");
        const title = document.createElement("span");
        title.textContent = "推理强度";
        const select = document.createElement("select");
        select.setAttribute("aria-label", "推理强度");
        for (const option of draftModel.efforts) {
          const element = document.createElement("option");
          element.value = option.effort;
          element.textContent = reasoningEffortLabel(option.effort);
          element.title = option.description;
          select.append(element);
        }
        if (!draftModel.efforts.length) {
          const automatic = document.createElement("option");
          automatic.value = "";
          automatic.textContent = "服务器默认";
          select.append(automatic);
          draftEffort = null;
        } else if (!draftEffort || !draftModel.efforts.some((option) => option.effort === draftEffort)) {
          draftEffort = draftModel.defaultEffort || draftModel.efforts[0]!.effort;
        }
        select.value = draftEffort || "";
        select.addEventListener("change", () => { draftEffort = select.value || null; });
        label.append(title, select);
        const helper = document.createElement("small");
        helper.textContent = draftModel.efforts.find((option) => option.effort === draftEffort)?.description
          || "该模型使用服务器默认推理设置";
        select.addEventListener("change", () => {
          helper.textContent = draftModel.efforts.find((option) => option.effort === draftEffort)?.description
            || "该模型使用服务器默认推理设置";
        });
        effortPanel.append(label, helper);
      };

      for (const option of catalog) {
        const label = document.createElement("label");
        label.className = "runtime-model-option";
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "runtime-work-model";
        radio.value = option.model;
        radio.checked = option.model === draftModel.model;
        const copy = document.createElement("span");
        copy.className = "runtime-model-copy";
        const heading = document.createElement("span");
        heading.className = "runtime-model-heading";
        const name = document.createElement("strong");
        name.textContent = option.displayName;
        heading.append(name);
        if (option.isDefault) {
          const recommended = document.createElement("small");
          recommended.className = "runtime-model-recommended";
          recommended.textContent = "默认";
          heading.append(recommended);
        }
        const description = document.createElement("small");
        description.textContent = option.description || `${option.inputModalities.join(" · ")} 输入`;
        copy.append(heading, description);
        label.append(radio, copy);
        radio.addEventListener("change", () => {
          if (!radio.checked) return;
          draftModel = option;
          draftEffort = option.defaultEffort;
          renderEfforts();
        });
        list.append(label);
      }
      renderEfforts();
      picker.append(list, effortPanel);
      loading.replaceWith(picker);

      const apply = document.createElement("button");
      apply.type = "button";
      apply.className = "runtime-primary";
      apply.textContent = "应用";
      apply.addEventListener("click", () => {
        const selection: WorkModelSelection = {
          model: draftModel.model,
          displayName: draftModel.displayName,
          effort: draftEffort,
        };
        if (!this.persistWorkModelSelection(selection)) {
          toast("模型选择无法保存，请检查本地存储权限", "error");
          return;
        }
        dialog.close();
        toast(`已选择 ${selection.displayName}${selection.effort ? ` · ${reasoningEffortLabel(selection.effort)}` : ""}`, "success");
      });
      actions.append(apply);
    } catch (error) {
      if (dialog.open) loading.textContent = error instanceof Error ? `读取模型失败：${error.message}` : "读取模型失败，请稍后重试";
    } finally {
      trigger?.removeAttribute("aria-busy");
    }
  }

  private createInfoDialog(titleText: string, descriptionText: string): HTMLDialogElement {
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const existing = document.querySelector<HTMLDialogElement>("#runtime-info");
    if (existing?.open) existing.close();
    else existing?.remove();
    const dialog = document.createElement("dialog");
    dialog.id = "runtime-info";
    dialog.className = "runtime-dialog";
    const card = document.createElement("div");
    card.className = "runtime-dialog-card";
    const eyebrow = document.createElement("span");
    eyebrow.className = "runtime-dialog-eyebrow";
    eyebrow.textContent = "彼岸花·智体";
    const title = document.createElement("h2");
    title.id = "runtime-info-title";
    title.textContent = titleText;
    dialog.setAttribute("aria-labelledby", title.id);
    const description = document.createElement("p");
    description.textContent = descriptionText;
    const actions = document.createElement("div");
    actions.className = "runtime-dialog-actions";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "runtime-secondary";
    close.textContent = "关闭";
    close.addEventListener("click", () => dialog.close());
    actions.append(close);
    card.append(eyebrow, title, description, actions);
    dialog.append(card);
    dialog.addEventListener("close", () => {
      dialog.remove();
      if (restoreTarget?.isConnected) queueMicrotask(() => restoreTarget.focus());
    }, { once: true });
    document.body.append(dialog);
    return dialog;
  }
}

export async function startClient(): Promise<void> {
  const config = loadRuntimeConfig();
  await bindWindowControls();
  const artifacts = await installArtifactSync(config);
  const client = new GatewayClient(config);
  const controller = new SessionController(config, client, artifacts);
  bindVideoPreviewViewer();
  const deterministicCapture = window.BianhuaVisualDemoAllowed === true
    && new URLSearchParams(location.search).get("capture") === "1";
  if (!deterministicCapture) {
    const video = new VideoRuntime(client);
    video.bind();
  }
  // Video restores its principal-scoped paid-create journal first. Session
  // listeners are installed before connect, so its submitted event is still
  // observed before workflow recovery decides which draft may be superseded.
  controller.bind();
  if (!deterministicCapture && config.gatewayUrl) dispatchSyncState("connecting");
  if (config.gatewayUrl && config.autoConnect) {
    try {
      client.connect();
    } catch (error) {
      toast(error instanceof Error ? error.message : "服务器连接配置无效", "error");
    }
  }
}
